import React, { useEffect, useMemo, useRef, useState } from 'react'
import Swal from 'sweetalert2'
import { supabase } from '../../supabaseClient'
import Header from '../../components/Header'
import { useNavigate } from 'react-router-dom'
import { authHeaders } from '../../utils/authFetch'

if (typeof document !== 'undefined' && !document.getElementById('shimmer-keyframes')) {
    const style = document.createElement('style')
    style.id = 'shimmer-keyframes'
    style.innerHTML = `
            @keyframes shimmer {
                0% { background-position: -400px 0; }
                100% { background-position: 400px 0; }
            }
        `
    document.head.appendChild(style)
}

if (typeof document !== 'undefined' && !document.getElementById('scrollbar-hide-styles')) {
    const style = document.createElement('style')
    style.id = 'scrollbar-hide-styles'
    style.innerHTML = `
            .scrollbar-hide::-webkit-scrollbar { display: none; }
            .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
        `
    document.head.appendChild(style)
}

// ─── Design tokens ────────────────────────────────────────────────────
const C = {
    espresso: 'transparent',
    dark: '#F9FAFB',
    card: '#FFFFFF',
    border: '#E5E7EB',
    muted: '#9CA3AF',
    text: '#111827',
    textSoft: '#6B7280',
    amber: '#D97706',
    gold: '#B45309',
    teal: '#0D9488',
    coral: '#DC2626',
    blue: '#2563EB',
    green: '#16A34A',
    purple: '#7C3AED',
    indigo: '#4F46E5',
    nude: '#A0826D',
    midgrey: '#6B7280',
}

const SkeletonBlock = ({ className = '', style = {} }) => (
    <div
        className={`rounded ${className}`}
        style={{
            backgroundImage: 'linear-gradient(90deg, #e5e7eb 0px, #f3f4f6 40px, #e5e7eb 80px)',
            backgroundSize: '400px 100%',
            animation: 'shimmer 1.4s ease-in-out infinite',
            ...style,
        }}
    />
)

export default function MenuManagement({ onGoToProfile }) {
    const [activeTab, setActiveTab] = useState('menu_overview')
    const [branches, setBranches] = useState([])
    const [selectedBranchIds, setSelectedBranchIds] = useState(new Set())

    const [menuItemsPage, setMenuItemsPage] = useState(1)
    const [variantsPage, setVariantsPage] = useState(1)
    const [modifiersPage, setModifiersPage] = useState(1)
    const [itemSortField, setItemSortField] = useState('name')
    const [itemSortDirection, setItemSortDirection] = useState('asc')
    const ITEMS_PAGE_SIZE = 10
    const MAX_PRICE_LENGTH = 4

    // Branch menu overview
    const [allBranchMenuItems, setAllBranchMenuItems] = useState([])
    const [branchMenuItems, setBranchMenuItems] = useState([])
    const [branchMenuLoading, setBranchMenuLoading] = useState(true)
    const [selectedBranchForMenu, setSelectedBranchForMenu] = useState('all')
    const [branchBreakdownCollapsed, setBranchBreakdownCollapsed] = useState(true)
    const [uniqueItemsCollapsed, setUniqueItemsCollapsed] = useState(true)
    const [menuItemSearch, setMenuItemSearch] = useState('')
    const [openMenuItemId, setOpenMenuItemId] = useState(null)
    const menuItemDropdownRefs = useRef({})
    const [menuItemDropdownPosition, setMenuItemDropdownPosition] = useState({ top: 0, left: 0 })

    const [filterClassification, setFilterClassification] = useState('All')
    const [availableClassificationsForCategory, setAvailableClassificationsForCategory] = useState([])

    const [menuItems, setMenuItems] = useState([])
    const [modifiers, setModifiers] = useState([])
    const [loading, setLoading] = useState(true)
    const [loadingModifiers, setLoadingModifiers] = useState(true)

    const [showAddItemModal, setShowAddItemModal] = useState(false)
    const [showEditItemModal, setShowEditItemModal] = useState(false)

    // Variants
    const [variants, setVariants] = useState([])
    const [loadingVariants, setLoadingVariants] = useState(true)
    const [showAddVariantModal, setShowAddVariantModal] = useState(false)
    const [showEditVariantModal, setShowEditVariantModal] = useState(false)
    const [showAssignVariantsModal, setShowAssignVariantsModal] = useState(false)

    const [assignVariantSearch, setAssignVariantSearch] = useState('')
    const [editingVariant, setEditingVariant] = useState(null)
    const [selectedItemForVariants, setSelectedItemForVariants] = useState(null)
    const [assignVariantCategoryFilter, setAssignVariantCategoryFilter] = useState('All')   // ← ADD

    const [assignedVariantIds, setAssignedVariantIds] = useState(new Set())
    const [loadingVariantAssignments, setLoadingVariantAssignments] = useState(false)
    const [savingVariantAssignments, setSavingVariantAssignments] = useState(false)

    const [variantSearchQuery, setVariantSearchQuery] = useState("")
    const [filterVariantCategory, setFilterVariantCategory] = useState("All")

    const [savingVariant, setSavingVariant] = useState(false)
    const [loadingItemVariants, setLoadingItemVariants] = useState(false)

    const [variantCategorySearch, setVariantCategorySearch] = useState("")
    const [openVariantCategoryDropdown, setOpenVariantCategoryDropdown] = useState(false)
    const variantCategoryDropdownRef = useRef(null)

    // Modifiers
    const [showAddModifierModal, setShowAddModifierModal] = useState(false)
    const [showEditModifierModal, setShowEditModifierModal] = useState(false)
    const [showAssignModifiersModal, setShowAssignModifiersModal] = useState(false)

    const [assignModifierSearch, setAssignModifierSearch] = useState('')
    const [editingItem, setEditingItem] = useState(null)
    const [editingModifier, setEditingModifier] = useState(null)
    const [selectedItemForModifiers, setSelectedItemForModifiers] = useState(null)
    const [assignModifierCategoryFilter, setAssignModifierCategoryFilter] = useState('All')

    const [assignedModifierIds, setAssignedModifierIds] = useState(new Set())
    const [loadingAssignments, setLoadingAssignments] = useState(false)
    const [savingAssignments, setSavingAssignments] = useState(false)
    const [searchQuery, setSearchQuery] = useState("")
    const [filterCategory, setFilterCategory] = useState("All")
    const [modifierSearchQuery, setModifierSearchQuery] = useState("")
    const [filterModifierCategory, setFilterModifierCategory] = useState("All")

    //Item Image
    const [itemImageFile, setItemImageFile] = useState(null)
    const [imageInputMode, setImageInputMode] = useState('upload')
    const [uploadingImage, setUploadingImage] = useState(false)

    //Initial Data
    const [initialItemFormData, setInitialItemFormData] = useState(null)
    const [initialVariantFormData, setInitialVariantFormData] = useState(null)
    const [initialModifierFormData, setInitialModifierFormData] = useState(null)
    const [initialVariantRuleFormData, setInitialVariantRuleFormData] = useState(null)

    const [initialSelectedBranchIds, setInitialSelectedBranchIds] = useState(new Set())
    const [initialAssignedVariantIds, setInitialAssignedVariantIds] = useState(new Set())
    const [initialAssignedModifierIds, setInitialAssignedModifierIds] = useState(new Set())

    //Variant Rules
    const [variantRuleTableSearch, setVariantRuleTableSearch] = useState('')
    const [showVariantRulesModal, setShowVariantRulesModal] = useState(false)
    const [selectedItemForVariantRules, setSelectedItemForVariantRules] = useState(null)

    const [itemVariants, setItemVariants] = useState([])
    const [ingredients, setIngredients] = useState([])
    const [variantRules, setVariantRules] = useState([])

    const [loadingVariantRules, setLoadingVariantRules] = useState(false)
    const [savingVariantRule, setSavingVariantRule] = useState(false)

    const [variantRuleVariantSearch, setVariantRuleVariantSearch] = useState('')
    const [variantRuleIngredientSearch, setVariantRuleIngredientSearch] = useState('')

    const [openVariantDropdown, setOpenVariantDropdown] = useState(false)
    const [openIngredientDropdown, setOpenIngredientDropdown] = useState(false)

    const variantDropdownRef = useRef(null)
    const ingredientDropdownRef = useRef(null)

    //View Archived
    const [showArchivedViewerModal, setShowArchivedViewerModal] = useState(false)
    const [archivedMenuItems, setArchivedMenuItems] = useState([])
    const [archivedVariants, setArchivedVariants] = useState([])
    const [archivedModifiers, setArchivedModifiers] = useState([])
    const [loadingArchivedItems, setLoadingArchivedItems] = useState(false)
    const [archivedSearch, setArchivedSearch] = useState('')
    const [archivedPage, setArchivedPage] = useState(1)
    const [archivedCategoryFilter, setArchivedCategoryFilter] = useState('All')
    const [archivedClassificationFilter, setArchivedClassificationFilter] = useState('All')
    const [archivedAvailableClassifications, setArchivedAvailableClassifications] = useState([])

    //Menu Requests
    const [showPriceRequestsModal, setShowPriceRequestsModal] = useState(false)
    const [priceRequests, setPriceRequests] = useState([])
    const [loadingPriceRequests, setLoadingPriceRequests] = useState(false)
    const [processingPriceRequestId, setProcessingPriceRequestId] = useState(null)
    const [priceRequestFilter, setPriceRequestFilter] = useState('pending')
    const [priceRequestsModalPage, setPriceRequestsModalPage] = useState(1)

    const [menuChangeRequests, setMenuChangeRequests] = useState([])
    const [loadingMenuChangeRequests, setLoadingMenuChangeRequests] = useState(false)
    const [processingMenuChangeRequestId, setProcessingMenuChangeRequestId] = useState(null)
    const [menuChangeRequestFilter, setMenuChangeRequestFilter] = useState('pending')

    const [showRejectReasonModal, setShowRejectReasonModal] = useState(false)
    const [rejectingRequest, setRejectingRequest] = useState(null)
    const [rejectionReason, setRejectionReason] = useState('')
    const [submittingRejection, setSubmittingRejection] = useState(false)
    const [rejectingSource, setRejectingSource] = useState('price') // 'price' | 'admin'



    //Dropdown search
    const [itemCategorySearch, setItemCategorySearch] = useState("")
    const [itemClassificationSearch, setItemClassificationSearch] = useState("")
    const [openItemCategoryDropdown, setOpenItemCategoryDropdown] = useState(false)
    const [openItemClassificationDropdown, setOpenItemClassificationDropdown] = useState(false)

    const itemCategoryDropdownRef = useRef(null)
    const itemClassificationDropdownRef = useRef(null)

    const [modifierCategorySearch, setModifierCategorySearch] = useState("")
    const [openModifierCategoryDropdown, setOpenModifierCategoryDropdown] = useState(false)
    const modifierCategoryDropdownRef = useRef(null)

    // Modifier Ingredient
    const [modifierIngredientSearch, setModifierIngredientSearch] = useState("")
    const [openModifierIngredientDropdown, setOpenModifierIngredientDropdown] = useState(false)
    const modifierIngredientDropdownRef = useRef(null)

    const [expandedBranchId, setExpandedBranchId] = useState(null)
    const [branchMenuSubTab, setBranchMenuSubTab] = useState({})
    const [branchMenuItemPages, setBranchMenuItemPages] = useState({})
    const [branchPriceRequestPages, setBranchPriceRequestPages] = useState({})
    const [branchMenuChangeRequestPages, setBranchMenuChangeRequestPages] = useState({})
    const [branchVariantChangeRequestPages, setBranchVariantChangeRequestPages] = useState({})
    const [branchModifierChangeRequestPages, setBranchModifierChangeRequestPages] = useState({})
    const [branchMenuItemSearch, setBranchMenuItemSearch] = useState({})


    //Bulk Imports
    const [showBulkImportModal, setShowBulkImportModal] = useState(false)
    const [bulkImportFile, setBulkImportFile] = useState(null)
    const [bulkImportPreview, setBulkImportPreview] = useState([])
    const [bulkImportErrors, setBulkImportErrors] = useState([])
    const [bulkImporting, setBulkImporting] = useState(false)
    const [bulkImportResult, setBulkImportResult] = useState(null)
    const [bulkImportBranchIds, setBulkImportBranchIds] = useState(new Set())

    // Variant CSV Import/Export
    const [showVariantImportModal, setShowVariantImportModal] = useState(false)
    const [variantImportFile, setVariantImportFile] = useState(null)
    const [variantImportPreview, setVariantImportPreview] = useState([])
    const [variantImportErrors, setVariantImportErrors] = useState([])
    const [variantImporting, setVariantImporting] = useState(false)

    // Modifier CSV Import/Export
    const [showModifierImportModal, setShowModifierImportModal] = useState(false)
    const [modifierImportFile, setModifierImportFile] = useState(null)
    const [modifierImportPreview, setModifierImportPreview] = useState([])
    const [modifierImportErrors, setModifierImportErrors] = useState([])
    const [modifierImporting, setModifierImporting] = useState(false)

    const [menuOverviewPage, setMenuOverviewPage] = useState(1)
    const MENU_PAGE_SIZE = 10

    const [variantRuleFormData, setVariantRuleFormData] = useState({
        id: null,
        variant_id: '',
        ingredient_id: '',
        quantity_delta: '',
        unit: '',
        notes: ''
    })

    const [itemFormData, setItemFormData] = useState({
        name: "",
        item_price: "",
        category: "",
        classification: "",
        description: "",
        prep_time_minutes: "",
        item_img: ""
    })

    const [variantFormData, setVariantFormData] = useState({
        category: "",
        variant_name: "",
        price: "",
        applicable_categories: []
    })

    const [modifierFormData, setModifierFormData] = useState({
        modifier_name: "",
        category: "",
        price: "",
        applicable_categories: [],
        ingredient_id: ""
    })

    const navigate = useNavigate();

    useEffect(() => {
        const style = document.createElement('style')
        style.innerHTML = `
                .swal2-confirm { background-color: #FF6F3C !important; border: none !important; }
                .swal2-confirm:hover { background-color: #ff4c0b !important; }
                .swal2-cancel { background-color: #6b7280 !important; border: none !important; }
                .swal2-cancel:hover { background-color: #5a5a5a !important; }
            `
        document.head.appendChild(style)
        return () => document.head.removeChild(style)
    }, [])

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (variantDropdownRef.current && !variantDropdownRef.current.contains(event.target)) {
                setOpenVariantDropdown(false)
            }

            if (ingredientDropdownRef.current && !ingredientDropdownRef.current.contains(event.target)) {
                setOpenIngredientDropdown(false)
            }

            if (variantCategoryDropdownRef.current && !variantCategoryDropdownRef.current.contains(event.target)) {
                setOpenVariantCategoryDropdown(false)
            }

            if (itemCategoryDropdownRef.current && !itemCategoryDropdownRef.current.contains(event.target)) {
                setOpenItemCategoryDropdown(false)
            }

            if (itemClassificationDropdownRef.current && !itemClassificationDropdownRef.current.contains(event.target)) {
                setOpenItemClassificationDropdown(false)
            }

            if (openMenuItemId) {
                setOpenMenuItemId(null)
            }

            if (modifierCategoryDropdownRef.current && !modifierCategoryDropdownRef.current.contains(event.target)) {
                setOpenModifierCategoryDropdown(false)
            }

            if (modifierIngredientDropdownRef.current && !modifierIngredientDropdownRef.current.contains(event.target)) {
                setOpenModifierIngredientDropdown(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    useEffect(() => {
        fetchBranchMenuItems(selectedBranchForMenu)
    }, [selectedBranchForMenu])

    useEffect(() => {
        fetchMenuItems()
        fetchModifiers()
        fetchVariants()
        fetchBranches()
        fetchIngredients()
        fetchBranchMenuItems()
        fetchPriceChangeRequests()
        fetchMenuChangeRequests()
    }, [])

    useEffect(() =>
        setMenuItemsPage(1), [searchQuery, filterCategory, filterClassification])

    useEffect(() =>
        setVariantsPage(1), [variantSearchQuery, filterVariantCategory])

    useEffect(() =>
        setModifiersPage(1), [modifierSearchQuery, filterModifierCategory])

    useEffect(() =>
        setArchivedPage(1), [archivedSearch, archivedCategoryFilter, archivedClassificationFilter, activeTab])

    useEffect(() =>
        setPriceRequestsModalPage(1), [priceRequestFilter])

    const fetchBranches = async () => {
        try {
            const { data, error } = await supabase
                .from('branches')
                .select('branch_id, name, status')
                .order('name', { ascending: true })

            if (error) throw error

            setBranches((data || []).filter(branch => branch.status !== 'inactive'))
        } catch (err) {
            console.error("Error fetching branches:", err)
        }
    }

    const fetchMenuItems = async () => {
        try {
            setLoading(true)
            const { data, error } = await supabase
                .from('menu_items')
                .select('*')
                .eq('is_archived', false)
                .order('name', { ascending: true })
            if (error) throw error
            setMenuItems(data || [])
        } catch (err) {
            console.error("Error fetching menu items:", err)
        } finally {
            setLoading(false)
        }
    }

    const fetchVariants = async () => {
        try {
            setLoadingVariants(true)

            const { data, error } = await supabase
                .from('item_variants')
                .select('variant_id, category, variant_name, price, applicable_categories, created_at, is_archived')
                .eq('is_archived', false)
                .order('category', { ascending: true })
                .order('variant_name', { ascending: true })

            if (error) throw error
            setVariants(data || [])
        } catch (err) {
            console.error("Error fetching variants:", err)
            setVariants([])
        } finally {
            setLoadingVariants(false)
        }
    }

    const fetchModifiers = async () => {
        try {
            setLoadingModifiers(true)
            const { data, error } = await supabase
                .from('item_modifiers')
                .select('*')
                .eq('is_archived', false)
                .order('modifier_name', { ascending: true })

            if (error) throw error

            setModifiers(data || [])
        } catch (err) {
            console.error("Error fetching modifiers:", err)
            setModifiers([])
        } finally {
            setLoadingModifiers(false)
        }
    }

    const fetchItemVariants = async (itemId) => {
        try {
            setLoadingItemVariants(true)

            const { data, error } = await supabase
                .from('menu_variants')
                .select(`
                        variant_id,
                        item_variants (
                            variant_id,
                            category,
                            variant_name,
                            price,
                            created_at,
                            is_archived
                        )
                    `)
                .eq('item_id', itemId)

            if (error) throw error

            const flattenedVariants = (data || [])
                .map((row) => row.item_variants)
                .filter((variant) => variant && variant.is_archived === false)
                .sort((a, b) => {
                    const categoryCompare = String(a.category || '').localeCompare(String(b.category || ''))
                    if (categoryCompare !== 0) return categoryCompare
                    return String(a.variant_name || '').localeCompare(String(b.variant_name || ''))
                })

            setItemVariants(flattenedVariants)
        } catch (err) {
            console.error("Error fetching item variants:", err)
            setItemVariants([])
        } finally {
            setLoadingItemVariants(false)
        }
    }

    const fetchIngredients = async () => {
        try {
            const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080'
            const res = await fetch(`${BASE_URL}/api/inventory-pos/ingredients`, { headers: authHeaders() })
            if (!res.ok) throw new Error('Failed to fetch ingredients')
            const data = await res.json()

            setIngredients(
                (data || []).map((ingredient) => ({
                    ...ingredient,
                    ingredient_name: ingredient.name
                }))
            )
        } catch (err) {
            console.error("Error fetching ingredients:", err)
            setIngredients([])
        }
    }

    const fetchVariantRules = async (itemId) => {
        try {
            setLoadingVariantRules(true)

            const { data, error } = await supabase
                .schema('inventory')
                .from('menu_item_variant_ingredient_rules')
                .select(`
                        id,
                        item_id,
                        variant_id,
                        ingredient_id,
                        quantity_delta,
                        unit,
                        notes,
                        created_at
                    `)
                .eq('item_id', itemId)
                .order('created_at', { ascending: true })

            if (error) throw error

            const { data: activeIngredients, error: ingredientError } = await supabase
                .schema('inventory')
                .from('ingredients')
                .select('ingredient_id')
                .eq('is_active', true)

            if (ingredientError) throw ingredientError

            const activeIngredientIds = new Set(
                (activeIngredients || []).map(ing => String(ing.ingredient_id))
            )

            const filteredRules = (data || []).filter(rule =>
                activeIngredientIds.has(String(rule.ingredient_id))
            )

            setVariantRules(filteredRules)
        } catch (err) {
            console.error("Error fetching variant rules:", err)
            setVariantRules([])
        } finally {
            setLoadingVariantRules(false)
        }
    }

    const fetchArchivedMenuItems = async () => {
        try {
            setLoadingArchivedItems(true)

            const { data, error } = await supabase
                .from('menu_items')
                .select('*')
                .eq('is_archived', true)
                .order('archived_at', { ascending: false })

            if (error) throw error
            setArchivedMenuItems(data || [])
        } catch (err) {
            console.error("Error fetching archived menu items:", err)
            setArchivedMenuItems([])
        } finally {
            setLoadingArchivedItems(false)
        }
    }

    const fetchArchivedVariants = async () => {
        try {
            setLoadingArchivedItems(true)

            const { data, error } = await supabase
                .from('item_variants')
                .select('variant_id, category, variant_name, price, created_at, archived_at, is_archived')
                .eq('is_archived', true)
                .order('archived_at', { ascending: false })

            if (error) throw error
            setArchivedVariants(data || [])
        } catch (err) {
            console.error("Error fetching archived variants:", err)
            setArchivedVariants([])
        } finally {
            setLoadingArchivedItems(false)
        }
    }

    const fetchArchivedModifiers = async () => {
        try {
            setLoadingArchivedItems(true)

            const { data, error } = await supabase
                .from('item_modifiers')
                .select('*')
                .eq('is_archived', true)
                .order('archived_at', { ascending: false })

            if (error) throw error
            setArchivedModifiers(data || [])
        } catch (err) {
            console.error("Error fetching archived modifiers:", err)
            setArchivedModifiers([])
        } finally {
            setLoadingArchivedItems(false)
        }
    }

    const fetchPriceChangeRequests = async () => {
        try {
            setLoadingPriceRequests(true)
            const { data, error } = await supabase
                .from('menu_price_change_requests')
                .select(`
                    id,
                    item_type,
                    item_id,
                    item_name,
                    current_price,
                    requested_price,
                    reason,
                    status,
                    branch_id,
                    requested_by_staff_id,
                    created_at,
                    updated_at
                `)
                .order('created_at', { ascending: false })

            if (error) throw error
            setPriceRequests(data || [])
        } catch (err) {
            console.error('Error fetching price change requests:', err)
            setPriceRequests([])
        } finally {
            setLoadingPriceRequests(false)
        }
    }

    const fetchMenuChangeRequests = async () => {
        try {
            setLoadingMenuChangeRequests(true)
            const { data, error } = await supabase
                .from('admin_requests')
                .select('id, request_type, item_type, item_id, item_name, reason, changes, status, branch_id, created_at')
                .in('item_type', ['menu', 'variant', 'modifier'])
                .in('request_type', ['add', 'delete', 'reinstate'])
                .order('created_at', { ascending: false })
            if (error) throw error
            setMenuChangeRequests(data || [])
        } catch (err) {
            console.error('Error fetching menu change requests:', err)
            setMenuChangeRequests([])
        } finally {
            setLoadingMenuChangeRequests(false)
        }
    }

    const openPriceRequestsModal = async () => {
        setShowPriceRequestsModal(true)
        await fetchPriceChangeRequests()
    }

    const closePriceRequestsModal = () => {
        setShowPriceRequestsModal(false)
        setPriceRequestFilter('pending')
        setPriceRequestsModalPage(1)
    }

    const handleApprovePriceRequest = async (request) => {
        const result = await Swal.fire({
            title: 'Approve Price Change?',
            html: `
                    <p class="text-gray-600 mb-3">You are about to update the price for:</p>
                    <p class="font-semibold text-gray-800">${request.item_name}</p>
                    <p class="text-gray-500 mt-2">
                        <span class="line-through text-red-400">₱${parseFloat(request.current_price).toFixed(2)}</span>
                        <span class="mx-2">→</span>
                        <span class="text-green-600 font-bold">₱${parseFloat(request.requested_price).toFixed(2)}</span>
                    </p>
                `,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#16a34a',
            cancelButtonColor: '#6b7280',
            confirmButtonText: 'Yes, approve & update',
            cancelButtonText: 'Cancel'
        })

        if (!result.isConfirmed) return

        try {
            setProcessingPriceRequestId(request.id)

            // Update the actual price on the correct table
            if (request.item_type === 'menu_item') {
                if (request.branch_id) {
                    // Branch-specific request — update branch_price only
                    const { error } = await supabase
                        .from('branch_menu_items')
                        .update({ branch_price: request.requested_price })
                        .eq('branch_id', request.branch_id)
                        .eq('item_id', request.item_id)
                    if (error) throw error
                } else {
                    // No branch — update master price
                    const { error } = await supabase
                        .from('menu_items')
                        .update({ item_price: request.requested_price })
                        .eq('item_id', request.item_id)
                    if (error) throw error
                }
            } else if (request.item_type === 'variant') {
                const { error } = await supabase
                    .from('item_variants')
                    .update({ price: request.requested_price })
                    .eq('variant_id', request.item_id)
                if (error) throw error
            } else if (request.item_type === 'modifier') {
                const { error } = await supabase
                    .from('item_modifiers')
                    .update({ price: request.requested_price })
                    .eq('modifier_id', request.item_id)
                if (error) throw error
            }

            // Mark request as approved
            const { error: updateError } = await supabase
                .from('menu_price_change_requests')
                .update({ status: 'approved' })
                .eq('id', request.id)

            if (updateError) throw updateError

            await fetchPriceChangeRequests()

            // Refresh the relevant list so the table reflects new price immediately
            if (request.item_type === 'menu_item') fetchMenuItems()
            else if (request.item_type === 'variant') fetchVariants()
            else if (request.item_type === 'modifier') fetchModifiers()

            Swal.fire({
                icon: 'success',
                title: 'Price Updated!',
                text: `${request.item_name} is now ₱${parseFloat(request.requested_price).toFixed(2)}`,
                confirmButtonColor: '#FF6F3C',
                timer: 2500
            })
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Failed to Approve', text: err.message, confirmButtonColor: '#FF6F3C' })
        } finally {
            setProcessingPriceRequestId(null)
        }
    }

    const handleRejectPriceRequest = (request) => {
        setRejectingRequest(request)
        setRejectingSource('price')
        setRejectionReason('')
        setShowRejectReasonModal(true)
    }

    const handleRejectMenuChangeRequestWithReason = (request) => {
        setRejectingRequest(request)
        setRejectingSource('admin')
        setRejectionReason('')
        setShowRejectReasonModal(true)
    }

    const handleSubmitRejection = async () => {
        if (!rejectionReason.trim()) {
            Swal.fire({ icon: 'warning', title: 'Reason Required', text: 'Please provide a reason for rejection.', confirmButtonColor: '#FF6F3C' })
            return
        }

        try {
            setSubmittingRejection(true)

            const table = rejectingSource === 'price' ? 'menu_price_change_requests' : 'admin_requests'

            const { error } = await supabase
                .from(table)
                .update({ status: 'rejected', rejection_reason: rejectionReason.trim() })
                .eq('id', rejectingRequest.id)

            if (error) throw error

            setShowRejectReasonModal(false)
            setRejectingRequest(null)
            setRejectionReason('')

            if (rejectingSource === 'price') {
                await fetchPriceChangeRequests()
            } else {
                await fetchMenuChangeRequests()
            }

            Swal.fire({
                icon: 'info',
                title: 'Request Rejected',
                text: `The ${rejectingSource === 'price' ? 'price change' : ''} request has been rejected.`,
                confirmButtonColor: '#FF6F3C',
                timer: 2000
            })
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Failed to Reject', text: err.message, confirmButtonColor: '#FF6F3C' })
        } finally {
            setSubmittingRejection(false)
        }
    }

    const handleApproveMenuChangeRequest = async (request) => {
        const isAdd = request.request_type === 'add'
        const isReinstate = request.request_type === 'reinstate'
        const typeLabel = request.item_type === 'variant' ? 'Variant' : request.item_type === 'modifier' ? 'Modifier' : 'Item'
        const result = await Swal.fire({
            title: isAdd ? `Approve New ${typeLabel}?` : isReinstate ? `Approve ${typeLabel} Reinstatement?` : `Approve ${typeLabel} Deletion?`,
            html: `<p class="font-semibold text-gray-800">${request.item_name}</p>`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#16a34a',
            cancelButtonColor: '#6b7280',
            confirmButtonText: isAdd ? `Yes, add ${typeLabel.toLowerCase()}` : isReinstate ? `Yes, reinstate ${typeLabel.toLowerCase()}` : `Yes, archive ${typeLabel.toLowerCase()}`,
            cancelButtonText: 'Cancel'
        })
        if (!result.isConfirmed) return

        try {
            setProcessingMenuChangeRequestId(request.id)

            if (request.item_type === 'variant') {
                if (isAdd) {
                    const details = JSON.parse(request.changes)
                    if (isDuplicateVariant(details.category, details.variant_name)) {
                        Swal.fire({ icon: 'warning', title: 'Duplicate Variant', text: `"${details.variant_name}" already exists — cannot approve.`, confirmButtonColor: '#FF6F3C' })
                        return
                    }
                    const { error } = await supabase.from('item_variants').insert([{
                        category: details.category,
                        variant_name: details.variant_name,
                        price: details.price,
                        applicable_categories: details.applicable_categories || []
                    }])
                    if (error) throw error
                } else if (isReinstate) {
                    const { error } = await supabase
                        .from('item_variants')
                        .update({ is_archived: false, archived_at: null })
                        .eq('variant_id', request.item_id)
                    if (error) throw error
                } else {
                    const { error } = await supabase
                        .from('item_variants')
                        .update({ is_archived: true, archived_at: new Date().toISOString() })
                        .eq('variant_id', request.item_id)
                    if (error) throw error
                }
                await fetchVariants()
            } else if (request.item_type === 'modifier') {
                if (isAdd) {
                    const details = JSON.parse(request.changes)
                    if (isDuplicateModifier(details.modifier_name, details.category)) {
                        Swal.fire({ icon: 'warning', title: 'Duplicate Modifier', text: `"${details.modifier_name}" already exists — cannot approve.`, confirmButtonColor: '#FF6F3C' })
                        return
                    }
                    const { error } = await supabase.from('item_modifiers').insert([{
                        modifier_name: details.modifier_name,
                        category: details.category,
                        price: details.price,
                        applicable_categories: details.applicable_categories || [],
                        ingredient_id: details.ingredient_id || null
                    }])
                    if (error) throw error
                } else if (isReinstate) {
                    const { error } = await supabase
                        .from('item_modifiers')
                        .update({ is_archived: false, archived_at: null })
                        .eq('modifier_id', request.item_id)
                    if (error) throw error
                } else {
                    const { error } = await supabase
                        .from('item_modifiers')
                        .update({ is_archived: true, archived_at: new Date().toISOString() })
                        .eq('modifier_id', request.item_id)
                    if (error) throw error
                }
                await fetchModifiers()
            } else {
                if (isAdd) {
                    const details = JSON.parse(request.changes)
                    if (isDuplicateMenuItem(details.name, details.category, details.classification)) {
                        Swal.fire({ icon: 'warning', title: 'Duplicate Item', text: `"${details.name}" already exists — cannot approve.`, confirmButtonColor: '#FF6F3C' })
                        return
                    }
                    const { data: inserted, error } = await supabase
                        .from('menu_items')
                        .insert([{
                            name: details.name,
                            item_price: details.item_price,
                            category: details.category,
                            classification: details.classification,
                            description: details.description,
                            item_img: details.item_img || null,
                            status: 'available'
                        }])
                        .select()
                        .single()
                    if (error) throw error

                    const branchIds = Array.isArray(details.branch_ids) ? details.branch_ids : []
                    if (branchIds.length > 0) {
                        const rows = branchIds.map(branch_id => ({ item_id: inserted.item_id, branch_id }))
                        const { error: branchError } = await supabase.from('branch_menu_items').insert(rows)
                        if (branchError) throw branchError
                    }
                } else if (isReinstate) {
                    const { error } = await supabase
                        .from('menu_items')
                        .update({ is_archived: false, archived_at: null })
                        .eq('item_id', request.item_id)
                    if (error) throw error
                } else {
                    const { error } = await supabase
                        .from('menu_items')
                        .update({ is_archived: true, archived_at: new Date().toISOString() })
                        .eq('item_id', request.item_id)
                    if (error) throw error
                }
                await fetchMenuItems()
                await fetchBranchMenuItems('all')
                if (selectedBranchForMenu !== 'all') {
                    await fetchBranchMenuItems(selectedBranchForMenu)
                }
            }

            const { error: approveError } = await supabase.from('admin_requests').update({ status: 'approved' }).eq('id', request.id)
            if (approveError) throw approveError
            await fetchMenuChangeRequests()

            Swal.fire({ icon: 'success', title: isAdd ? `${typeLabel} Added!` : isReinstate ? `${typeLabel} Reinstated!` : `${typeLabel} Archived!`, confirmButtonColor: '#FF6F3C', timer: 2500 })
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Failed to Approve', text: err.message, confirmButtonColor: '#FF6F3C' })
        } finally {
            setProcessingMenuChangeRequestId(null)
        }
    }


    const fetchBranchMenuItems = async (branchId = 'all') => {
        try {
            setBranchMenuLoading(true)

            const buildQuery = () => {
                let q = supabase
                    .from('branch_menu_items')
                    .select(`
                        id,
                        branch_id,
                        item_id,
                        branch_price,
                        branches:branch_id (
                            branch_id,
                            name
                        ),
                         menu_items:item_id (
                            item_id,
                            name,
                            item_price,
                            category,
                            classification,
                            item_img,
                            is_archived,
                            status
                        )
                    `)
                    .eq('menu_items.is_archived', false)

                if (branchId !== 'all') {
                    q = q.eq('branch_id', branchId)
                }
                return q
            }

            const PAGE_SIZE = 1000
            let data = []
            let from = 0
            while (true) {
                const { data: page, error } = await buildQuery().range(from, from + PAGE_SIZE - 1)
                if (error) throw error
                if (!page || page.length === 0) break
                data = data.concat(page)
                if (page.length < PAGE_SIZE) break
                from += PAGE_SIZE
            }

            const formatted = (data || [])
                .filter((row) => row.menu_items && row.menu_items.is_archived === false && row.menu_items.status !== 'discontinued')
                .map((row) => {
                    const masterPrice = parseFloat(row.menu_items?.item_price ?? 0)
                    const rawBranchPrice = row.branch_price
                    const hasPriceOverride = rawBranchPrice !== null && rawBranchPrice !== undefined
                    const effectivePrice = hasPriceOverride ? parseFloat(rawBranchPrice) : masterPrice

                    return {
                        id: row.id,
                        branch_id: row.branch_id,
                        item_id: row.item_id,
                        branch_name: formatBranchName(row.branches?.name) || 'Unknown Branch',
                        name: row.menu_items?.name || 'Unnamed Item',
                        price: effectivePrice,
                        master_price: masterPrice,
                        has_price_override: hasPriceOverride,
                        category: row.menu_items?.category || 'Uncategorized',
                        classification: row.menu_items?.classification || 'N/A',
                        item_img: row.menu_items?.item_img || null,
                    }
                })

            setBranchMenuItems(formatted)

            if (branchId === 'all') {
                setAllBranchMenuItems(formatted)
            }
        } catch (err) {
            console.error('Error fetching branch menu items:', err)
            setBranchMenuItems([])
        } finally {
            setBranchMenuLoading(false)
        }
    }

    const resetVariantRuleForm = () => {
        const emptyRule = {
            id: null,
            variant_id: '',
            ingredient_id: '',
            quantity_delta: '',
            unit: '',
            notes: ''
        }

        setVariantRuleFormData(emptyRule)
        setInitialVariantRuleFormData(emptyRule)
        setVariantRuleVariantSearch('')
        setVariantRuleIngredientSearch('')
        setOpenVariantDropdown(false)
        setOpenIngredientDropdown(false)
    }

    const openVariantRulesModal = async (item) => {
        setSelectedItemForVariantRules(item)
        setShowVariantRulesModal(true)

        const emptyRule = {
            id: null,
            variant_id: '',
            ingredient_id: '',
            quantity_delta: '',
            unit: '',
            notes: ''
        }

        setVariantRuleFormData(emptyRule)
        setInitialVariantRuleFormData(emptyRule)
        setVariantRuleVariantSearch('')
        setVariantRuleIngredientSearch('')
        setOpenVariantDropdown(false)
        setOpenIngredientDropdown(false)

        await Promise.all([
            fetchItemVariants(item.item_id),
            fetchIngredients(),
            fetchVariantRules(item.item_id)
        ])
    }

    const closeVariantRulesModal = () => {
        setShowVariantRulesModal(false)
        setSelectedItemForVariantRules(null)
        setItemVariants([])
        setVariantRules([])
        setVariantRuleTableSearch('')
        resetVariantRuleForm()
    }

    const handleSaveVariantRule = async () => {
        if (!hasVariantRuleChanges) return

        if (!selectedItemForVariantRules?.item_id) {
            Swal.fire({
                icon: 'warning',
                title: 'No item selected',
                text: 'Please select a menu item first.',
                confirmButtonColor: '#FF6F3C'
            })
            return
        }

        if (!variantRuleFormData.variant_id || !variantRuleFormData.ingredient_id || variantRuleFormData.quantity_delta === '') {
            Swal.fire({
                icon: 'warning',
                title: 'Missing Information',
                text: 'Please fill in variant, ingredient, and quantity.',
                confirmButtonColor: '#FF6F3C'
            })
            return
        }

        const quantity = parseFloat(variantRuleFormData.quantity_delta)
        if (isNaN(quantity) || quantity < 0) {
            Swal.fire({
                icon: 'warning',
                title: 'Invalid Quantity',
                text: 'Quantity delta must be 0 or greater.',
                confirmButtonColor: '#FF6F3C'
            })
            return
        }

        try {
            setSavingVariantRule(true)

            const selectedIngredient = ingredients.find(
                ing => String(ing.ingredient_id) === String(variantRuleFormData.ingredient_id)
            )

            if (!selectedIngredient?.unit || !String(selectedIngredient.unit).trim()) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Missing Ingredient Unit',
                    text: 'The selected ingredient has no unit. Please set its unit first in Inventory Management.',
                    confirmButtonColor: '#FF6F3C'
                })
                return
            }

            const payload = {
                item_id: selectedItemForVariantRules.item_id,
                variant_id: variantRuleFormData.variant_id,
                ingredient_id: variantRuleFormData.ingredient_id,
                quantity_delta: quantity,
                unit: String(selectedIngredient.unit).trim(),
                notes: variantRuleFormData.notes.trim() || null
            }

            if (variantRuleFormData.id) {
                const { error } = await supabase
                    .schema('inventory')
                    .from('menu_item_variant_ingredient_rules')
                    .update(payload)
                    .eq('id', variantRuleFormData.id)

                if (error) throw error
            } else {
                const { error } = await supabase
                    .schema('inventory')
                    .from('menu_item_variant_ingredient_rules')
                    .insert([payload])

                if (error) throw error
            }

            resetVariantRuleForm()
            await fetchVariantRules(selectedItemForVariantRules.item_id)

            Swal.fire({
                icon: 'success',
                title: variantRuleFormData.id ? 'Rule Updated!' : 'Rule Added!',
                text: 'Variant ingredient rule has been saved successfully.',
                confirmButtonColor: '#FF6F3C',
                timer: 1800
            })
        } catch (err) {
            console.error("Error saving variant rule:", err)
            Swal.fire({
                icon: 'error',
                title: 'Failed to Save Rule',
                text: err.message,
                confirmButtonColor: '#FF6F3C'
            })
        } finally {
            setSavingVariantRule(false)
        }
    }

    const handleEditVariantRule = (rule) => {
        const selectedVariant = itemVariants.find(
            (variant) => String(variant.variant_id) === String(rule.variant_id)
        )

        const selectedIngredient = ingredients.find(
            (ingredient) => String(ingredient.ingredient_id) === String(rule.ingredient_id)
        )

        const normalizedRuleData = {
            id: rule.id,
            variant_id: rule.variant_id || '',
            ingredient_id: rule.ingredient_id || '',
            quantity_delta: String(rule.quantity_delta ?? ''),
            unit: rule.unit || selectedIngredient?.unit || '',
            notes: rule.notes || ''
        }

        setVariantRuleFormData(normalizedRuleData)
        setInitialVariantRuleFormData(normalizedRuleData)

        setVariantRuleVariantSearch(
            selectedVariant
                ? `${selectedVariant.category ? `${selectedVariant.category} - ` : ''}${selectedVariant.variant_name || ''}`
                : ''
        )

        setVariantRuleIngredientSearch(
            selectedIngredient?.ingredient_name || selectedIngredient?.name || ''
        )

        setOpenVariantDropdown(false)
        setOpenIngredientDropdown(false)
    }

    const handleDeleteVariantRule = async (ruleId) => {
        const result = await Swal.fire({
            title: 'Delete Variant Rule?',
            text: 'This rule will be permanently removed.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            cancelButtonColor: '#6b7280',
            confirmButtonText: 'Yes, delete',
            cancelButtonText: 'Cancel'
        })

        if (!result.isConfirmed) return

        try {
            const { error } = await supabase
                .schema('inventory')
                .from('menu_item_variant_ingredient_rules')
                .delete()
                .eq('id', ruleId)

            if (error) throw error

            if (variantRuleFormData.id === ruleId) {
                resetVariantRuleForm()
            }

            await fetchVariantRules(selectedItemForVariantRules.item_id)

            Swal.fire({
                icon: 'success',
                title: 'Rule Deleted',
                text: 'Variant ingredient rule has been removed.',
                confirmButtonColor: '#FF6F3C',
                timer: 1800
            })
        } catch (err) {
            console.error("Error deleting variant rule:", err)
            Swal.fire({
                icon: 'error',
                title: 'Failed to Delete Rule',
                text: err.message,
                confirmButtonColor: '#FF6F3C'
            })
        }
    }

    // === Modifier assignment ──────────────────────────────────────────────────

    const openAssignModifiersModal = async (item) => {
        setSelectedItemForModifiers(item)
        setLoadingAssignments(true)
        setShowAssignModifiersModal(true)

        try {
            await fetchModifiers()

            const { data, error } = await supabase
                .from('menu_modifiers')
                .select('modifier_id')
                .eq('item_id', item.item_id)

            if (error) throw error

            const modifierSet = new Set((data || []).map(r => r.modifier_id))
            setAssignedModifierIds(modifierSet)
            setInitialAssignedModifierIds(new Set(modifierSet))
        } catch (err) {
            console.error("Error fetching assignments:", err)
            Swal.fire({
                icon: 'error',
                title: 'Failed to load assignments',
                text: err.message,
                confirmButtonColor: '#FF6F3C'
            })
        } finally {
            setLoadingAssignments(false)
        }
    }

    const closeAssignModifiersModal = () => {
        setShowAssignModifiersModal(false)
        setSelectedItemForModifiers(null)
        setAssignedModifierIds(new Set())
        setInitialAssignedModifierIds(new Set())
        setAssignModifierCategoryFilter('All')
        setAssignModifierSearch('')
    }

    const toggleModifierAssignment = (modifierId) => {
        setAssignedModifierIds(prev => {
            const next = new Set(prev)
            if (next.has(modifierId)) { next.delete(modifierId) } else { next.add(modifierId) }
            return next
        })
    }

    const handleSelectAllModifiersInCategory = () => {
        const itemCategory = selectedItemForModifiers?.category || ''
        const relevantModifiers = modifiers.filter(m => {
            if (m.is_archived) return false
            const cats = m.applicable_categories || []
            const isApplicable = cats.length > 0 && cats.includes(itemCategory)
            const matchesFilter = assignModifierCategoryFilter === 'All' || m.category === assignModifierCategoryFilter
            const matchesSearch = assignModifierSearch === '' ||
                String(m.modifier_name || '').toLowerCase().includes(assignModifierSearch.toLowerCase()) ||
                String(m.category || '').toLowerCase().includes(assignModifierSearch.toLowerCase())
            return isApplicable && matchesFilter && matchesSearch
        })
        setAssignedModifierIds(prev => {
            const next = new Set(prev)
            relevantModifiers.forEach(m => next.add(m.modifier_id))
            return next
        })
    }

    const handleClearAllModifiersInCategory = () => {
        const itemCategory = selectedItemForModifiers?.category || ''
        const relevantModifiers = modifiers.filter(m => {
            if (m.is_archived) return false
            const cats = m.applicable_categories || []
            const isApplicable = cats.length > 0 && cats.includes(itemCategory)
            const matchesFilter = assignModifierCategoryFilter === 'All' || m.category === assignModifierCategoryFilter
            const matchesSearch = assignModifierSearch === '' ||
                String(m.modifier_name || '').toLowerCase().includes(assignModifierSearch.toLowerCase()) ||
                String(m.category || '').toLowerCase().includes(assignModifierSearch.toLowerCase())
            return isApplicable && matchesFilter && matchesSearch
        })
        setAssignedModifierIds(prev => {
            const next = new Set(prev)
            relevantModifiers.forEach(m => next.delete(m.modifier_id))
            return next
        })
    }

    const toggleBranchSelection = (branchId) => {
        setSelectedBranchIds(prev => {
            const next = new Set(prev)
            if (next.has(branchId)) {
                next.delete(branchId)
            } else {
                next.add(branchId)
            }
            return next
        })
    }

    const allActiveBranchIds = branches.map(branch => branch.branch_id)

    const areAllBranchesSelected =
        branches.length > 0 && selectedBranchIds.size === branches.length

    const handleSelectAllBranches = () => {
        setSelectedBranchIds(new Set(allActiveBranchIds))
    }

    const handleClearAllBranches = () => {
        setSelectedBranchIds(new Set())
    }

    const handleToggleMenuBranch = (branchId) => {
        if (expandedBranchId === branchId) {
            setExpandedBranchId(null)
        } else {
            setExpandedBranchId(branchId)
            setBranchMenuSubTab(prev => ({
                ...prev,
                [branchId]: prev[branchId] || 'menu_items'
            }))
            setBranchMenuItemPages(prev => ({ ...prev, [branchId]: 1 }))
            setBranchPriceRequestPages(prev => ({ ...prev, [branchId]: 1 }))
            setBranchMenuChangeRequestPages(prev => ({ ...prev, [branchId]: 1 }))
            setBranchVariantChangeRequestPages(prev => ({ ...prev, [branchId]: 1 }))
            setBranchModifierChangeRequestPages(prev => ({ ...prev, [branchId]: 1 }))
        }
    }

    const handleToggleSelectAllBranches = () => {
        if (areAllBranchesSelected) {
            handleClearAllBranches()
        } else {
            handleSelectAllBranches()
        }
    }

    const saveModifierAssignments = async () => {
        if (!hasModifierAssignmentChanges) return

        if (!selectedItemForModifiers) return

        setSavingAssignments(true)

        try {
            const { error: deleteError } = await supabase
                .from('menu_modifiers')
                .delete()
                .eq('item_id', selectedItemForModifiers.item_id)

            if (deleteError) throw deleteError

            if (assignedModifierIds.size > 0) {
                const rows = Array.from(assignedModifierIds).map(modifier_id => ({
                    item_id: selectedItemForModifiers.item_id,
                    modifier_id
                }))

                const { error: insertError } = await supabase
                    .from('menu_modifiers')
                    .insert(rows)

                if (insertError) throw insertError
            }

            closeAssignModifiersModal()

            Swal.fire({
                icon: 'success',
                title: 'Modifiers updated!',
                text: `Modifiers for ${selectedItemForModifiers.name} have been saved`,
                confirmButtonColor: '#FF6F3C',
                timer: 2500
            })
        } catch (err) {
            console.error("Error saving assignments:", err)
            Swal.fire({
                icon: 'error',
                title: 'Failed to save',
                text: err.message,
                confirmButtonColor: '#FF6F3C'
            })
        } finally {
            setSavingAssignments(false)
        }
    }

    // === Archived ───────────────────────────────────────────────────────────

    const openArchivedViewerModal = async () => {
        setShowArchivedViewerModal(true)

        if (activeTab === 'menu') {
            await fetchArchivedMenuItems()
        } else if (activeTab === 'variants') {
            await fetchArchivedVariants()
        } else if (activeTab === 'modifiers') {
            await fetchArchivedModifiers()
        }
    }

    const closeArchivedViewerModal = () => {
        setShowArchivedViewerModal(false)
        setArchivedMenuItems([])
        setArchivedVariants([])
        setArchivedModifiers([])
        setArchivedSearch('')
        setArchivedPage(1)
        setArchivedCategoryFilter('All')
        setArchivedClassificationFilter('All')
        setArchivedAvailableClassifications([])
    }

    // === Reinstate ───────────────────────────────────────────────────────────

    const handleReinstateItem = async (item) => {
        if (
            isDuplicateMenuItem(
                item.name,
                item.category,
                item.classification,
                item.item_id
            )
        ) {
            Swal.fire({
                icon: "warning",
                title: "Cannot Reinstate Item",
                text: `"${item.name}" already exists as an active menu item.`,
                confirmButtonColor: "#FF6F3C"
            })
            return
        }

        const result = await Swal.fire({
            title: "Reinstate Menu Item?",
            text: `${item.name} will return to the active menu list.`,
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#16a34a",
            cancelButtonColor: "#6b7280",
            confirmButtonText: "Yes, reinstate",
            cancelButtonText: "Cancel"
        })

        if (!result.isConfirmed) return

        try {
            const { error } = await supabase
                .from('menu_items')
                .update({
                    is_archived: false,
                    archived_at: null
                })
                .eq('item_id', item.item_id)

            if (error) throw error

            Swal.fire({
                icon: "success",
                title: "Item Reinstated",
                text: `${item.name} is active again.`,
                confirmButtonColor: "#FF6F3C",
                timer: 1800
            })

            await fetchMenuItems()
            await fetchArchivedMenuItems()
        } catch (err) {
            Swal.fire({
                icon: "error",
                title: "Failed to Reinstate",
                text: err.message,
                confirmButtonColor: "#FF6F3C"
            })
        }
    }

    const handleReinstateVariant = async (variant) => {
        if (isDuplicateActiveVariant(variant)) {
            Swal.fire({
                icon: "warning",
                title: "Cannot Reinstate Variant",
                text: `The variant "${variant.variant_name}" already exists as an active variant under "${normalizeVariantCategory(variant.category || '')}".`,
                confirmButtonColor: "#FF6F3C"
            })
            return
        }

        const result = await Swal.fire({
            title: "Reinstate Variant?",
            text: `"${variant.variant_name}" will become available again.`,
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#16a34a",
            cancelButtonColor: "#6b7280",
            confirmButtonText: "Yes, reinstate",
            cancelButtonText: "Cancel"
        })

        if (!result.isConfirmed) return

        try {
            const { error } = await supabase
                .from('item_variants')
                .update({
                    is_archived: false,
                    archived_at: null
                })
                .eq('variant_id', variant.variant_id)

            if (error) throw error

            Swal.fire({
                icon: "success",
                title: "Variant Reinstated",
                text: `${variant.variant_name} is active again.`,
                confirmButtonColor: "#FF6F3C",
                timer: 1800
            })

            await fetchVariants()
            await fetchArchivedVariants()
        } catch (err) {
            Swal.fire({
                icon: "error",
                title: "Failed to Reinstate Variant",
                text: err.message,
                confirmButtonColor: "#FF6F3C"
            })
        }
    }

    const handleReinstateModifier = async (modifier) => {
        if (isDuplicateActiveModifier(modifier)) {
            Swal.fire({
                icon: "warning",
                title: "Cannot Reinstate Modifier",
                text: `The modifier "${modifier.modifier_name}" already exists as an active modifier under "${normalizeModifierCategory(modifier.category || '')}".`,
                confirmButtonColor: "#FF6F3C"
            })
            return
        }

        const result = await Swal.fire({
            title: "Reinstate Modifier?",
            text: `"${modifier.modifier_name}" will become available again.`,
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#16a34a",
            cancelButtonColor: "#6b7280",
            confirmButtonText: "Yes, reinstate",
            cancelButtonText: "Cancel"
        })

        if (!result.isConfirmed) return

        try {
            const { error } = await supabase
                .from('item_modifiers')
                .update({
                    is_archived: false,
                    archived_at: null
                })
                .eq('modifier_id', modifier.modifier_id)

            if (error) throw error

            Swal.fire({
                icon: "success",
                title: "Modifier Reinstated",
                text: `${modifier.modifier_name} is active again.`,
                confirmButtonColor: "#FF6F3C",
                timer: 1800
            })

            await fetchModifiers()
            await fetchArchivedModifiers()
        } catch (err) {
            Swal.fire({
                icon: "error",
                title: "Failed to Reinstate Modifier",
                text: err.message,
                confirmButtonColor: "#FF6F3C"
            })
        }
    }

    // === Validation ───────────────────────────────────────────────────────────

    const validateItemForm = () => {
        if (itemFormData.name.length > 50) { Swal.fire({ icon: "warning", title: "Invalid Item Name", text: "Item name must not exceed 50 characters", confirmButtonColor: "#FF6F3C" }); return false }
        if (itemFormData.name.trim().length < 2) { Swal.fire({ icon: "warning", title: "Invalid Item Name", text: "Item name must be at least 2 characters long", confirmButtonColor: "#FF6F3C" }); return false }
        const price = parseFloat(itemFormData.item_price)
        if (isNaN(price) || price <= 0) { Swal.fire({ icon: "warning", title: "Invalid Price", text: "Price must be a positive number greater than 0", confirmButtonColor: "#FF6F3C" }); return false }
        if (price > 9999) { Swal.fire({ icon: "warning", title: "Invalid Price", text: "Price must not exceed ₱9,999", confirmButtonColor: "#FF6F3C" }); return false }
        if (itemFormData.description.length > 500) { Swal.fire({ icon: "warning", title: "Invalid Description", text: "Description must not exceed 100 characters", confirmButtonColor: "#FF6F3C" }); return false }
        if (itemFormData.description.trim().length < 10) { Swal.fire({ icon: "warning", title: "Invalid Description", text: "Description must be at least 10 characters long", confirmButtonColor: "#FF6F3C" }); return false }
        if (imageInputMode === 'url' && itemFormData.item_img && itemFormData.item_img.trim() !== "") {
            const urlRegex = /^https?:\/\/.+/i
            if (!urlRegex.test(itemFormData.item_img.trim())) {
                Swal.fire({
                    icon: "warning",
                    title: "Invalid Image URL",
                    text: "Please enter a valid image URL starting with http:// or https://",
                    confirmButtonColor: "#FF6F3C"
                })
                return false
            }
        }
        return true
    }

    const validateVariantForm = () => {
        if (String(variantFormData.category || '').trim().length < 2) {
            Swal.fire({
                icon: "warning",
                title: "Invalid Category",
                text: "Variant category must be at least 2 characters long",
                confirmButtonColor: "#FF6F3C"
            })
            return false
        }

        if (String(variantFormData.category || '').trim().length > 30) {
            Swal.fire({
                icon: "warning",
                title: "Invalid Category",
                text: "Variant category must not exceed 30 characters",
                confirmButtonColor: "#FF6F3C"
            })
            return false
        }

        if (variantFormData.variant_name.length > 30) {
            Swal.fire({
                icon: "warning",
                title: "Invalid Variant Name",
                text: "Variant name must not exceed 30 characters",
                confirmButtonColor: "#FF6F3C"
            })
            return false
        }

        if (variantFormData.variant_name.trim().length < 2) {
            Swal.fire({
                icon: "warning",
                title: "Invalid Variant Name",
                text: "Variant name must be at least 2 characters long",
                confirmButtonColor: "#FF6F3C"
            })
            return false
        }

        const price = parseFloat(variantFormData.price)
        if (isNaN(price) || price < 0) {
            Swal.fire({
                icon: "warning",
                title: "Invalid Price",
                text: "Price must be a valid number (can be 0 for no additional charge)",
                confirmButtonColor: "#FF6F3C"
            })
            return false
        }

        if (price > 1000) {
            Swal.fire({
                icon: "warning",
                title: "Invalid Price",
                text: "Variant price must not exceed ₱1,000",
                confirmButtonColor: "#FF6F3C"
            })
            return false
        }

        return true
    }

    const validateModifierForm = () => {
        if (modifierFormData.modifier_name.length > 50) { Swal.fire({ icon: "warning", title: "Invalid Modifier Name", text: "Modifier name must not exceed 50 characters", confirmButtonColor: "#FF6F3C" }); return false }
        if (modifierFormData.modifier_name.trim().length < 2) { Swal.fire({ icon: "warning", title: "Invalid Modifier Name", text: "Modifier name must be at least 2 characters long", confirmButtonColor: "#FF6F3C" }); return false }
        const price = parseFloat(modifierFormData.price)
        if (isNaN(price) || price < 0) { Swal.fire({ icon: "warning", title: "Invalid Price", text: "Price must be a valid number (can be 0 for no additional charge)", confirmButtonColor: "#FF6F3C" }); return false }
        if (price > 500) { Swal.fire({ icon: "warning", title: "Invalid Price", text: "Modifier price must not exceed ₱500", confirmButtonColor: "#FF6F3C" }); return false }
        return true
    }

    const findDuplicateMenuItem = (name, category, classification, excludeItemId = null) => {
        const normalizedName = String(name || '').trim().toLowerCase()
        const normalizedCategory = normalizeItemCategory(category || '').toLowerCase()
        const normalizedClassification = normalizeItemClassification(classification || '').toLowerCase()

        return menuItems.find((item) => {
            if (excludeItemId && String(item.item_id) === String(excludeItemId)) {
                return false
            }

            return (
                String(item.name || '').trim().toLowerCase() === normalizedName &&
                normalizeItemCategory(item.category || '').toLowerCase() === normalizedCategory &&
                normalizeItemClassification(item.classification || '').toLowerCase() === normalizedClassification
            )
        }) || null
    }

    const isDuplicateMenuItem = (name, category, classification, excludeItemId = null) =>
        findDuplicateMenuItem(name, category, classification, excludeItemId) !== null

    const isDuplicateActiveMenuItem = (item) => {
        const normalizedName = String(item.name || '').trim().toLowerCase()
        const normalizedCategory = normalizeItemCategory(item.category || '').toLowerCase()
        const normalizedClassification = normalizeItemClassification(item.classification || '').toLowerCase()

        return menuItems.some((activeItem) => {
            return (
                String(activeItem.item_id) !== String(item.item_id) &&
                String(activeItem.name || '').trim().toLowerCase() === normalizedName &&
                normalizeItemCategory(activeItem.category || '').toLowerCase() === normalizedCategory &&
                normalizeItemClassification(activeItem.classification || '').toLowerCase() === normalizedClassification
            )
        })
    }

    const isDuplicateVariant = (category, variantName, excludeVariantId = null) => {
        const normalizedCategory = normalizeVariantCategory(category)
        const normalizedName = normalizeVariantName(variantName)

        return variants.some((variant) => {
            if (excludeVariantId && String(variant.variant_id) === String(excludeVariantId)) {
                return false
            }

            return (
                normalizeVariantCategory(variant.category) === normalizedCategory &&
                normalizeVariantName(variant.variant_name) === normalizedName
            )
        })
    }

    const isDuplicateActiveVariant = (variant) => {
        const normalizedCategory = normalizeVariantCategory(variant.category || '')
        const normalizedName = normalizeVariantName(variant.variant_name || '')

        return variants.some((activeVariant) => {
            return (
                String(activeVariant.variant_id) !== String(variant.variant_id) &&
                normalizeVariantCategory(activeVariant.category || '') === normalizedCategory &&
                normalizeVariantName(activeVariant.variant_name || '') === normalizedName
            )
        })
    }

    const isDuplicateModifier = (modifierName, category, excludeModifierId = null) => {
        const normalizedName = normalizeModifierName(modifierName)
        const normalizedCategory = normalizeModifierCategory(category)

        return modifiers.some((modifier) => {
            if (excludeModifierId && String(modifier.modifier_id) === String(excludeModifierId)) {
                return false
            }

            return (
                normalizeModifierName(modifier.modifier_name) === normalizedName &&
                normalizeModifierCategory(modifier.category) === normalizedCategory
            )
        })
    }

    const isDuplicateActiveModifier = (modifier) => {
        const normalizedCategory = normalizeModifierCategory(modifier.category || '')
        const normalizedName = normalizeModifierName(modifier.modifier_name || '')

        return modifiers.some((activeModifier) => {
            return (
                String(activeModifier.modifier_id) !== String(modifier.modifier_id) &&
                normalizeModifierCategory(activeModifier.category || '') === normalizedCategory &&
                normalizeModifierName(activeModifier.modifier_name || '') === normalizedName
            )
        })
    }

    // === Excel Helpers ────────────────────────────────────────────────────────

    const loadExcelJS = async () => {
        if (!window.ExcelJS) {
            await new Promise((resolve, reject) => {
                const script = document.createElement("script")
                script.src = "https://cdnjs.cloudflare.com/ajax/libs/exceljs/4.4.0/exceljs.min.js"
                script.onload = resolve
                script.onerror = reject
                document.head.appendChild(script)
            })
        }
        return window.ExcelJS
    }

    const cellToString = (cell) => {
        const v = cell.value
        if (v === null || v === undefined) return ''
        if (typeof v === 'object') {
            if (v.richText) return v.richText.map(rt => rt.text).join('')
            if (v.text) return String(v.text)
            if (v.result !== undefined) return String(v.result)
            return ''
        }
        return String(v)
    }

    const downloadExcelTemplate = async (filename, sheetName, headers, sampleRows) => {
        const ExcelJS = await loadExcelJS()
        const wb = new ExcelJS.Workbook()
        const ws = wb.addWorksheet(sheetName)
        ws.columns = headers.map(h => ({ header: h, width: 24 }))
        const headerRow = ws.getRow(1)
        headerRow.eachCell(cell => {
            cell.font = { name: "Arial", bold: true, color: { argb: "FFFFFFFF" }, size: 10 }
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF111827" } }
            cell.alignment = { vertical: "middle", horizontal: "center" }
        })
        headerRow.height = 22
        sampleRows.forEach(row => ws.addRow(row))
        const buffer = await wb.xlsx.writeBuffer()
        const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        a.click()
        URL.revokeObjectURL(url)
    }

    // === CRUD ─────────────────────────────────────────────────────────────────

    const handleAddItem = async () => {
        const normalizedName = toTitleCase(itemFormData.name)
        const normalizedCategory = normalizeItemCategory(itemFormData.category)
        const normalizedClassification = normalizeItemClassification(itemFormData.classification)

        if (isDuplicateMenuItem(normalizedName, normalizedCategory, normalizedClassification)) {
            Swal.fire({
                icon: "warning",
                title: "Duplicate Item",
                text: `"${normalizedName}" already exists under "${normalizedCategory}" (${normalizedClassification}).`,
                confirmButtonColor: "#FF6F3C"
            })
            return
        }

        if (!itemFormData.name ||
            !itemFormData.item_price ||
            !itemFormData.category ||
            !itemFormData.classification ||
            !itemFormData.description
        ) {
            Swal.fire({
                icon: "warning",
                title: "Missing Information",
                text: "Please fill in all required fields",
                confirmButtonColor: "#FF6F3C"
            })
            return
        }

        if (selectedBranchIds.size === 0) {
            Swal.fire({
                icon: "warning",
                title: "No Branch Selected",
                text: "Please select at least one branch where this menu item is available.",
                confirmButtonColor: "#FF6F3C"
            })
            return
        }

        if (!validateItemForm()) return

        try {
            let finalImageUrl = itemFormData.item_img.trim() || null

            if (imageInputMode === 'upload' && itemImageFile) {
                setUploadingImage(true)
                finalImageUrl = await uploadMenuItemImage(itemImageFile)
                setUploadingImage(false)
            }

            const { data, error } = await supabase
                .from('menu_items')
                .insert([{
                    name: normalizedName,
                    item_price: parseFloat(itemFormData.item_price),
                    category: normalizedCategory,
                    classification: normalizedClassification,
                    description: itemFormData.description.trim(),
                    item_img: finalImageUrl,
                    prep_time_minutes: 0,
                    status: 'available'
                }])
                .select()
                .single()

            if (error) throw error

            const branchRows = Array.from(selectedBranchIds).map(branch_id => ({
                item_id: data.item_id,
                branch_id
            }))

            const { error: branchError } = await supabase
                .from('branch_menu_items')
                .insert(branchRows)

            if (branchError) throw branchError

            resetItemForm()
            setShowAddItemModal(false)

            Swal.fire({
                icon: "success",
                title: "Item Added!",
                text: `${itemFormData.name} has been added to the menu`,
                confirmButtonColor: "#FF6F3C",
                timer: 3000
            })

            fetchMenuItems()
        } catch (err) {
            setUploadingImage(false)
            Swal.fire({
                icon: "error",
                title: "Failed to Add Item",
                text: err.message,
                confirmButtonColor: "#FF6F3C"
            })
        }
    }

    const uploadMenuItemImage = async (file) => {
        if (!file) return null

        const allowedTypes = ['image/jpeg', 'image/png', 'image/webp']
        if (!allowedTypes.includes(file.type)) {
            throw new Error('Only JPG, PNG, and WEBP images are allowed.')
        }

        const maxSizeInBytes = 5 * 1024 * 1024
        if (file.size > maxSizeInBytes) {
            throw new Error('Image size must not exceed 5MB.')
        }

        const fileExt = file.name.split('.').pop()

        const slugify = (value = "") =>
            String(value)
                .trim()
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/^-+|-+$/g, '')

        const now = new Date()
        const pad = (n) => String(n).padStart(2, '0')
        const datetime = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`

        const namePart = slugify(itemFormData.name) || 'item'
        const categoryPart = slugify(itemFormData.category) || 'uncategorized'

        const fileName = `${namePart}_${categoryPart}_${datetime}.${fileExt}`
        const filePath = `menu-items/${fileName}`

        const { error: uploadError } = await supabase
            .storage
            .from('menu-images')
            .upload(filePath, file, {
                cacheControl: '3600',
                upsert: false
            })

        if (uploadError) throw uploadError

        const { data } = supabase
            .storage
            .from('menu-images')
            .getPublicUrl(filePath)

        return data.publicUrl
    }

    const parseBulkImportCSV = async (file) => {
        const ExcelJS = await loadExcelJS()
        const buffer = await file.arrayBuffer()
        const wb = new ExcelJS.Workbook()
        await wb.xlsx.load(buffer)
        const ws = wb.worksheets[0]
        if (!ws || ws.rowCount < 2) {
            throw new Error('Excel file must have a header row and at least one data row.')
        }

        const required = ['name', 'item_price', 'category', 'classification', 'description']

        // Scan the first few rows to find the actual header row (handles files
        // that have title/subtitle rows above the real header, e.g. our export).
        let headerRowNum = null
        let colIndex = {}
        for (let r = 1; r <= Math.min(10, ws.rowCount); r++) {
            const candidateColIndex = {}
            ws.getRow(r).eachCell({ includeEmpty: true }, (cell, colNumber) => {
                const h = cellToString(cell).trim().toLowerCase()
                if (h) candidateColIndex[h] = colNumber
            })
            if (required.every(col => col in candidateColIndex)) {
                headerRowNum = r
                colIndex = candidateColIndex
                break
            }
        }

        if (headerRowNum === null) {
            throw new Error(`Missing required columns: ${required.join(', ')}`)
        }

        const rows = []
        const errors = []
        const getVal = (row, key) => {
            const idx = colIndex[key]
            if (!idx) return ''
            return cellToString(row.getCell(idx)).trim()
        }

        for (let rowNum = headerRowNum + 1; rowNum <= ws.rowCount; rowNum++) {
            const excelRow = ws.getRow(rowNum)
            if (!excelRow.hasValues) continue

            const rowErrors = []
            const name = toTitleCase(getVal(excelRow, 'name'))
            const category = normalizeItemCategory(getVal(excelRow, 'category'))
            const classification = normalizeItemClassification(getVal(excelRow, 'classification'))
            const description = getVal(excelRow, 'description')
            const price = parseFloat(getVal(excelRow, 'item_price'))
            const item_img = getVal(excelRow, 'item_img')
            const branchNamesRaw = getVal(excelRow, 'branches')
            const isExplicitAll = branchNamesRaw && branchNamesRaw.trim().toLowerCase() === 'all'
            const branchNames = branchNamesRaw && !isExplicitAll
                ? branchNamesRaw.split('|').map(s => s.trim()).filter(Boolean)
                : []

            if (!name || name.length < 2) rowErrors.push('Name must be at least 2 characters')
            if (name.length > 50) rowErrors.push('Name must not exceed 50 characters')
            if (!category) rowErrors.push('Category is required')
            if (!classification) rowErrors.push('Classification is required')
            if (!description || description.length < 10) rowErrors.push('Description must be at least 10 characters')
            if (description.length > 255) rowErrors.push('Description must not exceed 255 characters')
            if (isNaN(price) || price <= 0) rowErrors.push('item_price must be a positive number')
            if (price > 9999) rowErrors.push('item_price must not exceed 9999')
            if (item_img && !/^https?:\/\/.+/i.test(item_img)) rowErrors.push('item_img must be a valid URL if provided')

            const matchedBranchIds = branchNames.length > 0
                ? branchNames.map(bn => {
                    const found = branches.find(b => normalizeBranchName(formatBranchName(b.name)) === normalizeBranchName(bn))
                    return found ? found.branch_id : null
                })
                : []

            if (branchNames.length > 0 && matchedBranchIds.includes(null)) {
                const unmatched = branchNames.filter((bn, i) => matchedBranchIds[i] === null)
                rowErrors.push(`Unknown branch name(s): ${unmatched.join(', ')}`)
            }

            if (rowErrors.length > 0) {
                errors.push({ row: rowNum, name: name || `Row ${rowNum}`, errors: rowErrors })
            }

            rows.push({
                name,
                item_price: isNaN(price) ? '' : price,
                category,
                classification,
                description,
                item_img: item_img || null,
                _branchIds: branchNames.length > 0
                    ? matchedBranchIds.filter(Boolean)
                    : (isExplicitAll ? branches.map(b => b.branch_id) : 'all'),
                status: 'available',
                _rowNum: rowNum,
                _hasError: rowErrors.length > 0
            })
        }

        return { rows, errors }
    }

    const handleBulkImportFileChange = async (e) => {
        const file = e.target.files?.[0]
        if (!file) return
        setBulkImportFile(file)
        setBulkImportPreview([])
        setBulkImportErrors([])
        setBulkImportResult(null)

        try {
            const { rows, errors } = await parseBulkImportCSV(file)
            setBulkImportPreview(rows)
            setBulkImportErrors(errors)
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Invalid Excel File', text: err.message, confirmButtonColor: '#FF6F3C' })
            setBulkImportFile(null)
        }
    }

    const bulkImportNeedsFallbackBranches = bulkImportPreview.some(
        r => !r._hasError && r._branchIds === 'all'
    )

    const handleBulkImport = async () => {
        const validRows = bulkImportPreview.filter(r => !r._hasError)
        if (validRows.length === 0) {
            Swal.fire({ icon: 'warning', title: 'No Valid Rows', text: 'Fix all errors before importing.', confirmButtonColor: '#FF6F3C' })
            return
        }

        const needsFallback = validRows.some(r => r._branchIds === 'all')
        if (needsFallback && bulkImportBranchIds.size === 0) {
            Swal.fire({ icon: 'warning', title: 'No Branch Selected', text: 'Some rows don\'t specify branches in the CSV — please select at least one fallback branch for them.', confirmButtonColor: '#FF6F3C' })
            return
        }

        try {
            setBulkImporting(true)

            const skipped = []
            const toInsert = []
            const toUpdate = []
            const seenInBatch = new Set()

            // const areBranchSetsEqual = (idsA = [], idsB = []) => {
            //     const setA = new Set(idsA.map(String))
            //     const setB = new Set(idsB.map(String))
            //     if (setA.size !== setB.size) return false
            //     for (const id of setA) {
            //         if (!setB.has(id)) return false
            //     }
            //     return true
            // }

            for (const row of validRows) {
                const batchKey = `${row.name.trim().toLowerCase()}|${row.category.trim().toLowerCase()}|${row.classification.trim().toLowerCase()}`

                if (seenInBatch.has(batchKey)) {
                    skipped.push(row.name)
                    continue
                }

                const existingItem = findDuplicateMenuItem(row.name, row.category, row.classification)

                if (existingItem) {
                    const targetBranchIds = row._branchIds === 'all'
                        ? Array.from(bulkImportBranchIds)
                        : row._branchIds

                    const currentBranchIds = getCurrentBranchIdsForItem(existingItem.item_id)
                    const targetBranchIdsSet = getTargetBranchIds(row)

                    const hasChanges =
                        parseFloat(existingItem.item_price) !== parseFloat(row.item_price) ||
                        String(existingItem.description || '').trim() !== String(row.description || '').trim() ||
                        String(existingItem.item_img || '') !== String(row.item_img || '') ||
                        !areBranchIdSetsEqual(currentBranchIds, targetBranchIdsSet)

                    seenInBatch.add(batchKey)

                    if (hasChanges) {
                        toUpdate.push({
                            item_id: existingItem.item_id,
                            item_price: row.item_price,
                            description: row.description,
                            item_img: row.item_img || null,
                            _branchIds: targetBranchIds
                        })
                    } else {
                        skipped.push(row.name)
                    }
                    continue
                }

                seenInBatch.add(batchKey)
                toInsert.push({
                    name: row.name,
                    item_price: row.item_price,
                    category: row.category,
                    classification: row.classification,
                    description: row.description,
                    item_img: row.item_img || null,
                    _branchIds: row._branchIds,
                    _key: batchKey,
                    status: 'available'
                })
            }

            let inserted = 0
            if (toInsert.length > 0) {
                const { data: insertedItems, error } = await supabase
                    .from('menu_items')
                    .insert(toInsert.map(({ _branchIds, _key, ...rest }) => rest))
                    .select('item_id, name, category, classification')
                if (error) throw error

                const toInsertByKey = new Map(toInsert.map(row => [row._key, row]))

                const branchRows = insertedItems.flatMap((item) => {
                    const key = `${item.name.trim().toLowerCase()}|${item.category.trim().toLowerCase()}|${item.classification.trim().toLowerCase()}`
                    const originalRow = toInsertByKey.get(key)
                    if (!originalRow) return []

                    const targetBranchIds = originalRow._branchIds === 'all'
                        ? Array.from(bulkImportBranchIds)
                        : originalRow._branchIds

                    return targetBranchIds.map(branch_id => ({ item_id: item.item_id, branch_id }))
                })

                const { error: insertBranchError } = await supabase
                    .from('branch_menu_items')
                    .upsert(branchRows, { onConflict: 'item_id,branch_id' })

                if (insertBranchError) throw insertBranchError

                inserted = toInsert.length
            }

            let updated = 0
            if (toUpdate.length > 0) {
                for (const row of toUpdate) {
                    const { error: updateError } = await supabase
                        .from('menu_items')
                        .update({
                            item_price: row.item_price,
                            description: row.description,
                            item_img: row.item_img
                        })
                        .eq('item_id', row.item_id)
                    if (updateError) throw updateError

                    const { error: deleteBranchError } = await supabase
                        .from('branch_menu_items')
                        .delete()
                        .eq('item_id', row.item_id)
                    if (deleteBranchError) throw deleteBranchError

                    if (row._branchIds.length > 0) {
                        const { error: insertBranchError } = await supabase
                            .from('branch_menu_items')
                            .insert(row._branchIds.map(branch_id => ({ item_id: row.item_id, branch_id })))
                        if (insertBranchError) throw insertBranchError
                    }
                }
                updated = toUpdate.length
            }

            await fetchMenuItems()
            await fetchBranchMenuItems(selectedBranchForMenu)

            setBulkImportResult({ inserted, updated, skipped })

            const parts = []
            if (inserted > 0) parts.push(`${inserted} added`)
            if (updated > 0) parts.push(`${updated} updated`)
            if (skipped.length > 0) parts.push(`${skipped.length} unchanged (skipped)`)

            if (inserted > 0 || updated > 0) {
                Swal.fire({
                    icon: 'success',
                    title: 'Import Complete',
                    html: `
                            <p class="text-gray-600 mb-2">${parts.join(', ')}.</p>
                            ${skipped.length > 0 ? `<p class="text-sm text-gray-500 italic">Unchanged, skipped: ${skipped.slice(0, 5).join(', ')}${skipped.length > 5 ? ` and ${skipped.length - 5} more...` : ''}</p>` : ''}
                        `,
                    confirmButtonColor: '#FF6F3C',
                })
            } else {
                Swal.fire({
                    icon: 'info',
                    title: 'Nothing to Import',
                    html: `<p class="text-gray-600 mb-1">All ${skipped.length} item${skipped.length !== 1 ? 's' : ''} already exist with no changes.</p>`,
                    confirmButtonColor: '#FF6F3C'
                })
            }

            setShowBulkImportModal(false)
            setBulkImportFile(null)
            setBulkImportPreview([])
            setBulkImportErrors([])
            setBulkImportResult(null)
            setBulkImportBranchIds(new Set())
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Import Failed', text: err.message, confirmButtonColor: '#FF6F3C' })
        } finally {
            setBulkImporting(false)
        }
    }

    // === Variant CSV ─────────────────────────────────────────────────────────────

    const parseVariantCSV = async (file) => {
        const ExcelJS = await loadExcelJS()
        const buffer = await file.arrayBuffer()
        const wb = new ExcelJS.Workbook()
        await wb.xlsx.load(buffer)
        const ws = wb.worksheets[0]
        if (!ws || ws.rowCount < 2) {
            throw new Error('Excel file must have a header row and at least one data row.')
        }

        const headerRow = ws.getRow(1)
        const colIndex = {}
        headerRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
            const h = cellToString(cell).trim().toLowerCase()
            if (h) colIndex[h] = colNumber
        })

        const required = ['category', 'variant_name', 'price']
        const missing = required.filter(r => !(r in colIndex))
        if (missing.length > 0) {
            throw new Error(`Missing required columns: ${missing.join(', ')}`)
        }

        const rows = []
        const errors = []
        const getVal = (row, key) => {
            const idx = colIndex[key]
            if (!idx) return ''
            return cellToString(row.getCell(idx)).trim()
        }

        for (let rowNum = 2; rowNum <= ws.rowCount; rowNum++) {
            const excelRow = ws.getRow(rowNum)
            if (!excelRow.hasValues) continue

            const rowErrors = []
            const category = normalizeVariantCategory(getVal(excelRow, 'category'))
            const variant_name = normalizeVariantName(getVal(excelRow, 'variant_name'))
            const price = parseFloat(getVal(excelRow, 'price'))
            const applicable_categories = getVal(excelRow, 'applicable_categories')
                .split('|').map(s => s.trim()).filter(Boolean)

            if (!category || category.length < 2) rowErrors.push('category must be at least 2 characters')
            if (category.length > 30) rowErrors.push('category must not exceed 30 characters')
            if (!variant_name || variant_name.length < 2) rowErrors.push('variant_name must be at least 2 characters')
            if (variant_name.length > 30) rowErrors.push('variant_name must not exceed 30 characters')
            if (isNaN(price) || price < 0) rowErrors.push('price must be 0 or greater')
            if (price > 1000) rowErrors.push('price must not exceed 1000')

            if (rowErrors.length > 0) errors.push({ row: rowNum, name: `${category} - ${variant_name}`, errors: rowErrors })
            rows.push({ category, variant_name, price: isNaN(price) ? '' : price, applicable_categories, _rowNum: rowNum, _hasError: rowErrors.length > 0 })
        }

        return { rows, errors }
    }
    const handleVariantImportFileChange = async (e) => {
        const file = e.target.files?.[0]
        if (!file) return
        setVariantImportFile(file)
        setVariantImportPreview([])
        setVariantImportErrors([])
        try {
            const { rows, errors } = await parseVariantCSV(file)
            setVariantImportPreview(rows)
            setVariantImportErrors(errors)
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Invalid Excel File', text: err.message, confirmButtonColor: '#FF6F3C' })
            setVariantImportFile(null)
        }
    }

    const handleVariantImport = async () => {
        const validRows = variantImportPreview.filter(r => !r._hasError)
        if (validRows.length === 0) {
            Swal.fire({ icon: 'warning', title: 'No Valid Rows', text: 'Fix all errors before importing.', confirmButtonColor: '#FF6F3C' })
            return
        }
        try {
            setVariantImporting(true)
            const duplicates = []
            const toInsert = []
            for (const row of validRows) {
                if (isDuplicateVariant(row.category, row.variant_name)) {
                    duplicates.push(`${row.category} - ${row.variant_name}`)
                } else {
                    toInsert.push({
                        category: row.category,
                        variant_name: row.variant_name,
                        price: row.price,
                        applicable_categories: row.applicable_categories
                    })
                }
            }
            if (toInsert.length > 0) {
                const { error } = await supabase.from('item_variants').insert(toInsert)
                if (error) throw error
            }
            await fetchVariants()
            Swal.fire({
                icon: toInsert.length > 0 ? 'success' : 'warning',
                title: toInsert.length > 0 ? `${toInsert.length} Variant${toInsert.length !== 1 ? 's' : ''} Imported!` : 'Nothing Imported',
                html: duplicates.length > 0
                    ? `<p>${duplicates.length} duplicate(s) skipped: <em>${duplicates.slice(0, 5).join(', ')}${duplicates.length > 5 ? '...' : ''}</em></p>`
                    : undefined,
                confirmButtonColor: '#FF6F3C',
                timer: 3000
            })
            if (duplicates.length === 0) {
                setShowVariantImportModal(false)
                setVariantImportFile(null)
                setVariantImportPreview([])
                setVariantImportErrors([])
            }
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Import Failed', text: err.message, confirmButtonColor: '#FF6F3C' })
        } finally {
            setVariantImporting(false)
        }
    }

    const exportVariantsCSV = async () => {
        const ExcelJS = await loadExcelJS()
        const wb = new ExcelJS.Workbook()
        wb.creator = "Coffee Gus·to"
        wb.created = new Date()

        const ws = wb.addWorksheet("Variants")
        ws.columns = [{ width: 22 }, { width: 22 }, { width: 14 }, { width: 32 }]

        const headerRow = ws.getRow(1)
        headerRow.values = ["category", "variant_name", "price", "applicable_categories"]
        headerRow.height = 22
            ;[1, 2, 3, 4].forEach(ci => {
                const cell = headerRow.getCell(ci)
                cell.font = { name: "Arial", bold: true, color: { argb: "FFFFFFFF" }, size: 10 }
                cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF4F46E5" } }
                cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true }
                cell.border = { bottom: { style: "medium", color: { argb: "FFD97706" } } }
            })

        filteredVariants.forEach((v, i) => {
            const row = ws.getRow(2 + i)
            row.height = 18
            row.getCell(1).value = v.category || ''
            row.getCell(2).value = v.variant_name || ''
            row.getCell(3).value = v.price ?? 0
            row.getCell(3).numFmt = '0.00'
            row.getCell(3).alignment = { horizontal: "right" }
            row.getCell(4).value = (v.applicable_categories || []).join('|')

            if (i % 2 === 0) {
                [1, 2, 3, 4].forEach(ci => {
                    row.getCell(ci).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF9FAFB" } }
                })
            }
        })

        const buffer = await wb.xlsx.writeBuffer()
        const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `variants_export_${new Date().toISOString().slice(0, 10)}.xlsx`
        a.click()
        URL.revokeObjectURL(url)
    }


    const parseModifierCSV = async (file) => {
        const ExcelJS = await loadExcelJS()
        const buffer = await file.arrayBuffer()
        const wb = new ExcelJS.Workbook()
        await wb.xlsx.load(buffer)
        const ws = wb.worksheets[0]
        if (!ws || ws.rowCount < 2) {
            throw new Error('Excel file must have a header row and at least one data row.')
        }

        const headerRow = ws.getRow(1)
        const colIndex = {}
        headerRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
            const h = cellToString(cell).trim().toLowerCase()
            if (h) colIndex[h] = colNumber
        })

        const required = ['modifier_name', 'category', 'price']
        const missing = required.filter(r => !(r in colIndex))
        if (missing.length > 0) {
            throw new Error(`Missing required columns: ${missing.join(', ')}`)
        }

        const rows = []
        const errors = []
        const getVal = (row, key) => {
            const idx = colIndex[key]
            if (!idx) return ''
            return cellToString(row.getCell(idx)).trim()
        }

        for (let rowNum = 2; rowNum <= ws.rowCount; rowNum++) {
            const excelRow = ws.getRow(rowNum)
            if (!excelRow.hasValues) continue

            const rowErrors = []
            const modifier_name = normalizeModifierName(getVal(excelRow, 'modifier_name'))
            const category = normalizeModifierCategory(getVal(excelRow, 'category'))
            const price = parseFloat(getVal(excelRow, 'price'))
            const applicable_categories = getVal(excelRow, 'applicable_categories')
                .split('|').map(s => s.trim()).filter(Boolean)
            const ingredient_id = getVal(excelRow, 'ingredient_id') || null

            if (!modifier_name || modifier_name.length < 2) rowErrors.push('modifier_name must be at least 2 characters')
            if (modifier_name.length > 50) rowErrors.push('modifier_name must not exceed 50 characters')
            if (!category || category.length < 2) rowErrors.push('category must be at least 2 characters')
            if (category.length > 30) rowErrors.push('category must not exceed 30 characters')
            if (isNaN(price) || price < 0) rowErrors.push('price must be 0 or greater')
            if (price > 500) rowErrors.push('price must not exceed 500')

            if (rowErrors.length > 0) errors.push({ row: rowNum, name: modifier_name || `Row ${rowNum}`, errors: rowErrors })
            rows.push({ modifier_name, category, price: isNaN(price) ? '' : price, applicable_categories, ingredient_id, _rowNum: rowNum, _hasError: rowErrors.length > 0 })
        }

        return { rows, errors }
    }

    const handleModifierImportFileChange = async (e) => {
        const file = e.target.files?.[0]
        if (!file) return
        setModifierImportFile(file)
        setModifierImportPreview([])
        setModifierImportErrors([])
        try {
            const { rows, errors } = await parseModifierCSV(file)
            setModifierImportPreview(rows)
            setModifierImportErrors(errors)
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Invalid Excel File', text: err.message, confirmButtonColor: '#FF6F3C' })
            setModifierImportFile(null)
        }
    }

    const handleModifierImport = async () => {
        const validRows = modifierImportPreview.filter(r => !r._hasError)
        if (validRows.length === 0) {
            Swal.fire({ icon: 'warning', title: 'No Valid Rows', text: 'Fix all errors before importing.', confirmButtonColor: '#FF6F3C' })
            return
        }
        try {
            setModifierImporting(true)
            const duplicates = []
            const toInsert = []
            for (const row of validRows) {
                if (isDuplicateModifier(row.modifier_name, row.category)) {
                    duplicates.push(`${row.modifier_name} (${row.category})`)
                } else {
                    const matchedIngredient = row.ingredient_id
                        ? ingredients.find(ing => String(ing.ingredient_id) === String(row.ingredient_id))
                        : null

                    toInsert.push({
                        modifier_name: row.modifier_name,
                        category: row.category,
                        price: row.price,
                        applicable_categories: row.applicable_categories,
                        ingredient_id: matchedIngredient ? matchedIngredient.ingredient_id : null
                    })
                }
            }
            if (toInsert.length > 0) {
                const { error } = await supabase.from('item_modifiers').insert(toInsert)
                if (error) throw error
            }
            await fetchModifiers()
            Swal.fire({
                icon: toInsert.length > 0 ? 'success' : 'warning',
                title: toInsert.length > 0 ? `${toInsert.length} Modifier${toInsert.length !== 1 ? 's' : ''} Imported!` : 'Nothing Imported',
                html: duplicates.length > 0
                    ? `<p>${duplicates.length} duplicate(s) skipped: <em>${duplicates.slice(0, 5).join(', ')}${duplicates.length > 5 ? '...' : ''}</em></p>`
                    : undefined,
                confirmButtonColor: '#FF6F3C',
                timer: 3000
            })
            if (duplicates.length === 0) {
                setShowModifierImportModal(false)
                setModifierImportFile(null)
                setModifierImportPreview([])
                setModifierImportErrors([])
            }
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Import Failed', text: err.message, confirmButtonColor: '#FF6F3C' })
        } finally {
            setModifierImporting(false)
        }
    }

    const exportModifiersCSV = async () => {
        const ExcelJS = await loadExcelJS()
        const wb = new ExcelJS.Workbook()
        wb.creator = "Coffee Gus·to"
        wb.created = new Date()

        const ws = wb.addWorksheet("Modifiers")
        ws.columns = [{ width: 24 }, { width: 20 }, { width: 14 }, { width: 30 }, { width: 24 }]

        const headerRow = ws.getRow(1)
        headerRow.values = ["modifier_name", "category", "price", "applicable_categories", "ingredient_id"]
        headerRow.height = 22
            ;[1, 2, 3, 4, 5].forEach(ci => {
                const cell = headerRow.getCell(ci)
                cell.font = { name: "Arial", bold: true, color: { argb: "FFFFFFFF" }, size: 10 }
                cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF7C3AED" } }
                cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true }
                cell.border = { bottom: { style: "medium", color: { argb: "FFD97706" } } }
            })

        filteredModifiers.forEach((m, i) => {
            const row = ws.getRow(2 + i)
            row.height = 18
            row.getCell(1).value = m.modifier_name || ''
            row.getCell(2).value = m.category || ''
            row.getCell(3).value = m.price ?? 0
            row.getCell(3).numFmt = '0.00'
            row.getCell(3).alignment = { horizontal: "right" }
            row.getCell(4).value = (m.applicable_categories || []).join('|')
            row.getCell(5).value = m.ingredient_id || ''

            if (i % 2 === 0) {
                [1, 2, 3, 4, 5].forEach(ci => {
                    row.getCell(ci).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF9FAFB" } }
                })
            }
        })

        const buffer = await wb.xlsx.writeBuffer()
        const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `modifiers_export_${new Date().toISOString().slice(0, 10)}.xlsx`
        a.click()
        URL.revokeObjectURL(url)
    }

    const exportMenuItemsCSV = async () => {
        const ExcelJS = await loadExcelJS()
        const wb = new ExcelJS.Workbook()
        wb.creator = "Coffee Gus·to"
        wb.created = new Date()

        const ws = wb.addWorksheet("Menu Items")
        ws.columns = [
            { width: 26 }, { width: 14 }, { width: 20 }, { width: 20 },
            { width: 36 }, { width: 34 }, { width: 30 }
        ]

        // Header row must be row 1 and use the exact keys the importer expects
        const headerRow = ws.getRow(1)
        headerRow.values = ["name", "item_price", "category", "classification", "description", "item_img", "branches"]
        headerRow.height = 22
            ;[1, 2, 3, 4, 5, 6, 7].forEach(ci => {
                const cell = headerRow.getCell(ci)
                cell.font = { name: "Arial", bold: true, color: { argb: "FFFFFFFF" }, size: 10 }
                cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFF6F3C" } }
                cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true }
                cell.border = { bottom: { style: "medium", color: { argb: "FFD97706" } } }
            })

        filteredItems.forEach((item, i) => {
            const itemBranches = allBranchMenuItems
                .filter(b => b.item_id === item.item_id)
                .map(b => b.branch_name)
            const isAllBranches = itemBranches.length === branches.length && branches.length > 0

            const row = ws.getRow(2 + i)
            row.height = 18
            row.getCell(1).value = item.name || ''
            row.getCell(2).value = item.item_price ?? 0
            row.getCell(2).numFmt = '₱#,##0.00'
            row.getCell(2).alignment = { horizontal: "right" }
            row.getCell(3).value = item.category || ''
            row.getCell(4).value = item.classification || ''
            row.getCell(5).value = item.description || ''
            row.getCell(6).value = item.item_img || ''
            row.getCell(7).value = isAllBranches ? 'All' : itemBranches.join('|')

            if (i % 2 === 0) {
                [1, 2, 3, 4, 5, 6, 7].forEach(ci => {
                    row.getCell(ci).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF9FAFB" } }
                })
            }
        })

        const buffer = await wb.xlsx.writeBuffer()
        const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `menu_items_export_${new Date().toISOString().slice(0, 10)}.xlsx`
        a.click()
        URL.revokeObjectURL(url)
    }

    const handleEditClick = async (item) => {
        const normalizedItemData = {
            name: normalizeItemName(item.name || ''),
            item_price: String(item.item_price ?? ''),
            category: normalizeItemCategory(item.category || ''),
            classification: normalizeItemClassification(item.classification || ''),
            description: item.description || '',
            item_img: item.item_img || ''
        }

        setEditingItem(item)
        setItemFormData(normalizedItemData)
        setInitialItemFormData(normalizedItemData)
        setItemCategorySearch(normalizeItemCategory(item.category || ""))
        setItemClassificationSearch(normalizeItemClassification(item.classification || ""))
        setOpenItemCategoryDropdown(false)
        setOpenItemClassificationDropdown(false)

        try {
            const { data, error } = await supabase
                .from('branch_menu_items')
                .select('branch_id')
                .eq('item_id', item.item_id)

            if (error) throw error

            const branchSet = new Set((data || []).map(row => row.branch_id))
            setSelectedBranchIds(branchSet)
            setInitialSelectedBranchIds(new Set(branchSet))
            setItemImageFile(null)
            setImageInputMode(item.item_img ? 'url' : 'upload')
            setShowEditItemModal(true)
        } catch (err) {
            console.error("Error loading branch assignments:", err)
            Swal.fire({
                icon: "error",
                title: "Failed to Load Branches",
                text: err.message,
                confirmButtonColor: "#FF6F3C"
            })
        }
    }

    const handleUpdateItem = async () => {
        if (!editingItem) return

        const normalizedName = normalizeItemName(itemFormData.name)
        const normalizedCategory = normalizeItemCategory(itemFormData.category)
        const normalizedClassification = normalizeItemClassification(itemFormData.classification)

        if (
            !itemFormData.name ||
            !itemFormData.item_price ||
            !itemFormData.category ||
            !itemFormData.classification ||
            !itemFormData.description
        ) {
            Swal.fire({
                icon: "warning",
                title: "Missing Information",
                text: "Please fill in all required fields",
                confirmButtonColor: "#FF6F3C"
            })
            return
        }

        if (selectedBranchIds.size === 0) {
            Swal.fire({
                icon: "warning",
                title: "No Branch Selected",
                text: "Please select at least one branch where this menu item is available.",
                confirmButtonColor: "#FF6F3C"
            })
            return
        }

        if (!validateItemForm()) return

        if (
            isDuplicateMenuItem(
                normalizedName,
                normalizedCategory,
                normalizedClassification,
                editingItem.item_id
            )
        ) {
            Swal.fire({
                icon: "warning",
                title: "Duplicate Item",
                text: `"${normalizedName}" already exists under "${normalizedCategory}" (${normalizedClassification}).`,
                confirmButtonColor: "#FF6F3C"
            })
            return
        }

        try {
            let finalImageUrl = itemFormData.item_img.trim() || null

            if (imageInputMode === 'upload' && itemImageFile) {
                setUploadingImage(true)
                finalImageUrl = await uploadMenuItemImage(itemImageFile)
                setUploadingImage(false)
            }

            const { error } = await supabase
                .from('menu_items')
                .update({
                    name: normalizedName,
                    item_price: parseFloat(itemFormData.item_price),
                    category: normalizedCategory,
                    classification: normalizedClassification,
                    description: itemFormData.description.trim(),
                    prep_time_minutes: parseInt(itemFormData.prep_time_minutes) || 0,
                    item_img: finalImageUrl
                })
                .eq('item_id', editingItem.item_id)

            if (error) throw error

            const { error: deleteBranchError } = await supabase
                .from('branch_menu_items')
                .delete()
                .eq('item_id', editingItem.item_id)

            if (deleteBranchError) throw deleteBranchError

            const branchRows = Array.from(selectedBranchIds).map((branch_id) => ({
                item_id: editingItem.item_id,
                branch_id
            }))

            if (branchRows.length > 0) {
                const { error: insertBranchError } = await supabase
                    .from('branch_menu_items')
                    .insert(branchRows)

                if (insertBranchError) throw insertBranchError
            }

            setShowEditItemModal(false)
            setEditingItem(null)
            resetItemForm()

            Swal.fire({
                icon: "success",
                title: "Item Updated!",
                text: `${normalizedName} has been updated`,
                confirmButtonColor: "#FF6F3C",
                timer: 3000
            })

            fetchMenuItems()
        } catch (err) {
            setUploadingImage(false)
            Swal.fire({
                icon: "error",
                title: "Failed to Update Item",
                text: err.message,
                confirmButtonColor: "#FF6F3C"
            })
        }
    }

    const handleArchiveItem = async (item) => {
        const result = await Swal.fire({
            title: "Archive Menu Item?",
            text: `Are you sure you want to archive ${item.name}? It will no longer appear in active menus.`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d97706",
            cancelButtonColor: "#6b7280",
            confirmButtonText: "Yes, archive",
            cancelButtonText: "Cancel"
        })

        if (!result.isConfirmed) return

        try {
            const { error } = await supabase
                .from('menu_items')
                .update({
                    is_archived: true,
                    archived_at: new Date().toISOString()
                })
                .eq('item_id', item.item_id)

            if (error) throw error

            Swal.fire({
                icon: "success",
                title: "Item Archived",
                text: `${item.name} has been archived.`,
                confirmButtonColor: "#FF6F3C",
                timer: 1800
            })

            fetchMenuItems()
        } catch (err) {
            Swal.fire({
                icon: 'error',
                title: "Failed to Archive",
                text: err.message,
                confirmButtonColor: "#FF6F3C"
            })
        }
    }

    const handleAddVariant = async () => {
        if (!variantFormData.category || !variantFormData.variant_name || variantFormData.price === "") {
            Swal.fire({
                icon: "warning",
                title: "Missing Information",
                text: "Please fill in all fields",
                confirmButtonColor: "#FF6F3C"
            })
            return
        }

        if (!validateVariantForm()) return

        const normalizedCategory = normalizeVariantCategory(variantFormData.category)
        const normalizedVariantName = normalizeVariantName(variantFormData.variant_name)

        if (isDuplicateVariant(normalizedCategory, normalizedVariantName)) {
            Swal.fire({
                icon: "warning",
                title: "Duplicate Variant",
                text: `The variant "${normalizedVariantName}" already exists under "${normalizedCategory}".`,
                confirmButtonColor: "#FF6F3C"
            })
            return
        }

        try {
            setSavingVariant(true)

            const { error } = await supabase
                .from('item_variants')
                .insert([{
                    category: normalizedCategory,
                    variant_name: normalizedVariantName,
                    price: parseFloat(variantFormData.price),
                    applicable_categories: variantFormData.applicable_categories
                }])

            if (error) throw error

            Swal.fire({
                icon: "success",
                title: "Variant Added!",
                text: "Variant has been added successfully",
                confirmButtonColor: "#FF6F3C",
                timer: 1800
            })

            setVariantFormData({
                category: "",
                variant_name: "",
                price: "",
                applicable_categories: []
            })

            setVariantCategorySearch("")
            setOpenVariantCategoryDropdown(false)
            setShowAddVariantModal(false)
            fetchVariants()

        } catch (err) {
            console.error("Error adding variant:", err)
            Swal.fire({
                icon: "error",
                title: "Failed to Add Variant",
                text: err.message,
                confirmButtonColor: "#FF6F3C"
            })
        } finally {
            setSavingVariant(false)
        }
    }

    const openAssignVariantsModal = async (item) => {
        setSelectedItemForVariants(item)
        setLoadingVariantAssignments(true)
        setShowAssignVariantsModal(true)

        try {
            await fetchVariants()

            const { data, error } = await supabase
                .from('menu_variants')
                .select('variant_id')
                .eq('item_id', item.item_id)

            if (error) throw error

            const variantSet = new Set((data || []).map(r => r.variant_id))
            setAssignedVariantIds(variantSet)
            setInitialAssignedVariantIds(new Set(variantSet))
        } catch (err) {
            console.error("Error fetching variant assignments:", err)
            Swal.fire({
                icon: 'error',
                title: 'Failed to load assignments',
                text: err.message,
                confirmButtonColor: '#FF6F3C'
            })
        } finally {
            setLoadingVariantAssignments(false)
        }
    }

    const closeAssignVariantsModal = () => {
        setShowAssignVariantsModal(false)
        setSelectedItemForVariants(null)
        setAssignedVariantIds(new Set())
        setInitialAssignedVariantIds(new Set())
        setAssignVariantCategoryFilter('All')
        setAssignVariantSearch('')
    }

    const toggleVariantAssignment = (variantId) => {
        setAssignedVariantIds(prev => {
            const next = new Set(prev)
            if (next.has(variantId)) {
                next.delete(variantId)
            } else {
                next.add(variantId)
            }
            return next
        })
    }

    const handleSelectAllVariantsInCategory = () => {
        const itemCategory = selectedItemForVariants?.category || ''
        const relevantVariants = variants.filter(v => {
            if (v.is_archived) return false
            const cats = v.applicable_categories || []
            const isApplicable = cats.length > 0 && cats.includes(itemCategory)
            const matchesFilter = assignVariantCategoryFilter === 'All' || v.category === assignVariantCategoryFilter
            const matchesSearch = assignVariantSearch === '' ||
                String(v.variant_name || '').toLowerCase().includes(assignVariantSearch.toLowerCase()) ||
                String(v.category || '').toLowerCase().includes(assignVariantSearch.toLowerCase())
            return isApplicable && matchesFilter && matchesSearch
        })
        setAssignedVariantIds(prev => {
            const next = new Set(prev)
            relevantVariants.forEach(v => next.add(v.variant_id))
            return next
        })
    }

    const handleClearAllVariantsInCategory = () => {
        const itemCategory = selectedItemForVariants?.category || ''
        const relevantVariants = variants.filter(v => {
            if (v.is_archived) return false
            const cats = v.applicable_categories || []
            const isApplicable = cats.length > 0 && cats.includes(itemCategory)
            const matchesFilter = assignVariantCategoryFilter === 'All' || v.category === assignVariantCategoryFilter
            const matchesSearch = assignVariantSearch === '' ||
                String(v.variant_name || '').toLowerCase().includes(assignVariantSearch.toLowerCase()) ||
                String(v.category || '').toLowerCase().includes(assignVariantSearch.toLowerCase())
            return isApplicable && matchesFilter && matchesSearch
        })
        setAssignedVariantIds(prev => {
            const next = new Set(prev)
            relevantVariants.forEach(v => next.delete(v.variant_id))
            return next
        })
    }

    const saveVariantAssignments = async () => {
        if (!hasAssignVariantChanges) return

        if (!selectedItemForVariants) return

        setSavingVariantAssignments(true)

        try {
            const { error: deleteError } = await supabase
                .from('menu_variants')
                .delete()
                .eq('item_id', selectedItemForVariants.item_id)

            if (deleteError) throw deleteError

            if (assignedVariantIds.size > 0) {
                const rows = Array.from(assignedVariantIds).map(variant_id => ({
                    item_id: selectedItemForVariants.item_id,
                    variant_id
                }))

                const { error: insertError } = await supabase
                    .from('menu_variants')
                    .insert(rows)

                if (insertError) throw insertError
            }

            closeAssignVariantsModal()

            Swal.fire({
                icon: 'success',
                title: 'Variants updated!',
                text: `Variants for ${selectedItemForVariants.name} have been saved`,
                confirmButtonColor: '#FF6F3C',
                timer: 2500
            })
        } catch (err) {
            console.error("Error saving variant assignments:", err)
            Swal.fire({
                icon: 'error',
                title: 'Failed to save',
                text: err.message,
                confirmButtonColor: '#FF6F3C'
            })
        } finally {
            setSavingVariantAssignments(false)
        }
    }

    const handleEditVariantClick = (variant) => {
        const normalizedCategory = normalizeVariantCategory(variant.category || "")
        const normalizedName = normalizeVariantName(variant.variant_name || "")

        const normalizedVariantData = {
            category: normalizedCategory,
            variant_name: normalizedName,
            price: String(variant.price ?? ""),
            applicable_categories: variant.applicable_categories || []
        }

        setEditingVariant(variant)
        setVariantFormData(normalizedVariantData)
        setInitialVariantFormData(normalizedVariantData)
        setVariantCategorySearch(normalizedCategory)
        setOpenVariantCategoryDropdown(false)
    }

    const handleUpdateVariant = async () => {
        if (!editingVariant) return

        if (!variantFormData.category || !variantFormData.variant_name || variantFormData.price === "") {
            Swal.fire({
                icon: "warning",
                title: "Missing Information",
                text: "Please fill in all fields",
                confirmButtonColor: "#FF6F3C"
            })
            return
        }

        if (!validateVariantForm()) return

        const normalizedCategory = normalizeVariantCategory(variantFormData.category)
        const normalizedVariantName = normalizeVariantName(variantFormData.variant_name)

        if (isDuplicateVariant(normalizedCategory, normalizedVariantName, editingVariant.variant_id)) {
            Swal.fire({
                icon: "warning",
                title: "Duplicate Variant",
                text: `The variant "${normalizedVariantName}" already exists under "${normalizedCategory}".`,
                confirmButtonColor: "#FF6F3C"
            })
            return
        }

        try {
            setSavingVariant(true)

            const { error } = await supabase
                .from('item_variants')
                .update({
                    category: normalizedCategory,
                    variant_name: normalizedVariantName,
                    price: parseFloat(variantFormData.price),
                    applicable_categories: variantFormData.applicable_categories
                })
                .eq('variant_id', editingVariant.variant_id)

            if (error) throw error

            Swal.fire({
                icon: "success",
                title: "Variant Updated!",
                text: `${normalizedVariantName} has been updated.`,
                confirmButtonColor: "#FF6F3C",
                timer: 1800
            })

            setEditingVariant(null)
            setVariantFormData({
                category: "",
                variant_name: "",
                price: "",
                applicable_categories: []
            })

            setVariantCategorySearch("")
            setOpenVariantCategoryDropdown(false)
            setShowEditVariantModal(false)
            setInitialVariantFormData(null)

            fetchVariants()
        } catch (err) {
            console.error("Error updating variant:", err)
            Swal.fire({
                icon: "error",
                title: "Failed to Update Variant",
                text: err.message,
                confirmButtonColor: "#FF6F3C"
            })
        } finally {
            setSavingVariant(false)
        }
    }

    const handleArchiveVariant = async (variant) => {
        const result = await Swal.fire({
            title: "Archive Variant?",
            text: `Are you sure you want to archive "${variant.variant_name}"? It will no longer be available for new assignments or orders.`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d97706",
            cancelButtonColor: "#6b7280",
            confirmButtonText: "Yes, archive",
            cancelButtonText: "Cancel"
        })

        if (!result.isConfirmed) return

        try {
            const { error } = await supabase
                .from('item_variants')
                .update({
                    is_archived: true,
                    archived_at: new Date().toISOString()
                })
                .eq('variant_id', variant.variant_id)

            if (error) throw error

            Swal.fire({
                icon: "success",
                title: "Variant Archived",
                text: `${variant.variant_name} has been archived.`,
                confirmButtonColor: "#FF6F3C",
                timer: 1800
            })

            fetchVariants()
        } catch (err) {
            console.error("Error archiving variant:", err)
            Swal.fire({
                icon: "error",
                title: "Failed to Archive Variant",
                text: err.message,
                confirmButtonColor: "#FF6F3C"
            })
        }
    }

    const handleAddModifier = async () => {
        if (!modifierFormData.modifier_name || !modifierFormData.category || modifierFormData.price === "") {
            Swal.fire({
                icon: "warning",
                title: "Missing Information",
                text: "Please fill in all fields",
                confirmButtonColor: "#FF6F3C"
            })
            return
        }

        if (!validateModifierForm()) return

        const normalizedModifierName = normalizeModifierName(modifierFormData.modifier_name)
        const normalizedCategory = normalizeModifierCategory(modifierFormData.category)

        if (isDuplicateModifier(normalizedModifierName, normalizedCategory)) {
            Swal.fire({
                icon: "warning",
                title: "Duplicate Modifier",
                text: `The modifier "${normalizedModifierName}" already exists under "${normalizedCategory}".`,
                confirmButtonColor: "#FF6F3C"
            })
            return
        }

        try {
            const { error } = await supabase
                .from('item_modifiers')
                .insert([{
                    modifier_name: normalizedModifierName,
                    category: normalizedCategory,
                    price: parseFloat(modifierFormData.price),
                    applicable_categories: modifierFormData.applicable_categories,
                    ingredient_id: modifierFormData.ingredient_id || null
                }])

            if (error) throw error

            setModifierFormData({
                modifier_name: "",
                category: "",
                price: "",
                applicable_categories: [],
                ingredient_id: ""
            })

            setShowAddModifierModal(false)
            setModifierIngredientSearch("")

            Swal.fire({
                icon: "success",
                title: "Modifier Added!",
                text: "Modifier has been added successfully",
                confirmButtonColor: "#FF6F3C",
                timer: 2000
            })

            fetchModifiers()

        } catch (err) {
            Swal.fire({
                icon: "error",
                title: "Failed to Add Modifier",
                text: err.message,
                confirmButtonColor: "#FF6F3C"
            })
        }
    }

    const handleEditModifierClick = (modifier) => {
        const normalizedModifier = {
            modifier_name: normalizeModifierName(modifier.modifier_name || ""),
            category: normalizeModifierCategory(modifier.category || ""),
            price: String(modifier.price ?? ""),
            applicable_categories: modifier.applicable_categories || [],
            ingredient_id: modifier.ingredient_id || ""
        }

        setEditingModifier(modifier)
        setModifierFormData(normalizedModifier)
        setInitialModifierFormData(normalizedModifier)
        setModifierCategorySearch(normalizedModifier.category)
        setOpenModifierCategoryDropdown(false)

        const linkedIngredient = ingredients.find(
            ing => String(ing.ingredient_id) === String(modifier.ingredient_id || '')
        )
        setModifierIngredientSearch(linkedIngredient?.ingredient_name || linkedIngredient?.name || "")
        setOpenModifierIngredientDropdown(false)

        setShowEditModifierModal(true)
    }

    const handleUpdateModifier = async () => {
        if (!modifierFormData.modifier_name || !modifierFormData.category || modifierFormData.price === "") {
            Swal.fire({
                icon: "warning",
                title: "Missing Information",
                text: "Please fill in all fields",
                confirmButtonColor: "#FF6F3C"
            })
            return
        }

        if (!validateModifierForm()) return

        const normalizedModifierName = normalizeModifierName(modifierFormData.modifier_name)
        const normalizedCategory = normalizeModifierCategory(modifierFormData.category)

        if (isDuplicateModifier(normalizedModifierName, normalizedCategory, editingModifier.modifier_id)) {
            Swal.fire({
                icon: "warning",
                title: "Duplicate Modifier",
                text: `The modifier "${normalizedModifierName}" already exists under "${normalizedCategory}".`,
                confirmButtonColor: "#FF6F3C"
            })
            return
        }

        try {
            const { error } = await supabase
                .from('item_modifiers')
                .update({
                    modifier_name: normalizedModifierName,
                    category: normalizedCategory,
                    price: parseFloat(modifierFormData.price),
                    applicable_categories: modifierFormData.applicable_categories,
                    ingredient_id: modifierFormData.ingredient_id || null
                })
                .eq('modifier_id', editingModifier.modifier_id)

            if (error) throw error

            setShowEditModifierModal(false)
            setEditingModifier(null)
            setModifierIngredientSearch("")

            setModifierFormData({
                modifier_name: "",
                category: "",
                price: "",
                applicable_categories: [],
                ingredient_id: ""
            })

            Swal.fire({
                icon: "success",
                title: "Modifier Updated!",
                text: `${normalizedModifierName} has been updated`,
                confirmButtonColor: "#FF6F3C",
                timer: 3000
            })

            fetchModifiers()
        } catch (err) {
            Swal.fire({
                icon: "error",
                title: "Failed to Update Modifier",
                text: err.message,
                confirmButtonColor: "#FF6F3C"
            })
        }
    }

    const handleArchiveModifier = async (modifier) => {
        const result = await Swal.fire({
            title: "Archive Modifier?",
            text: `Are you sure you want to archive "${modifier.modifier_name}"?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d97706",
            cancelButtonColor: "#6b7280",
            confirmButtonText: "Yes, archive",
            cancelButtonText: "Cancel"
        })

        if (!result.isConfirmed) return

        try {
            const { error } = await supabase
                .from('item_modifiers')
                .update({
                    is_archived: true,
                    archived_at: new Date().toISOString()
                })
                .eq('modifier_id', modifier.modifier_id)

            if (error) throw error

            Swal.fire({
                icon: "success",
                title: "Modifier Archived",
                text: `"${modifier.modifier_name}" has been archived`,
                confirmButtonColor: "#FF6F3C",
                timer: 1800
            })

            fetchModifiers()
        } catch (err) {
            Swal.fire({
                icon: 'error',
                title: "Failed to Archive",
                text: err.message,
                confirmButtonColor: "#FF6F3C"
            })
        }
    }

    const formatBranchName = (name) =>
        name?.replace("Coffee Gus·to ", "") || ""

    const normalizeItemFormData = (data = {}) => ({
        name: String(data.name || '').trim(),
        item_price: String(data.item_price || '').trim(),
        category: String(data.category || '').trim(),
        classification: String(data.classification || '').trim(),
        description: String(data.description || '').trim(),
        item_img: String(data.item_img || '').trim()
    })

    const toTitleCase = (value = "") => {
        return String(value)
            .trim()
            .toLowerCase()
            .split(/\s+/)
            .filter(Boolean)
            .map(word =>
                word
                    .split('-')
                    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
                    .join('-')
            )
            .join(" ")
    }

    const normalizeItemName = (value = "") => {
        return toTitleCase(value)
    }

    const normalizeItemCategory = (value = "") => {
        return toTitleCase(value)
    }

    const normalizeItemClassification = (value = "") => {
        return toTitleCase(value)
    }

    const normalizeModifierCategory = (value = "") => {
        return toTitleCase(value)
    }

    const normalizeModifierName = (value = "") => {
        return toTitleCase(value)
    }

    const normalizeVariantCategory = (value = "") => {
        return toTitleCase(value)
    }

    const normalizeVariantName = (value = "") => {
        return toTitleCase(value)
    }

    const normalizeVariantFormData = (data = {}) => ({
        category: String(data.category || '').trim(),
        variant_name: String(data.variant_name || '').trim(),
        price: String(data.price || '').trim(),
        applicable_categories: [...(data.applicable_categories || [])].sort()
    })

    const normalizeVariantRuleFormData = (data = {}) => ({
        id: data.id ?? null,
        variant_id: String(data.variant_id || '').trim(),
        ingredient_id: String(data.ingredient_id || '').trim(),
        quantity_delta: String(data.quantity_delta || '').trim(),
        unit: String(data.unit || '').trim(),
        notes: String(data.notes || '').trim()
    })

    const areSetsEqual = (setA = new Set(), setB = new Set()) => {
        if (setA.size !== setB.size) return false
        for (const value of setA) {
            if (!setB.has(value)) return false
        }
        return true
    }

    const normalizeModifierFormData = (data = {}) => ({
        modifier_name: String(data.modifier_name || '').trim(),
        category: String(data.category || '').trim(),
        price: String(data.price || '').trim(),
        applicable_categories: [...(data.applicable_categories || [])].sort(),
        ingredient_id: String(data.ingredient_id || '').trim()
    })

    const hasAddItemChanges =
        JSON.stringify(normalizeItemFormData(itemFormData)) !==
        JSON.stringify(normalizeItemFormData({
            name: '',
            item_price: '',
            category: '',
            classification: '',
            description: '',
            item_img: ''
        })) ||
        selectedBranchIds.size > 0 ||
        itemImageFile !== null ||
        imageInputMode !== 'upload'

    const hasEditItemChanges =
        initialItemFormData !== null &&
        (
            JSON.stringify(normalizeItemFormData(itemFormData)) !==
            JSON.stringify(normalizeItemFormData(initialItemFormData)) ||
            !areSetsEqual(selectedBranchIds, initialSelectedBranchIds) ||
            itemImageFile !== null ||
            (initialItemFormData?.item_img ? imageInputMode !== 'url' : imageInputMode !== 'upload')
        )

    const hasAssignVariantChanges =
        !areSetsEqual(assignedVariantIds, initialAssignedVariantIds)

    const hasAddVariantChanges =
        JSON.stringify(normalizeVariantFormData(variantFormData)) !==
        JSON.stringify(normalizeVariantFormData({
            category: '',
            variant_name: '',
            price: ''
        }))

    const hasEditVariantChanges =
        initialVariantFormData !== null &&
        JSON.stringify(normalizeVariantFormData(variantFormData)) !==
        JSON.stringify(normalizeVariantFormData(initialVariantFormData))

    const hasVariantRuleChanges =
        JSON.stringify(normalizeVariantRuleFormData(variantRuleFormData)) !==
        JSON.stringify(normalizeVariantRuleFormData(initialVariantRuleFormData || {
            id: null,
            variant_id: '',
            ingredient_id: '',
            quantity_delta: '',
            unit: '',
            notes: ''
        }))

    const hasAddModifierChanges =
        JSON.stringify(normalizeModifierFormData(modifierFormData)) !==
        JSON.stringify(normalizeModifierFormData({
            modifier_name: '',
            category: '',
            price: '',
            ingredient_id: ''
        }))

    const hasEditModifierChanges =
        initialModifierFormData !== null &&
        JSON.stringify(normalizeModifierFormData(modifierFormData)) !==
        JSON.stringify(normalizeModifierFormData(initialModifierFormData))

    const hasModifierAssignmentChanges =
        !areSetsEqual(assignedModifierIds, initialAssignedModifierIds)

    // === Helpers ───────────────────────────────────────────────────────────────

   const filteredItems = [...menuItems]
        .filter(item => {
            const matchesSearch = item.name?.toLowerCase().includes(searchQuery.toLowerCase())
            const matchesCategory = filterCategory === "All" || item.category === filterCategory
            const matchesClassification = filterClassification === "All" || item.classification === filterClassification
            return matchesSearch && matchesCategory && matchesClassification
        })
        .sort((a, b) => {
            let result = 0
            if (itemSortField === 'item_price') {
                result = parseFloat(a.item_price || 0) - parseFloat(b.item_price || 0)
            } else {
                result = String(a[itemSortField] || '').localeCompare(String(b[itemSortField] || ''))
            }
            return itemSortDirection === 'asc' ? result : -result
        })

    const totalItemsInSelectedCategory = filterCategory === "All"
        ? menuItems.length
        : menuItems.filter(item => item.category === filterCategory).length

    const filteredVariantOptions = useMemo(() => {
        const query = variantRuleVariantSearch.toLowerCase()

        return [...itemVariants]
            .filter((variant) => {
                return (
                    String(variant.variant_name || '').toLowerCase().includes(query) ||
                    String(variant.category || '').toLowerCase().includes(query)
                )
            })
            .sort((a, b) => {
                const categoryCompare = String(a.category || '').localeCompare(String(b.category || ''))
                if (categoryCompare !== 0) return categoryCompare
                return String(a.variant_name || '').localeCompare(String(b.variant_name || ''))
            })
    }, [itemVariants, variantRuleVariantSearch])

    const filteredIngredientOptions = useMemo(() => {
        const query = variantRuleIngredientSearch.toLowerCase()

        return [...ingredients]
            .filter((ingredient) => {
                return (
                    String(ingredient.ingredient_name || ingredient.name || '').toLowerCase().includes(query) ||
                    String(ingredient.unit || '').toLowerCase().includes(query) ||
                    String(ingredient.category || '').toLowerCase().includes(query)
                )
            })
            .sort((a, b) =>
                String(a.ingredient_name || a.name || '').localeCompare(String(b.ingredient_name || b.name || ''))
            )
    }, [ingredients, variantRuleIngredientSearch])

    const getVariantLabelById = (variantId) => {
        const variant = itemVariants.find(
            (v) => String(v.variant_id) === String(variantId)
        )

        if (!variant) return '—'

        return `${variant.category ? `${variant.category} - ` : ''}${variant.variant_name || ''}`
    }

    const getIngredientLabelById = (ingredientId) => {
        const ingredient = ingredients.find(
            (ing) => String(ing.ingredient_id) === String(ingredientId)
        )

        return ingredient?.ingredient_name || ingredient?.name || '—'
    }

    const filteredModifiers = [...modifiers]
        .filter(modifier => {
            const matchesSearch = modifier.modifier_name?.toLowerCase().includes(modifierSearchQuery.toLowerCase())
            const matchesCategory = filterModifierCategory === "All" || modifier.category === filterModifierCategory
            return matchesSearch && matchesCategory
        })
        .sort((a, b) => (a.modifier_name || '').localeCompare(b.modifier_name || ''))

    const modifiersByCategory = Object.fromEntries(
        Object.entries(
            modifiers.reduce((acc, mod) => {
                if (!acc[mod.category]) acc[mod.category] = []
                acc[mod.category].push(mod)
                return acc
            }, {})
        )
            .sort(([categoryA], [categoryB]) => categoryA.localeCompare(categoryB))
            .map(([category, mods]) => [
                category,
                [...mods].sort((a, b) =>
                    (a.modifier_name || '').localeCompare(b.modifier_name || '')
                )
            ])
    )

    const toggleItemSort = (field) => {
        if (itemSortField === field) {
            setItemSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')
        } else {
            setItemSortField(field)
            setItemSortDirection('asc')
        }
    }

    const handlePriceKeyDown = (e) => {        
        const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', '.']
        if (allowedKeys.includes(e.key)) return
        // Block if not a digit
        if (!/^\d$/.test(e.key)) e.preventDefault()
    }

    const handleHorizontalWheelScroll = (e) => {
        if (e.deltaY === 0) return
        e.currentTarget.scrollLeft += e.deltaY
        e.preventDefault()
    }

    const dragScrollRef = useRef({ isDown: false, startX: 0, scrollLeft: 0, moved: false })

    const handleDragMouseDown = (e) => {
        const el = e.currentTarget
        dragScrollRef.current = {
            isDown: true,
            startX: e.pageX - el.offsetLeft,
            scrollLeft: el.scrollLeft,
            moved: false
        }
        el.classList.add('cursor-grabbing')
    }

    const handleDragMouseMove = (e) => {
        const state = dragScrollRef.current
        if (!state.isDown) return
        e.preventDefault()
        const el = e.currentTarget
        const x = e.pageX - el.offsetLeft
        const walk = x - state.startX
        if (Math.abs(walk) > 3) state.moved = true
        el.scrollLeft = state.scrollLeft - walk
    }

    const handleDragMouseUpOrLeave = (e) => {
        dragScrollRef.current.isDown = false
        e.currentTarget.classList.remove('cursor-grabbing')
    }

    const resetItemForm = () => {
        setItemFormData({
            name: '',
            item_price: '',
            category: '',
            classification: '',
            description: '',
            prep_time_minutes: '',
            item_img: ''
        })
        setSelectedBranchIds(new Set())
        setItemImageFile(null)
        setImageInputMode('upload')
        setItemCategorySearch('')
        setItemClassificationSearch('')
        setOpenItemCategoryDropdown(false)
        setOpenItemClassificationDropdown(false)
    }

    const confirmDiscardChanges = async (hasChanges, onConfirm) => {
        if (!hasChanges) {
            onConfirm()
            return
        }
        const result = await Swal.fire({
            title: 'Discard changes?',
            text: 'You have unsaved changes. Are you sure you want to close?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d97706',
            cancelButtonColor: '#6b7280',
            confirmButtonText: 'Yes, discard',
            cancelButtonText: 'Keep editing'
        })
        if (result.isConfirmed) onConfirm()
    }

    const getCategoryBadgeStyle = (category) => {
        switch (category) {
            case 'Add-ons': return 'bg-blue-100 text-blue-800'
            case 'Toppings': return 'bg-yellow-100 text-yellow-800'
            case 'Sinkers': return 'bg-orange-100 text-orange-800'
            default: return 'bg-gray-100 text-gray-800'
        }
    }

    const existingItemCategories = useMemo(() => {
        const normalized = menuItems
            .map((item) => normalizeItemCategory(item.category))
            .filter(Boolean)

        return [...new Set(normalized)].sort((a, b) => a.localeCompare(b))
    }, [menuItems])

    const filteredItemCategoryOptions = useMemo(() => {
        const query = String(itemCategorySearch || '').trim().toLowerCase()

        return existingItemCategories.filter((category) =>
            String(category || '').toLowerCase().includes(query)
        )
    }, [existingItemCategories, itemCategorySearch])

    const existingItemClassifications = useMemo(() => {
        const normalized = menuItems
            .map((item) => normalizeItemClassification(item.classification))
            .filter(Boolean)

        return [...new Set(normalized)].sort((a, b) => a.localeCompare(b))
    }, [menuItems])

    const filteredItemClassificationOptions = useMemo(() => {
        const query = String(itemClassificationSearch || '').trim().toLowerCase()

        return existingItemClassifications.filter((classification) =>
            String(classification || '').toLowerCase().includes(query)
        )
    }, [existingItemClassifications, itemClassificationSearch])

    const existingVariantCategories = useMemo(() => {
        const normalized = variants
            .map((variant) => normalizeVariantCategory(variant.category))
            .filter(Boolean)

        return [...new Set(normalized)].sort((a, b) => a.localeCompare(b))
    }, [variants])

    const filteredVariantCategoryOptions = useMemo(() => {
        const query = String(variantCategorySearch || '').trim().toLowerCase()

        return existingVariantCategories.filter((category) =>
            String(category || '').toLowerCase().includes(query)
        )
    }, [existingVariantCategories, variantCategorySearch])

    const existingModifierCategories = useMemo(() => {
        const normalized = modifiers
            .map((mod) => normalizeModifierCategory(mod.category))
            .filter(Boolean)
        return [...new Set(normalized)].sort((a, b) => a.localeCompare(b))
    }, [modifiers])

    const filteredModifierCategoryOptions = useMemo(() => {
        const query = String(modifierCategorySearch || '').trim().toLowerCase()
        return existingModifierCategories.filter((category) =>
            String(category || '').toLowerCase().includes(query)
        )
    }, [existingModifierCategories, modifierCategorySearch])

    // Branch menu computed values
    const itemBranchMap = useMemo(() => {
        const map = {}
        allBranchMenuItems.forEach(item => {
            if (!map[item.item_id]) map[item.item_id] = new Set()
            map[item.item_id].add(item.branch_id)
        })
        return map
    }, [allBranchMenuItems])

    const totalBranchCount = new Set(allBranchMenuItems.map(i => i.branch_id)).size

    const uniqueItemsList = useMemo(() => {
        return Object.keys(itemBranchMap).map(itemId => {
            const item = allBranchMenuItems.find(i => i.item_id === itemId)
            const branchCount = itemBranchMap[itemId].size
            return {
                item_id: itemId,
                name: item?.name || 'Unknown',
                category: item?.category || '—',
                classification: item?.classification || '—',
                item_img: item?.item_img || null,
                branchCount,
                isInAllBranches: branchCount === totalBranchCount
            }
        })
    }, [itemBranchMap, allBranchMenuItems, totalBranchCount])

    const menuSummary = useMemo(() => {
        const sourceItems = selectedBranchForMenu === 'all'
            ? allBranchMenuItems
            : allBranchMenuItems.filter(i => i.branch_id === selectedBranchForMenu)

        const drinkKeywords = ['coffee', 'tea', 'beverage', 'drink', 'juice', 'shake', 'smoothie']
        const foodKeywords = ['food', 'pastry', 'snack', 'meal', 'dessert', 'bread', 'sandwich']

        const uniqueItemIds = new Set(sourceItems.map(i => i.item_id))

        const coffeeSet = new Set()
        const foodSet = new Set()

        sourceItems.forEach(item => {
            const cat = String(item.category || '').toLowerCase()
            const cls = String(item.classification || '').toLowerCase()
            if (drinkKeywords.some(k => cat.includes(k) || cls.includes(k))) coffeeSet.add(item.item_id)
            if (foodKeywords.some(k => cat.includes(k) || cls.includes(k))) foodSet.add(item.item_id)
        })

        const totalCategories = new Set(sourceItems.map(i => i.category).filter(Boolean)).size
        const totalClassifications = new Set(sourceItems.map(i => i.classification).filter(Boolean)).size

        return {
            totalAssignments: sourceItems.length,
            uniqueItems: uniqueItemIds.size,
            coffeeItems: coffeeSet.size,
            foodItems: foodSet.size,
            totalCategories,
            totalClassifications,
        }
    }, [allBranchMenuItems, selectedBranchForMenu])

    const branchBreakdown = useMemo(() => {
        const grouped = {}
        allBranchMenuItems.forEach(item => {
            if (!grouped[item.branch_id]) {
                grouped[item.branch_id] = {
                    branch_id: item.branch_id,
                    branch_name: item.branch_name,
                    itemSet: new Set(),
                    coffeeSet: new Set(),
                    foodSet: new Set(),
                }
            }
            grouped[item.branch_id].itemSet.add(item.item_id)
            if (String(item.category).toLowerCase().includes('coffee')) {
                grouped[item.branch_id].coffeeSet.add(item.item_id)
            }
            const category = String(item.category).toLowerCase()
            const classification = String(item.classification).toLowerCase()
            if (
                classification.includes('food') || category.includes('food') ||
                category.includes('pastry') || category.includes('snack') ||
                category.includes('meal') || category.includes('dessert')
            ) {
                grouped[item.branch_id].foodSet.add(item.item_id)
            }
        })
        return Object.values(grouped).map(branch => ({
            branch_id: branch.branch_id,
            branch_name: branch.branch_name,
            totalItems: branch.itemSet.size,
            coffeeItems: branch.coffeeSet.size,
            foodItems: branch.foodSet.size,
        })).sort((a, b) => a.branch_name.localeCompare(b.branch_name))
    }, [allBranchMenuItems])

    const filteredBranchMenuItems = [...branchMenuItems]
        .filter(item => {
            const query = menuItemSearch.toLowerCase()
            return (
                String(item.name || '').toLowerCase().includes(query) ||
                String(item.category || '').toLowerCase().includes(query) ||
                String(item.classification || '').toLowerCase().includes(query) ||
                String(item.branch_name || '').toLowerCase().includes(query)
            )
        })
        .sort((a, b) => a.name.localeCompare(b.name))

    const totalMenuOverviewPages = Math.ceil(filteredBranchMenuItems.length / MENU_PAGE_SIZE)

    const paginatedBranchMenuItems = filteredBranchMenuItems.slice(
        (menuOverviewPage - 1) * MENU_PAGE_SIZE,
        menuOverviewPage * MENU_PAGE_SIZE
    )

    const VariantRuleGroups = ({ groupEntries, getVariantLabelById, getIngredientLabelById, handleEditVariantRule, handleDeleteVariantRule, query }) => {
        const [collapsedGroups, setCollapsedGroups] = useState({})

        const toggleGroup = (variantId) => {
            setCollapsedGroups(prev => ({ ...prev, [variantId]: !prev[variantId] }))
        }

        return (
            <div className="divide-y divide-gray-100">
                {groupEntries.map(([variantId, rules]) => {
                    const isCollapsed = collapsedGroups[variantId]
                    const variantLabel = getVariantLabelById(variantId)

                    const visibleRules = query
                        ? rules.filter(r =>
                            getVariantLabelById(r.variant_id).toLowerCase().includes(query) ||
                            getIngredientLabelById(r.ingredient_id).toLowerCase().includes(query) ||
                            String(r.notes || '').toLowerCase().includes(query)
                        )
                        : rules

                    return (
                        <div key={variantId}>
                            {/* Group header */}
                            <button
                                type="button"
                                onClick={() => toggleGroup(variantId)}
                                className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                            >
                                <div className="flex items-center gap-2">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
                                        fill="none" stroke="currentColor" strokeWidth="2"
                                        className={`text-gray-400 transition-transform duration-150 flex-shrink-0 ${isCollapsed ? '-rotate-90' : ''}`}
                                    >
                                        <path d="M6 9l6 6 6-6" />
                                    </svg>
                                    <span className="text-sm font-semibold text-gray-800">{variantLabel}</span>
                                    <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-[#FF6F3C]/10 text-[#FF6F3C]">
                                        {visibleRules.length} rule{visibleRules.length !== 1 ? 's' : ''}
                                    </span>
                                </div>
                            </button>

                            {/* Group rows */}
                            {!isCollapsed && (
                                <div>
                                    {/* Column headers */}
                                    <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-white border-b border-gray-100 text-xs font-medium text-gray-400 uppercase tracking-wider">
                                        <div className="col-span-4">Ingredient</div>
                                        <div className="col-span-2">Qty</div>
                                        <div className="col-span-2">Unit</div>
                                        <div className="col-span-2">Notes</div>
                                        <div className="col-span-2 text-right">Actions</div>
                                    </div>
                                    {visibleRules.map((rule) => (
                                        <div
                                            key={rule.id}
                                            className="grid grid-cols-12 gap-2 px-4 py-3 items-center border-b border-gray-50 last:border-b-0 hover:bg-gray-50 transition-colors"
                                        >
                                            <div className="col-span-4 text-sm text-gray-800">
                                                {getIngredientLabelById(rule.ingredient_id)}
                                            </div>
                                            <div className="col-span-2 text-sm text-gray-800">
                                                {parseFloat(rule.quantity_delta).toFixed(2)}
                                            </div>
                                            <div className="col-span-2 text-sm text-gray-600">
                                                {rule.unit || '—'}
                                            </div>
                                            <div className="col-span-2 text-sm text-gray-400 truncate" title={rule.notes || ''}>
                                                {rule.notes || '—'}
                                            </div>
                                            <div className="col-span-2 flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => handleEditVariantRule(rule)}
                                                    className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                                                    title="Edit rule"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24">
                                                        <path fill="currentColor" d="m14.06 9l.94.94L5.92 19H5v-.92zm3.6-6c-.25 0-.51.1-.7.29l-1.83 1.83l3.75 3.75l1.83-1.83c.39-.39.39-1.04 0-1.41l-2.34-2.34c-.2-.2-.45-.29-.71-.29m-3.6 3.19L3 17.25V21h3.75L17.81 9.94z" />
                                                    </svg>
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteVariantRule(rule.id)}
                                                    className="p-1 text-red-600 hover:bg-red-50 rounded"
                                                    title="Delete rule"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24">
                                                        <path fill="currentColor" d="M7 21q-.825 0-1.412-.587T5 19V6H4V4h5V3h6v1h5v2h-1v13q0 .825-.587 1.413T17 21zM17 6H7v13h10zM9 17h2V8H9zm4 0h2V8h-2z" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>
        )
    }

    const ApplicableCategoriesSelector = ({ value, onChange }) => {
        return (
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Applicable Item Categories
                    <span className="ml-1 text-xs font-normal text-gray-500">(check each category this applies to)</span>
                </label>
                {existingItemCategories.length === 0 ? (
                    <p className="text-sm text-gray-500 italic">No item categories found. Add menu items first.</p>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 border border-gray-200 rounded-lg p-3 max-h-40 overflow-y-auto">
                        {existingItemCategories.map(cat => {
                            const checked = value.includes(cat)
                            return (
                                <label
                                    key={cat}
                                    className={`flex items-center gap-2 p-2 rounded cursor-pointer border text-sm ${checked ? 'bg-indigo-50 border-indigo-300' : 'border-gray-200 hover:bg-gray-50'
                                        }`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => {
                                            if (checked) {
                                                onChange(value.filter(c => c !== cat))
                                            } else {
                                                onChange([...value, cat])
                                            }
                                        }}
                                        className="accent-indigo-600"
                                    />
                                    <span className={checked ? 'text-indigo-800 font-medium' : 'text-gray-700'}>{cat}</span>
                                </label>
                            )
                        })}
                    </div>
                )}
                <p className="text-xs text-gray-500 mt-1">
                    {value.length === 0
                        ? 'No categories selected — this will not appear in any assign modal.'
                        : `${value.length} categor${value.length === 1 ? 'y' : 'ies'} selected`}
                </p>
            </div>
        )
    }

    // ─── Pagination  ─────────────────────────────────────────────
    function Pagination({ page, totalItems, pageSize, onPageChange }) {
        const totalPages = Math.ceil(totalItems / pageSize)
        if (totalPages <= 1) return null

        return (
            <div className="flex items-center justify-between px-4 py-2 rounded-b-xl"
            >
                <span style={{ fontSize: 14, color: C.midgrey }}>
                    Showing <span className="font-medium text-gray-700">{Math.min((page - 1) * pageSize + 1, totalItems)}–{Math.min(page * pageSize, totalItems)}</span> of <span className="font-medium text-gray-700">{totalItems}</span> items
                </span>
                <div className="flex items-center gap-1">
                    <button
                        onClick={() => onPageChange(Math.max(1, page - 1))}
                        disabled={page === 1}
                        style={{
                            padding: "3px 10px", borderRadius: 8,
                            border: `1px solid ${C.border}`, background: C.card,
                            color: page === 1 ? C.border : C.textSoft,
                            fontSize: 14, cursor: page === 1 ? "not-allowed" : "pointer",
                        }}>
                        ← Prev
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(n => n === 1 || n === totalPages || Math.abs(n - page) <= 1)
                        .reduce((acc, n, idx, arr) => {
                            if (idx > 0 && n - arr[idx - 1] > 1) acc.push("…")
                            acc.push(n)
                            return acc
                        }, [])
                        .map((n, idx) =>
                            n === "…" ? (
                                <span key={`e-${idx}`} style={{ fontSize: 16, color: C.midgrey, padding: "0 2px" }}>…</span>
                            ) : (
                                <button key={n} onClick={() => onPageChange(n)}
                                    style={{
                                        width: 28, height: 28, borderRadius: 8,
                                        border: `1px solid ${n === page ? C.nude : C.border}`,
                                        background: n === page ? C.nude : C.card,
                                        color: n === page ? "#fff" : C.textSoft,
                                        fontSize: 14, fontWeight: n === page ? 600 : 400,
                                        cursor: "pointer",
                                    }}>
                                    {n}
                                </button>
                            )
                        )
                    }
                    <button
                        onClick={() => onPageChange(Math.min(totalPages, page + 1))}
                        disabled={page === totalPages}
                        style={{
                            padding: "3px 10px", borderRadius: 8,
                            border: `1px solid ${C.border}`, background: C.card,
                            color: page === totalPages ? C.border : C.textSoft,
                            fontSize: 14, cursor: page === totalPages ? "not-allowed" : "pointer",
                        }}>
                        Next →
                    </button>
                </div>
            </div>
        )
    }

    const normalizeBranchName = (name = '') =>
        String(name)
            .normalize('NFKC')       // unify unicode variants (handles the "·" character consistently)
            .replace(/\s+/g, ' ')    // collapse multiple/odd whitespace
            .trim()
            .toLowerCase()

    const activeBranchIdSet = useMemo(
        () => new Set(branches.map(b => String(b.branch_id))),
        [branches]
    )

    const getCurrentBranchIdsForItem = (itemId) => {
        return new Set(
            allBranchMenuItems
                .filter(b => String(b.item_id) === String(itemId))   // was: b.item_id === itemId
                .map(b => String(b.branch_id))
                .filter(id => activeBranchIdSet.has(id))
        )
    }

    const getTargetBranchIds = (row) => {
        const branchIds = row._branchIds === 'all'
            ? Array.from(bulkImportBranchIds)
            : row._branchIds

        return new Set(branchIds.map(String))
    }

    const areBranchIdSetsEqual = (setA, setB) => {
        if (setA.size !== setB.size) return false
        for (const id of setA) {
            if (!setB.has(id)) return false
        }
        return true
    }

    const getBulkImportRowStatus = (row) => {
        if (row._hasError) return { status: 'error', changes: [] }

        const existingItem = findDuplicateMenuItem(row.name, row.category, row.classification)
        if (!existingItem) return { status: 'new', changes: [] }

        const currentBranchIds = getCurrentBranchIdsForItem(existingItem.item_id)
        const targetBranchIds = getTargetBranchIds(row)

        const changes = []
        if (parseFloat(existingItem.item_price) !== parseFloat(row.item_price)) {
            changes.push(`Price: ₱${parseFloat(existingItem.item_price).toFixed(2)} → ₱${parseFloat(row.item_price).toFixed(2)}`)
        }
        if (String(existingItem.description || '').trim() !== String(row.description || '').trim()) {
            changes.push('Description')
        }
        if (String(existingItem.item_img || '') !== String(row.item_img || '')) {
            changes.push('Image')
        }
        if (!areBranchIdSetsEqual(currentBranchIds, targetBranchIds)) {
            changes.push('Branches')
        }

        if (changes.length === 0) return { status: 'duplicate', changes: [] }
        return { status: 'update', changes }
    }

    const bulkImportRowStatuses = bulkImportPreview.map(row => getBulkImportRowStatus(row))
    const bulkImportCounts = bulkImportRowStatuses.reduce((acc, r) => {
        acc[r.status] = (acc[r.status] || 0) + 1
        return acc
    }, { new: 0, update: 0, duplicate: 0, error: 0 })

    const pagedFilteredItems = filteredItems.slice((menuItemsPage - 1) * ITEMS_PAGE_SIZE, menuItemsPage * ITEMS_PAGE_SIZE)

    const filteredVariants = variants.filter(variant => {
        const matchesSearch =
            String(variant.variant_name || '').toLowerCase().includes(variantSearchQuery.toLowerCase()) ||
            String(variant.category || '').toLowerCase().includes(variantSearchQuery.toLowerCase())
        const matchesCategory = filterVariantCategory === "All" || variant.category === filterVariantCategory
        return matchesSearch && matchesCategory
    }).sort((a, b) => {
        const cat = String(a.category || '').localeCompare(String(b.category || ''))
        if (cat !== 0) return cat
        return String(a.variant_name || '').localeCompare(String(b.variant_name || ''))
    })
    const pagedVariants = filteredVariants.slice((variantsPage - 1) * ITEMS_PAGE_SIZE, variantsPage * ITEMS_PAGE_SIZE)

    const pagedModifiers = filteredModifiers.slice((modifiersPage - 1) * ITEMS_PAGE_SIZE, modifiersPage * ITEMS_PAGE_SIZE)

    return (
        <div className="flex-1 overflow-y-auto overflow-x-hidden bg-gray-50">            <Header
                title="Menu Management"
                subtitle="Create and manage your food and beverage offerings across all branches"
                onGoToProfile={onGoToProfile}
            />

            <div className="p-4 sm:p-6 lg:p-8">
                {/* Tab Navigation */}
                <div className="flex gap-1 mb-6 bg-white rounded-lg shadow-sm p-1 w-full sm:w-fit overflow-x-auto">
                    <button
                        onClick={() => { setActiveTab("menu_overview"); setMenuItemSearch(""); fetchPriceChangeRequests(); fetchMenuChangeRequests(); }}
                        className={`px-6 py-2.5 rounded-md font-medium text-sm transition-all flex-shrink-0 whitespace-nowrap ${activeTab === "menu_overview" ? 'bg-[#A0826D] text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'}`}
                    >
                        Branch Menu Overview
                    </button>

                    <button
                        onClick={() => { setActiveTab("menu"); setSearchQuery(""); setFilterCategory("All"); setFilterClassification("All"); setAvailableClassificationsForCategory([]) }}
                        className={`px-6 py-2.5 rounded-md font-medium text-sm transition-all ${activeTab === "menu" ? 'bg-[#FF6F3C] text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'}`}
                    >
                        Menu Items
                        {loading ? (
                            <span className={`ml-2 inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${activeTab === "menu" ? 'bg-white' : 'bg-orange-100'}`}>
                                <SkeletonBlock className="w-4 h-3" />
                            </span>
                        ) : menuItems.length > 0 && (
                            <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-semibold ${activeTab === "menu" ? 'bg-white text-[#FF6F3C]' : 'bg-orange-100 text-orange-700'}`}>
                                {menuItems.length}
                            </span>
                        )}
                    </button>

                    <button
                        onClick={() => { setActiveTab("variants"); setVariantSearchQuery("") }}
                        className={`px-6 py-2.5 rounded-md font-medium text-sm transition-all ${activeTab === "variants" ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'}`}
                    >
                        Variants
                        {loadingVariants ? (
                            <span className={`ml-2 inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${activeTab === "variants" ? 'bg-white' : 'bg-indigo-100'}`}>
                                <SkeletonBlock className="w-4 h-3" />
                            </span>
                        ) : variants.length > 0 && (
                            <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-semibold ${activeTab === "variants" ? 'bg-white text-indigo-600' : 'bg-indigo-100 text-indigo-700'}`}>
                                {variants.length}
                            </span>
                        )}
                    </button>

                    <button
                        onClick={() => { setActiveTab("modifiers"); setModifierSearchQuery("") }}
                        className={`px-6 py-2.5 rounded-md font-medium text-sm transition-all ${activeTab === "modifiers" ? 'bg-purple-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'}`}
                    >
                        Modifiers
                        {loadingModifiers ? (
                            <span className={`ml-2 inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${activeTab === "modifiers" ? 'bg-white' : 'bg-purple-100'}`}>
                                <SkeletonBlock className="w-4 h-3" />
                            </span>
                        ) : modifiers.length > 0 && (
                            <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-semibold ${activeTab === "modifiers" ? 'bg-white text-purple-600' : 'bg-purple-100 text-purple-700'}`}>
                                {modifiers.length}
                            </span>
                        )}
                    </button>
                </div>

                {/* BRANCH MENU OVERVIEW TAB */}
                {activeTab === "menu_overview" && (
                    <div className="space-y-4">
                        {/* Summary cards */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                            <div className="bg-white rounded-lg p-6 shadow-sm">
                                <div className="flex items-start justify-between mb-4">
                                    <div>
                                       <p className="text-sm text-gray-600 mb-1">Overall Menu Items</p>
                                        {loading ? (
                                            <SkeletonBlock className="h-9 w-16 mb-0.5" />
                                        ) : (
                                            <h2 className="text-3xl font-bold text-gray-800">{menuItems.filter(i => i.status !== 'discontinued').length}</h2>
                                        )}
                                        <p className="text-sm text-orange-600 mt-2">Distinct products offered</p>
                                    </div>
                                    <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" className="text-white"><path fill="currentColor" d="M3 5h18v14H3zm2 2v10h14V7zm2 2h10v2H7zm0 4h7v2H7z" /></svg>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-white rounded-lg p-6 shadow-sm">
                                <div className="flex items-start justify-between mb-4">
                                    <div>
                                        <p className="text-sm text-gray-600 mb-1">Drink Items</p>
                                        {branchMenuLoading ? (
                                            <SkeletonBlock className="h-9 w-16 mb-0.5" />
                                        ) : (
                                            <h2 className="text-3xl font-bold text-gray-800">{menuSummary.coffeeItems}</h2>
                                        )}
                                        <p className="text-sm text-green-600 mt-2">Category-based count</p>
                                    </div>
                                    <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
                                        <svg viewBox="0 0 34 42" className="w-6 h-6 text-white" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M28.6523 1.13536C28.4808 0.794927 28.2183 0.508646 27.894 0.308246C27.5697 0.107846 27.1963 0.00115539 26.8151 0H6.17222C5.38779 0 4.68593 0.4335 4.335 1.13536L2.84872 4.12858H0V8.25715H33.0286V4.12858H30.1799L28.6936 1.13536H28.6523ZM4.10793 39.3866C4.1905 40.46 5.07815 41.2858 6.17222 41.2858H26.8151C27.8885 41.2858 28.7968 40.46 28.8794 39.3866L31.1088 10.3214H1.8785L4.10793 39.3866ZM26.3197 18.5786L25.3701 30.9643H7.61722L6.66765 18.5786H26.3403H26.3197Z" fill="currentColor" /></svg>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-white rounded-lg p-6 shadow-sm">
                                <div className="flex items-start justify-between mb-4">
                                    <div>
                                        <p className="text-sm text-gray-600 mb-1">Food / Snack Items</p>
                                        {branchMenuLoading ? (
                                            <SkeletonBlock className="h-9 w-16 mb-0.5" />
                                        ) : (
                                            <h2 className="text-3xl font-bold text-gray-800">{menuSummary.foodItems}</h2>
                                        )}
                                        <p className="text-sm text-purple-600 mt-2">Classification-based count</p>
                                    </div>
                                    <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center">
                                        <svg viewBox="0 0 14 14" className="w-6 h-6 text-white" fill="none" xmlns="http://www.w3.org/2000/svg"><g clipPath="url(#clip0)"><path fillRule="evenodd" clipRule="evenodd" d="M1.184 1.09403C1.95082 0.38682 2.95686-0.00401942 4 3.11725e-05H10C11.05 3.11725e-05 12.064 0.390031 12.816 1.09403C13.1868 1.43548 13.4833 1.84966 13.687 2.31075C13.8907 2.77184 13.9973 3.26996 14 3.77403C14 4.16503 13.833 4.53403 13.549 4.80003C13.2635 5.06388 12.8888 5.20999 12.5 5.20903H1.5C1.11124 5.20999 0.736509 5.06388 0.451 4.80003C0.309087 4.66919 0.195765 4.51041 0.118153 4.33367C0.0405398 4.15694 0.000313367 3.96606 0 3.77303C0.00272084 3.26896 0.109255 2.77084 0.312961 2.30975C0.516666 1.84866 0.813175 1.43548 1.184 1.09403ZM0.77 6.75003C0.571088 6.75003 0.380322 6.82905 0.23967 6.9697C0.0990176 7.11035 0.02 7.30112 0.02 7.50003C0.02 7.69894 0.0990176 7.88971 0.23967 8.03036C0.380322 8.17101 0.571088 8.25003 0.77 8.25003H13.23C13.4289 8.25003 13.6197 8.17101 13.7603 8.03036C13.901 7.88971 13.98 7.69894 13.98 7.50003C13.98 7.30112 13.901 7.11035 13.7603 6.9697C13.6197 6.82905 13.4289 6.75003 13.23 6.75003H0.77ZM0.306 10.017C0.496 9.84103 0.746 9.74703 1 9.74703H2.5C2.57258 9.74727 2.64424 9.76331 2.71 9.79403L5.406 11.046L6.66 9.88103C6.75252 9.79543 6.87395 9.74792 7 9.74803H13C13.254 9.74803 13.504 9.84103 13.694 10.018C13.7899 10.1065 13.8666 10.2138 13.9193 10.3332C13.9719 10.4526 13.9994 10.5815 14 10.712C13.9971 11.1522 13.9034 11.587 13.7248 11.9893C13.5462 12.3915 13.2865 12.7527 12.962 13.05C12.2901 13.6652 11.411 14.0044 10.5 14H3.5C2.58903 14.0044 1.7099 13.6652 1.038 13.05C0.713492 12.7527 0.453819 12.3915 0.275217 11.9893C0.0966144 11.587 0.00292289 11.1522 0 10.712C0.000468548 10.5814 0.0278855 10.4522 0.0805369 10.3326C0.133188 10.213 0.209942 10.1056 0.306 10.017Z" fill="currentColor" /></g><defs><clipPath id="clip0"><rect width="14" height="14" fill="white" /></clipPath></defs></svg>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Header */}
                        <div className="flex items-center justify-between">
                            <div>
                                <h2 className="text-xl font-bold text-gray-800">Branch Menu Overview</h2>
                                <p className="text-sm text-gray-500 mt-1">Click a branch card to view menu items and price change requests</p>
                            </div>
                        </div>

                        {/* Unassigned Price Change Requests */}
                        {priceRequests.filter(r => r.branch_id === null && r.status === 'pending').length > 0 && (
                            <div className="bg-amber-50 border border-amber-200 rounded-xl shadow-sm overflow-hidden">
                                <div className="px-6 py-4 flex items-center gap-3 border-b border-amber-200">
                                    <div className="w-9 h-9 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24">
                                            <path fill="#d97706" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="font-semibold text-amber-800 text-sm">Unassigned Price Change Requests</p>
                                        <p className="text-xs text-amber-600 mt-0.5">
                                            These requests have no branch assigned — the branch app isn't sending <code className="bg-amber-100 px-1 rounded">branch_id</code> yet.
                                            {' '}<span className="font-semibold">{priceRequests.filter(r => r.branch_id === null).length} total request{priceRequests.filter(r => r.branch_id === null).length !== 1 ? 's' : ''}</span>
                                        </p>
                                    </div>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead className="bg-amber-50 border-b border-amber-100">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-amber-700 uppercase">Item</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-amber-700 uppercase">Type</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-amber-700 uppercase">Price Change</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-amber-700 uppercase">Reason</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-amber-700 uppercase">Date</th>
                                                <th className="px-6 py-3 text-right text-xs font-medium text-amber-700 uppercase">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-amber-50">
                                            {priceRequests.filter(r => r.branch_id === null).map(request => (
                                                <tr key={request.id} className="hover:bg-amber-50">
                                                    <td className="px-6 py-3">
                                                        <p className="text-sm font-medium text-gray-800">{request.item_name}</p>
                                                    </td>
                                                    <td className="px-6 py-3">
                                                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${request.item_type === 'menu_item' ? 'bg-orange-100 text-orange-700' :
                                                            request.item_type === 'variant' ? 'bg-indigo-100 text-indigo-700' :
                                                                'bg-purple-100 text-purple-700'
                                                            }`}>
                                                            {request.item_type.replace('_', ' ')}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-3 whitespace-nowrap">
                                                        <span className="line-through text-red-400 text-sm">₱{parseFloat(request.current_price).toFixed(2)}</span>
                                                        <span className="mx-1.5 text-gray-400">→</span>
                                                        <span className="text-emerald-600 font-bold text-sm">₱{parseFloat(request.requested_price).toFixed(2)}</span>
                                                    </td>
                                                    <td className="px-6 py-3 max-w-xs">
                                                        <p className="text-sm text-gray-600 truncate" title={request.reason}>{request.reason}</p>
                                                    </td>
                                                    <td className="px-6 py-3 whitespace-nowrap text-xs text-gray-500">
                                                        {new Date(request.created_at).toLocaleDateString('en-PH', { month: 'short', day: 'numeric', year: 'numeric' })}
                                                    </td>
                                                    <td className="px-6 py-3">
                                                        {request.status === 'pending' && (
                                                            <div className="flex items-center gap-2 justify-end">
                                                                <button
                                                                    onClick={() => handleApprovePriceRequest(request)}
                                                                    disabled={processingPriceRequestId === request.id}
                                                                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${processingPriceRequestId === request.id
                                                                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                                                        : 'bg-emerald-600 text-white hover:bg-emerald-700'
                                                                        }`}
                                                                >
                                                                    {processingPriceRequestId === request.id ? 'Processing...' : 'Approve'}
                                                                </button>
                                                                <button
                                                                    onClick={() => handleRejectPriceRequest(request)}
                                                                    disabled={processingPriceRequestId === request.id}
                                                                    className="px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-xs font-semibold hover:bg-red-200 disabled:opacity-50"
                                                                >
                                                                    Reject
                                                                </button>
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Requests from unknown/inactive branches */}
                        {(() => {
                            const activeBranchIds = new Set(branches.map(b => b.branch_id))
                            const orphanedRequests = priceRequests.filter(
                                r => r.branch_id !== null && !activeBranchIds.has(r.branch_id) && r.status === 'pending'
                            )
                            if (orphanedRequests.length === 0) return null
                            return (
                                <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
                                    <p className="font-semibold mb-1">⚠️ {orphanedRequests.length} pending request(s) linked to inactive/unknown branches</p>
                                    {orphanedRequests.map(r => (
                                        <p key={r.id}>{r.item_name} — branch_id: {r.branch_id}</p>
                                    ))}
                                </div>
                            )
                        })()}

                        {/* Branch Cards */}
                        <div className="space-y-3">
                            {[...branches].sort((a, b) => {
                                const hasPending = (branchId) => {
                                    const pendingPriceReqs = priceRequests.some(r => r.branch_id === branchId && r.status === 'pending')
                                    const pendingChangeReqs = menuChangeRequests.some(r => r.branch_id === branchId && r.status === 'pending')
                                    return pendingPriceReqs || pendingChangeReqs
                                }
                                const aPending = hasPending(a.branch_id)
                                const bPending = hasPending(b.branch_id)
                                if (aPending !== bPending) return aPending ? -1 : 1
                                return formatBranchName(a.name).localeCompare(formatBranchName(b.name))
                            }).map(branch => {
                                const isExpanded = expandedBranchId === branch.branch_id
                                const activeSubTab = branchMenuSubTab[branch.branch_id] || 'menu_items'
                                const branchItems = allBranchMenuItems.filter(i => i.branch_id === branch.branch_id)
                                const searchVal = branchMenuItemSearch[branch.branch_id] || ''
                                const currentPage = branchMenuItemPages[branch.branch_id] || 1
                                const PAGE_SIZE = 10

                                const filteredItems = branchItems
                                    .filter(item => {
                                        const q = searchVal.toLowerCase()
                                        return (
                                            item.name.toLowerCase().includes(q) ||
                                            item.category.toLowerCase().includes(q) ||
                                            item.classification.toLowerCase().includes(q)
                                        )
                                    })
                                    .sort((a, b) => a.name.localeCompare(b.name))

                                const paginatedItems = filteredItems.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)

                                const branchPriceRequests = priceRequests.filter(r =>
                                    r.branch_id === branch.branch_id
                                )

                                const branchMenuChangeRequests = menuChangeRequests.filter(r => r.branch_id === branch.branch_id && r.item_type === 'menu')
                                const MENU_CHANGE_PAGE_SIZE = 10
                                const currentMenuChangePage = branchMenuChangeRequestPages[branch.branch_id] || 1
                                const paginatedMenuChangeRequests = branchMenuChangeRequests.slice(
                                    (currentMenuChangePage - 1) * MENU_CHANGE_PAGE_SIZE,
                                    currentMenuChangePage * MENU_CHANGE_PAGE_SIZE
                                )

                                const branchVariantChangeRequests = menuChangeRequests.filter(r => r.branch_id === branch.branch_id && r.item_type === 'variant')
                                const currentVariantChangePage = branchVariantChangeRequestPages[branch.branch_id] || 1
                                const paginatedVariantChangeRequests = branchVariantChangeRequests.slice(
                                    (currentVariantChangePage - 1) * MENU_CHANGE_PAGE_SIZE,
                                    currentVariantChangePage * MENU_CHANGE_PAGE_SIZE
                                )

                                const branchModifierChangeRequests = menuChangeRequests.filter(r => r.branch_id === branch.branch_id && r.item_type === 'modifier')
                                const currentModifierChangePage = branchModifierChangeRequestPages[branch.branch_id] || 1
                                const paginatedModifierChangeRequests = branchModifierChangeRequests.slice(
                                    (currentModifierChangePage - 1) * MENU_CHANGE_PAGE_SIZE,
                                    currentModifierChangePage * MENU_CHANGE_PAGE_SIZE
                                )

                                const PRICE_REQ_PAGE_SIZE = 10
                                const currentPriceRequestPage = branchPriceRequestPages[branch.branch_id] || 1
                                const paginatedPriceRequests = branchPriceRequests.slice(
                                    (currentPriceRequestPage - 1) * PRICE_REQ_PAGE_SIZE,
                                    currentPriceRequestPage * PRICE_REQ_PAGE_SIZE
                                )

                                return (
                                    <div
                                        key={branch.branch_id}
                                        className={`bg-white rounded-xl border transition-all duration-200 shadow-sm overflow-hidden ${isExpanded ? 'border-[#A0826D]' : 'border-gray-200'
                                            }`}
                                    >
                                        {/* Card Header */}
                                        <button
                                            onClick={() => handleToggleMenuBranch(branch.branch_id)}
                                            className="w-full px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 hover:bg-gray-50 transition-colors text-left"
                                        >
                                            <div className="flex items-center gap-4 flex-1 min-w-0">
                                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isExpanded ? 'bg-[#A0826D] text-white' : 'bg-gray-100 text-gray-500'
                                                    }`}>
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 12c.263 0 .524-.06.767-.175a2 2 0 0 0 .65-.491c.186-.21.333-.46.433-.734s.15-.568.15-.864a2.4 2.4 0 0 0 .586 1.591c.375.422.884.659 1.414.659s1.04-.237 1.414-.659A2.4 2.4 0 0 0 12 9.736a2.4 2.4 0 0 0 .586 1.591c.375.422.884.659 1.414.659s1.04-.237 1.414-.659A2.4 2.4 0 0 0 16 9.736c0 .295.052.588.152.861s.248.521.434.73a2 2 0 0 0 .649.488a1.8 1.8 0 0 0 1.53 0a2 2 0 0 0 .65-.488c.185-.209.332-.457.433-.73s.152-.566.152-.861c0-.974-1.108-3.85-1.618-5.121A.98.98 0 0 0 17.466 4H6.456a.99.99 0 0 0-.93.645C5.045 5.962 4 8.905 4 9.736c.023.59.241 1.148.611 1.567c.37.418.865.667 1.389.697m0 0c.328 0 .651-.091.94-.266A2.1 2.1 0 0 0 7.66 11h.681a2.1 2.1 0 0 0 .718.734c.29.175.613.266.942.266s.651-.091.94-.266c.29-.174.537-.427.719-.734h.681a2.1 2.1 0 0 0 .719.734c.289.175.612.266.94.266c.329 0 .652-.091.942-.266c.29-.174.536-.427.718-.734h.681c.183.307.43.56.719.734c.29.174.613.266.941.266a1.8 1.8 0 0 0 1.06-.351M6 12a1.77 1.77 0 0 1-1.163-.476M5 12v7a1 1 0 0 0 1 1h2v-5h3v5h7a1 1 0 0 0 1-1v-7m-5 3v2h2v-2z" /></svg>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <p className="font-semibold text-gray-800 text-sm">{formatBranchName(branch.name)}</p>
                                                        {branchMenuLoading ? (
                                                            <SkeletonBlock className="w-16 h-3" />
                                                        ) : (
                                                            <span className="text-xs text-gray-500 whitespace-nowrap">
                                                                {branchItems.length} item{branchItems.length !== 1 ? 's' : ''}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-2 flex-wrap mt-1">
                                                        {(() => {
                                                            const branchReqs = priceRequests.filter(r => r.branch_id === branch.branch_id && r.status === 'pending')
                                                            const hasPriceChange = branchReqs.some(r => r.item_type === 'menu_item' || r.item_type === 'variant' || r.item_type === 'modifier')
                                                            return hasPriceChange ? (
                                                                <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200 whitespace-nowrap">
                                                                    Price Change Request
                                                                </span>
                                                            ) : null
                                                        })()}

                                                        {(() => {
                                                            const pendingMenuChanges = menuChangeRequests.filter(r => r.branch_id === branch.branch_id && r.status === 'pending' && r.item_type === 'menu')
                                                            return pendingMenuChanges.length > 0 ? (
                                                                <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-700 border border-green-200 whitespace-nowrap">
                                                                    Menu Change Request
                                                                </span>
                                                            ) : null
                                                        })()}

                                                        {(() => {
                                                            const pendingVariantChanges = menuChangeRequests.filter(r => r.branch_id === branch.branch_id && r.status === 'pending' && r.item_type === 'variant')
                                                            return pendingVariantChanges.length > 0 ? (
                                                                <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-100 text-indigo-700 border border-indigo-200 whitespace-nowrap">
                                                                    Variant Change Request
                                                                </span>
                                                            ) : null
                                                        })()}

                                                        {(() => {
                                                            const pendingModifierChanges = menuChangeRequests.filter(r => r.branch_id === branch.branch_id && r.status === 'pending' && r.item_type === 'modifier')
                                                            return pendingModifierChanges.length > 0 ? (
                                                                <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-purple-100 text-purple-700 border border-purple-200 whitespace-nowrap">
                                                                    Modifier Change Request
                                                                </span>
                                                            ) : null
                                                        })()}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap sm:flex-shrink-0 pl-14 sm:pl-0">
                                                {(() => {
                                                    const pendingCount = priceRequests.filter(r => r.branch_id === branch.branch_id && r.status === 'pending').length
                                                    return pendingCount > 0 ? (
                                                        <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 whitespace-nowrap">
                                                            {pendingCount} price request{pendingCount !== 1 ? 's' : ''}
                                                        </span>
                                                    ) : null
                                                })()}
                                                <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-700 flex items-center gap-1 whitespace-nowrap">
                                                    {branchMenuLoading ? <SkeletonBlock className="w-4 h-3" /> : branchItems.length} items
                                                </span>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                                                    className={`text-gray-400 transition-transform duration-200 ml-1 ${isExpanded ? 'rotate-180' : ''}`}>
                                                    <path d="M6 9l6 6 6-6" />
                                                </svg>
                                            </div>
                                        </button>

                                        {/* Expanded Content */}
                                        {isExpanded && (
                                            <div className="border-t border-gray-100">
                                                {/* Sub-tabs */}
                                                <div className="px-6 pt-4 pb-0 flex items-center justify-between gap-4 flex-wrap">
                                                    <div className="flex gap-1.5 mb-4 bg-gray-50 rounded-lg shadow-sm p-1 overflow-x-auto">
                                                        {['menu_items', 'price_requests', 'menu_change_requests', 'variant_change_requests', 'modifier_change_requests'].map(tab => (
                                                            <button
                                                                key={tab}
                                                                onClick={() => setBranchMenuSubTab(prev => ({ ...prev, [branch.branch_id]: tab }))}
                                                                className={`px-2.5 py-1.5 rounded-md text-xs font-medium transition-all whitespace-nowrap flex-shrink-0 ${activeSubTab === tab
                                                                    ? 'bg-[#A0826D] text-white shadow-sm'
                                                                    : 'text-gray-600 hover:bg-gray-100'
                                                                    }`}
                                                            >
                                                                {tab === 'menu_items' ? 'Menu Items'
                                                                    : tab === 'price_requests' ? 'Price Change Requests'
                                                                        : tab === 'menu_change_requests' ? 'Menu Item Requests'
                                                                            : tab === 'variant_change_requests' ? 'Variant Requests'
                                                                                : 'Modifier Requests'}
                                                                {tab === 'price_requests' && (() => {
                                                                    const pending = priceRequests.filter(r => r.branch_id === branch.branch_id && r.status === 'pending').length
                                                                    return pending > 0 ? <span className="ml-1 px-1 py-0.5 rounded-full text-[9px] font-bold bg-red-500 text-white">{pending}</span> : null
                                                                })()}
                                                                {tab === 'menu_change_requests' && (() => {
                                                                    const pending = menuChangeRequests.filter(r => r.branch_id === branch.branch_id && r.status === 'pending' && r.item_type === 'menu').length
                                                                    return pending > 0 ? <span className="ml-1 px-1 py-0.5 rounded-full text-[9px] font-bold bg-red-500 text-white">{pending}</span> : null
                                                                })()}
                                                                {tab === 'variant_change_requests' && (() => {
                                                                    const pending = menuChangeRequests.filter(r => r.branch_id === branch.branch_id && r.status === 'pending' && r.item_type === 'variant').length
                                                                    return pending > 0 ? <span className="ml-1 px-1 py-0.5 rounded-full text-[9px] font-bold bg-red-500 text-white">{pending}</span> : null
                                                                })()}
                                                                {tab === 'modifier_change_requests' && (() => {
                                                                    const pending = menuChangeRequests.filter(r => r.branch_id === branch.branch_id && r.status === 'pending' && r.item_type === 'modifier').length
                                                                    return pending > 0 ? <span className="ml-1 px-1 py-0.5 rounded-full text-[9px] font-bold bg-red-500 text-white">{pending}</span> : null
                                                                })()}
                                                            </button>
                                                        ))}
                                                    </div>

                                                    {activeSubTab === 'menu_items' && (
                                                        <div className="relative w-full sm:w-64 mb-4">
                                                            <input
                                                                type="text"
                                                                placeholder="Search menu items..."
                                                                value={searchVal}
                                                                onChange={e => setBranchMenuItemSearch(prev => ({ ...prev, [branch.branch_id]: e.target.value }))}
                                                                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                                            />
                                                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10a7 7 0 1 0 14 0a7 7 0 1 0-14 0m18 11l-6-6" /></svg>
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Menu Items Sub-tab */}
                                                {activeSubTab === 'menu_items' && (
                                                    <div className="overflow-x-auto">
                                                        <table className="w-full">
                                                            <thead className="bg-gray-50 border-y border-gray-100">
                                                                <tr>
                                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item</th>
                                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Classification</th>
                                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="bg-white divide-y divide-gray-100">
                                                                {filteredItems.length === 0 ? (
                                                                    <tr>
                                                                        <td colSpan="4" className="px-6 py-8 text-center text-gray-500 text-sm">
                                                                            {searchVal ? 'No items match your search.' : 'No menu items assigned to this branch.'}
                                                                        </td>
                                                                    </tr>
                                                                ) : (
                                                                    paginatedItems.map(item => (
                                                                        <tr key={item.id} className="hover:bg-gray-50">
                                                                            <td className="px-6 py-3">
                                                                                <div className="flex items-center gap-3">
                                                                                    {item.item_img ? (
                                                                                        <img src={item.item_img} alt={item.name} className="w-9 h-9 rounded-lg object-cover border border-gray-200" />
                                                                                    ) : (
                                                                                        <div className="w-9 h-9 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center text-[10px] text-gray-400">N/A</div>
                                                                                    )}
                                                                                    <p className="text-sm font-medium text-gray-800">{item.name}</p>
                                                                                </div>
                                                                            </td>
                                                                            <td className="px-6 py-3 text-sm text-gray-600">{item.category}</td>
                                                                            <td className="px-6 py-3 text-sm text-gray-600">{item.classification}</td>
                                                                            <td className="px-6 py-3 text-sm font-semibold text-gray-800">₱{parseFloat(item.price || 0).toFixed(2)}</td>
                                                                        </tr>
                                                                    ))
                                                                )}
                                                            </tbody>
                                                        </table>
                                                        <Pagination
                                                            page={currentPage}
                                                            totalItems={filteredItems.length}
                                                            pageSize={PAGE_SIZE}
                                                            onPageChange={p => setBranchMenuItemPages(prev => ({ ...prev, [branch.branch_id]: p }))}
                                                        />
                                                    </div>
                                                )}

                                                {/* Menu Change Requests Sub-tab */}
                                                {activeSubTab === 'menu_change_requests' && (
                                                    <div className="overflow-x-auto">
                                                        <table className="w-full">
                                                            <thead className="bg-gray-50 border-y border-gray-100">
                                                                <tr>
                                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item</th>
                                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="bg-white divide-y divide-gray-100">
                                                                {branchMenuChangeRequests.length === 0 ? (
                                                                    <tr>
                                                                        <td colSpan="6" className="px-6 py-8 text-center text-gray-500 text-sm">
                                                                            No menu change requests for this branch.
                                                                        </td>
                                                                    </tr>
                                                                ) : (
                                                                    paginatedMenuChangeRequests.map(request => (
                                                                        <tr key={request.id} className="hover:bg-gray-50">
                                                                            <td className="px-6 py-3">
                                                                                <p className="text-sm font-medium text-gray-800">{request.item_name}</p>
                                                                            </td>
                                                                            <td className="px-6 py-3">
                                                                                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${request.request_type === 'add' ? 'bg-green-100 text-green-700' :
                                                                                    request.request_type === 'reinstate' ? 'bg-emerald-100 text-emerald-700' :
                                                                                        'bg-red-100 text-red-700'
                                                                                    }`}>
                                                                                    {request.request_type === 'add' ? 'New' : request.request_type === 'reinstate' ? 'Reinstate' : 'Delete'}{' '}
                                                                                    {request.item_type === 'variant' ? 'Variant' : request.item_type === 'modifier' ? 'Modifier' : 'Item'}
                                                                                </span>
                                                                            </td>
                                                                            <td className="px-6 py-3 max-w-xs">
                                                                                <p className="text-sm text-gray-600 truncate" title={request.reason}>{request.reason}</p>
                                                                            </td>
                                                                            <td className="px-6 py-3">
                                                                                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${request.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                                                                    request.status === 'approved' ? 'bg-green-100 text-green-800' :
                                                                                        'bg-red-100 text-red-800'
                                                                                    }`}>
                                                                                    {request.status}
                                                                                </span>
                                                                            </td>
                                                                            <td className="px-6 py-3 whitespace-nowrap text-xs text-gray-500">
                                                                                {new Date(request.created_at).toLocaleDateString('en-PH', { month: 'short', day: 'numeric', year: 'numeric' })}
                                                                            </td>
                                                                            <td className="px-6 py-3">
                                                                                {request.status === 'pending' ? (
                                                                                    <div className="flex items-center gap-2 justify-end">
                                                                                        <button
                                                                                            onClick={() => handleApproveMenuChangeRequest(request)}
                                                                                            disabled={processingMenuChangeRequestId === request.id}
                                                                                            className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-medium hover:bg-emerald-700 disabled:opacity-50"
                                                                                        >
                                                                                            Approve
                                                                                        </button>
                                                                                        <button
                                                                                            onClick={() => handleRejectMenuChangeRequestWithReason(request)}
                                                                                            disabled={processingMenuChangeRequestId === request.id}
                                                                                            className="px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-xs font-medium hover:bg-red-200 disabled:opacity-50"
                                                                                        >
                                                                                            Reject
                                                                                        </button>
                                                                                    </div>
                                                                                ) : (
                                                                                    <span className="text-xs text-gray-400 italic">
                                                                                        {request.status === 'approved' ? 'Approved' : 'Rejected'}
                                                                                    </span>
                                                                                )}
                                                                            </td>
                                                                        </tr>
                                                                    ))
                                                                )}
                                                            </tbody>
                                                        </table>
                                                        <Pagination
                                                            page={currentMenuChangePage}
                                                            totalItems={branchMenuChangeRequests.length}
                                                            pageSize={MENU_CHANGE_PAGE_SIZE}
                                                            onPageChange={p => setBranchMenuChangeRequestPages(prev => ({ ...prev, [branch.branch_id]: p }))}
                                                        />
                                                    </div>
                                                )}

                                                {/* Variant Change Requests Sub-tab */}
                                                {activeSubTab === 'variant_change_requests' && (
                                                    <div className="overflow-x-auto">
                                                        <table className="w-full">
                                                            <thead className="bg-gray-50 border-y border-gray-100">
                                                                <tr>
                                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Variant</th>
                                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="bg-white divide-y divide-gray-100">
                                                                {branchVariantChangeRequests.length === 0 ? (
                                                                    <tr>
                                                                        <td colSpan="6" className="px-6 py-8 text-center text-gray-500 text-sm">
                                                                            No variant requests for this branch.
                                                                        </td>
                                                                    </tr>
                                                                ) : (
                                                                    paginatedVariantChangeRequests.map(request => (
                                                                        <tr key={request.id} className="hover:bg-gray-50">
                                                                            <td className="px-6 py-3">
                                                                                <p className="text-sm font-medium text-gray-800">{request.item_name}</p>
                                                                            </td>
                                                                            <td className="px-6 py-3">
                                                                                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${request.request_type === 'add' ? 'bg-green-100 text-green-700' :
                                                                                    request.request_type === 'reinstate' ? 'bg-emerald-100 text-emerald-700' :
                                                                                        'bg-red-100 text-red-700'
                                                                                    }`}>
                                                                                    {request.request_type === 'add' ? 'New Variant' : request.request_type === 'reinstate' ? 'Reinstate Variant' : 'Delete Variant'}
                                                                                </span>
                                                                            </td>
                                                                            <td className="px-6 py-3 max-w-xs">
                                                                                <p className="text-sm text-gray-600 truncate" title={request.reason}>{request.reason}</p>
                                                                            </td>
                                                                            <td className="px-6 py-3">
                                                                                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${request.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                                                                    request.status === 'approved' ? 'bg-green-100 text-green-800' :
                                                                                        'bg-red-100 text-red-800'
                                                                                    }`}>
                                                                                    {request.status}
                                                                                </span>
                                                                            </td>
                                                                            <td className="px-6 py-3 whitespace-nowrap text-xs text-gray-500">
                                                                                {new Date(request.created_at).toLocaleDateString('en-PH', { month: 'short', day: 'numeric', year: 'numeric' })}
                                                                            </td>
                                                                            <td className="px-6 py-3">
                                                                                {request.status === 'pending' ? (
                                                                                    <div className="flex items-center gap-2 justify-end">
                                                                                        <button
                                                                                            onClick={() => handleApproveMenuChangeRequest(request)}
                                                                                            disabled={processingMenuChangeRequestId === request.id}
                                                                                            className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-medium hover:bg-emerald-700 disabled:opacity-50"
                                                                                        >
                                                                                            Approve
                                                                                        </button>
                                                                                        <button
                                                                                            onClick={() => handleRejectMenuChangeRequestWithReason(request)}
                                                                                            disabled={processingMenuChangeRequestId === request.id}
                                                                                            className="px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-xs font-medium hover:bg-red-200 disabled:opacity-50"
                                                                                        >
                                                                                            Reject
                                                                                        </button>
                                                                                    </div>
                                                                                ) : (
                                                                                    <span className="text-xs text-gray-400 italic">
                                                                                        {request.status === 'approved' ? 'Approved' : 'Rejected'}
                                                                                    </span>
                                                                                )}
                                                                            </td>
                                                                        </tr>
                                                                    ))
                                                                )}
                                                            </tbody>
                                                        </table>
                                                        <Pagination
                                                            page={currentVariantChangePage}
                                                            totalItems={branchVariantChangeRequests.length}
                                                            pageSize={MENU_CHANGE_PAGE_SIZE}
                                                            onPageChange={p => setBranchVariantChangeRequestPages(prev => ({ ...prev, [branch.branch_id]: p }))}
                                                        />
                                                    </div>
                                                )}

                                                {/* Modifier Change Requests Sub-tab */}
                                                {activeSubTab === 'modifier_change_requests' && (
                                                    <div className="overflow-x-auto">
                                                        <table className="w-full">
                                                            <thead className="bg-gray-50 border-y border-gray-100">
                                                                <tr>
                                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Modifier</th>
                                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="bg-white divide-y divide-gray-100">
                                                                {branchModifierChangeRequests.length === 0 ? (
                                                                    <tr>
                                                                        <td colSpan="6" className="px-6 py-8 text-center text-gray-500 text-sm">
                                                                            No modifier requests for this branch.
                                                                        </td>
                                                                    </tr>
                                                                ) : (
                                                                    paginatedModifierChangeRequests.map(request => (
                                                                        <tr key={request.id} className="hover:bg-gray-50">
                                                                            <td className="px-6 py-3">
                                                                                <p className="text-sm font-medium text-gray-800">{request.item_name}</p>
                                                                            </td>
                                                                            <td className="px-6 py-3">
                                                                                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${request.request_type === 'add' ? 'bg-green-100 text-green-700' :
                                                                                    request.request_type === 'reinstate' ? 'bg-emerald-100 text-emerald-700' :
                                                                                        'bg-red-100 text-red-700'
                                                                                    }`}>
                                                                                    {request.request_type === 'add' ? 'New Modifier' : request.request_type === 'reinstate' ? 'Reinstate Modifier' : 'Delete Modifier'}
                                                                                </span>
                                                                            </td>
                                                                            <td className="px-6 py-3 max-w-xs">
                                                                                <p className="text-sm text-gray-600 truncate" title={request.reason}>{request.reason}</p>
                                                                            </td>
                                                                            <td className="px-6 py-3">
                                                                                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${request.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                                                                    request.status === 'approved' ? 'bg-green-100 text-green-800' :
                                                                                        'bg-red-100 text-red-800'
                                                                                    }`}>
                                                                                    {request.status}
                                                                                </span>
                                                                            </td>
                                                                            <td className="px-6 py-3 whitespace-nowrap text-xs text-gray-500">
                                                                                {new Date(request.created_at).toLocaleDateString('en-PH', { month: 'short', day: 'numeric', year: 'numeric' })}
                                                                            </td>
                                                                            <td className="px-6 py-3">
                                                                                {request.status === 'pending' ? (
                                                                                    <div className="flex items-center gap-2 justify-end">
                                                                                        <button
                                                                                            onClick={() => handleApproveMenuChangeRequest(request)}
                                                                                            disabled={processingMenuChangeRequestId === request.id}
                                                                                            className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-medium hover:bg-emerald-700 disabled:opacity-50"
                                                                                        >
                                                                                            Approve
                                                                                        </button>
                                                                                        <button
                                                                                            onClick={() => handleRejectMenuChangeRequestWithReason(request)}
                                                                                            disabled={processingMenuChangeRequestId === request.id}
                                                                                            className="px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-xs font-medium hover:bg-red-200 disabled:opacity-50"
                                                                                        >
                                                                                            Reject
                                                                                        </button>
                                                                                    </div>
                                                                                ) : (
                                                                                    <span className="text-xs text-gray-400 italic">
                                                                                        {request.status === 'approved' ? 'Approved' : 'Rejected'}
                                                                                    </span>
                                                                                )}
                                                                            </td>
                                                                        </tr>
                                                                    ))
                                                                )}
                                                            </tbody>
                                                        </table>
                                                        <Pagination
                                                            page={currentModifierChangePage}
                                                            totalItems={branchModifierChangeRequests.length}
                                                            pageSize={MENU_CHANGE_PAGE_SIZE}
                                                            onPageChange={p => setBranchModifierChangeRequestPages(prev => ({ ...prev, [branch.branch_id]: p }))}
                                                        />
                                                    </div>
                                                )}

                                                {/* Price Change Requests Sub-tab */}
                                                {activeSubTab === 'price_requests' && (
                                                    <div className="overflow-x-auto">
                                                        <table className="w-full">
                                                            <thead className="bg-gray-50 border-y border-gray-100">
                                                                <tr>
                                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item</th>
                                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price Change</th>
                                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="bg-white divide-y divide-gray-100">
                                                                {branchPriceRequests.length === 0 ? (
                                                                    <tr>
                                                                        <td colSpan="7" className="px-6 py-8 text-center text-gray-500 text-sm">
                                                                            No price change requests for this branch.
                                                                        </td>
                                                                    </tr>
                                                                ) : (
                                                                    paginatedPriceRequests.map(request => (<tr key={request.id} className="hover:bg-gray-50">
                                                                        <td className="px-6 py-3">
                                                                            <p className="text-sm font-medium text-gray-800">{request.item_name}</p>
                                                                        </td>
                                                                        <td className="px-6 py-3">
                                                                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${request.item_type === 'menu_item' ? 'bg-orange-100 text-orange-700' :
                                                                                request.item_type === 'variant' ? 'bg-indigo-100 text-indigo-700' :
                                                                                    'bg-purple-100 text-purple-700'
                                                                                }`}>
                                                                                {request.item_type.replace('_', ' ')}
                                                                            </span>
                                                                        </td>
                                                                        <td className="px-6 py-3 whitespace-nowrap">
                                                                            <span className="line-through text-red-400 text-sm">₱{parseFloat(request.current_price).toFixed(2)}</span>
                                                                            <span className="mx-1.5 text-gray-400">→</span>
                                                                            <span className="text-emerald-600 font-bold text-sm">₱{parseFloat(request.requested_price).toFixed(2)}</span>
                                                                        </td>
                                                                        <td className="px-6 py-3 max-w-xs">
                                                                            <p className="text-sm text-gray-600 truncate" title={request.reason}>{request.reason}</p>
                                                                        </td>
                                                                        <td className="px-6 py-3">
                                                                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${request.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                                                                request.status === 'approved' ? 'bg-green-100 text-green-800' :
                                                                                    'bg-red-100 text-red-800'
                                                                                }`}>
                                                                                {request.status}
                                                                            </span>
                                                                        </td>
                                                                        <td className="px-6 py-3 whitespace-nowrap text-xs text-gray-500">
                                                                            {new Date(request.created_at).toLocaleDateString('en-PH', { month: 'short', day: 'numeric', year: 'numeric' })}
                                                                        </td>
                                                                        <td className="px-6 py-3">
                                                                            {request.status === 'pending' && (
                                                                                <div className="flex items-center gap-2 justify-end">
                                                                                    <button
                                                                                        onClick={() => handleApprovePriceRequest(request)}
                                                                                        disabled={processingPriceRequestId === request.id}
                                                                                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${processingPriceRequestId === request.id
                                                                                            ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                                                                            : 'bg-emerald-600 text-white hover:bg-emerald-700'
                                                                                            }`}
                                                                                    >
                                                                                        {processingPriceRequestId === request.id ? 'Processing...' : 'Approve'}
                                                                                    </button>
                                                                                    <button
                                                                                        onClick={() => handleRejectPriceRequest(request)}
                                                                                        disabled={processingPriceRequestId === request.id}
                                                                                        className="px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-xs font-semibold hover:bg-red-200 disabled:opacity-50"
                                                                                    >
                                                                                        Reject
                                                                                    </button>
                                                                                </div>
                                                                            )}
                                                                        </td>
                                                                    </tr>
                                                                    ))
                                                                )}
                                                            </tbody>
                                                        </table>
                                                        <Pagination
                                                            page={currentPriceRequestPage}
                                                            totalItems={branchPriceRequests.length}
                                                            pageSize={PRICE_REQ_PAGE_SIZE}
                                                            onPageChange={p => setBranchPriceRequestPages(prev => ({ ...prev, [branch.branch_id]: p }))}
                                                        />
                                                    </div>
                                                )}

                                                <div className="h-4" />
                                            </div>
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                )}

                {activeTab === "menu" && (
                    <div className="bg-white rounded-lg shadow-sm">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex flex-col gap-4">
                                <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                                    <div>
                                        <div className="flex items-center gap-3">
                                            <h2 className="text-xl font-bold text-gray-800">Menu Items</h2>
                                            <button
                                                onClick={() => {
                                                    setBulkImportFile(null)
                                                    setBulkImportPreview([])
                                                    setBulkImportErrors([])
                                                    setBulkImportResult(null)
                                                    setBulkImportBranchIds(new Set())
                                                    setShowBulkImportModal(true)
                                                }}
                                                disabled={bulkImporting}
                                                className="px-3 py-1.5 bg-gray-700 text-white rounded-lg text-xs font-medium hover:bg-gray-800 flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"><path fill="currentColor" d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zm-1 7V3.5L18.5 9zM8 13h3v-3h2v3h3l-4 4z" /></svg>
                                                Import CSV
                                            </button>
                                            <button
                                                onClick={exportMenuItemsCSV}
                                                disabled={filteredItems.length === 0}
                                                className="px-3 py-1.5 bg-green-700 text-white rounded-lg text-xs font-medium hover:bg-green-800 flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"><path fill="currentColor" d="M5 20h14v-2H5zm7-18L5.33 9h4.84v6h3.66V9h4.84z" /></svg>
                                                Export CSV
                                            </button>
                                        </div>
                                        <p className="text-sm text-gray-500 mt-1">Manage your menu items across all branches</p>
                                    </div>

                                    <div className="flex flex-wrap items-center gap-3">
                                        <div className="relative">
                                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24">
                                                    <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10a7 7 0 1 0 14 0a7 7 0 1 0-14 0m18 11l-6-6" />
                                                </svg>
                                            </span>
                                            <input
                                                type="text"
                                                placeholder="Search Menu items..."
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                                className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6F3C] w-full sm:w-52"                                            />
                                        </div>

                                        <button
                                            onClick={openArchivedViewerModal}
                                            className="p-2 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg"
                                            title="View Archived"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                                                <path fill="currentColor" d="M3 3h18a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1h-1v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V9H3a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1m3 6v10h12V9zm2 2h8v2H8zm0 4h8v2H8zM5 5v2h14V5z" />
                                            </svg>
                                        </button>
                                        <button
                                            onClick={() => setShowAddItemModal(true)}
                                            className="px-4 py-2 bg-[#FF6F3C] text-white rounded-lg font-medium hover:bg-[#ff4c0b] flex items-center gap-2"
                                        >
                                            <span>+</span>
                                            <span>Add Menu Item</span>
                                        </button>
                                    </div>
                                </div>

                                <div className="flex flex-col gap-2">
                                    {/* Level 1: Category buttons */}
                                    <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide"
                                            onMouseDown={handleDragMouseDown}
                                            onMouseMove={handleDragMouseMove}
                                            onMouseUp={handleDragMouseUpOrLeave}
                                            onMouseLeave={handleDragMouseUpOrLeave}
                                        >
                                        {['All', ...[...new Set(menuItems.map(item => item.category).filter(Boolean))].sort()].map(cat => (
                                            <button
                                                key={cat}
                                                onClick={() => {
                                                    setFilterCategory(cat)
                                                    setFilterClassification('All')
                                                    if (cat === 'All') {
                                                        setAvailableClassificationsForCategory([])
                                                    } else {
                                                        const classifications = [
                                                            ...new Set(
                                                                menuItems
                                                                    .filter(item => item.category === cat)
                                                                    .map(item => item.classification)
                                                                    .filter(Boolean)
                                                            )
                                                        ].sort()
                                                        setAvailableClassificationsForCategory(classifications)
                                                    }
                                                }}
                                                className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors flex-shrink-0 whitespace-nowrap ${filterCategory === cat
                                                    ? 'bg-[#FF6F3C] text-white'
                                                    : 'text-gray-600 hover:bg-gray-100'
                                                    }`}
                                            >
                                                {cat}
                                                {cat !== 'All' && (
                                                    <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${filterCategory === cat
                                                        ? 'bg-white/30 text-white'
                                                        : 'bg-gray-100 text-gray-500'
                                                        }`}>
                                                        {menuItems.filter(i => i.category === cat).length}
                                                    </span>
                                                )}
                                            </button>
                                        ))}
                                    </div>

                                    {/* Level 2: Classification chips — only shown when a category is selected */}
                                    {filterCategory !== 'All' && availableClassificationsForCategory.length > 0 && (
                                        <div className="flex items-center gap-2 overflow-x-auto pb-1 pl-1 scrollbar-hide"
                                            onMouseDown={handleDragMouseDown}
                                            onMouseMove={handleDragMouseMove}
                                            onMouseUp={handleDragMouseUpOrLeave}
                                            onMouseLeave={handleDragMouseUpOrLeave}
                                        >
                                            <span className="text-xs text-gray-400 font-medium shrink-0">
                                                {filterCategory}:
                                            </span>
                                            {['All', ...availableClassificationsForCategory].map(cls => (
                                                <button
                                                    key={cls}
                                                    onClick={() => setFilterClassification(cls)}
                                                    className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all flex-shrink-0 whitespace-nowrap ${filterClassification === cls
                                                        ? 'bg-[#303030] border-[#303030] text-white'
                                                        : 'bg-white border-gray-300 text-gray-600 hover:border-gray-500'
                                                        }`}
                                                >
                                                    {cls === 'All' ? `All ${filterCategory}` : cls}
                                                    {cls !== 'All' && (
                                                        <span className={`ml-1 ${filterClassification === cls ? 'opacity-70' : 'text-gray-400'}`}>
                                                            ({menuItems.filter(i => i.category === filterCategory && i.classification === cls).length})
                                                        </span>
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full">
                               <thead className="bg-gray-50 border-b border-gray-200">
                                    <tr>
                                        {[
                                            { key: 'name', label: 'Item' },
                                            { key: 'category', label: 'Category' },
                                            { key: 'classification', label: 'Classification' },
                                            { key: 'item_price', label: 'Price' },
                                            { key: 'status', label: 'Status' },
                                        ].map(col => (
                                            <th key={col.key} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                <button
                                                    onClick={() => toggleItemSort(col.key)}
                                                    className="flex items-center gap-1 hover:text-gray-700"
                                                >
                                                    {col.label}
                                                    {itemSortField === col.key && (
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                            {itemSortDirection === 'asc' ? <path d="M6 15l6-6 6 6" /> : <path d="M6 9l6 6 6-6" />}
                                                        </svg>
                                                    )}
                                                </button>
                                            </th>
                                        ))}
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {loading ? (
                                        <tr><td colSpan="6" className="px-6 py-8 text-center text-gray-500">Loading menu items...</td></tr>
                                    ) : filteredItems.length === 0 ? (
                                        <tr><td colSpan="6" className="px-6 py-8 text-center text-gray-500">No menu items found</td></tr>
                                    ) : (
                                        pagedFilteredItems.map((item) => (
                                            <tr key={item.item_id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center gap-3">
                                                        {item.item_img && (
                                                            <img src={item.item_img} alt={item.name} className="w-12 h-12 rounded-lg object-cover" />
                                                        )}
                                                        <p className="font-medium text-gray-800">{item.name}</p>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className="px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-700">
                                                        {item.category}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className="px-3 py-1 rounded-full text-sm font-medium bg-orange-50 text-orange-700 border border-orange-100">
                                                        {item.classification}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-gray-800">
                                                    ₱{parseFloat(item.item_price).toFixed(2)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${item.status === 'available'
                                                        ? 'bg-green-100 text-green-800'
                                                        : 'bg-red-100 text-red-800'
                                                        }`}>
                                                        {item.status}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center gap-2 justify-end">
                                                        <button
                                                            onClick={() => handleEditClick(item)}
                                                            className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                                                            title="Edit item"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"><path fill="currentColor" d="m14.06 9l.94.94L5.92 19H5v-.92zm3.6-6c-.25 0-.51.1-.7.29l-1.83 1.83l3.75 3.75l1.83-1.83c.39-.39.39-1.04 0-1.41l-2.34-2.34c-.2-.2-.45-.29-.71-.29m-3.6 3.19L3 17.25V21h3.75L17.81 9.94z" /></svg>
                                                        </button>
                                                        <button
                                                            onClick={() => handleArchiveItem(item)}
                                                            className="p-1 text-red-600 hover:bg-red-50 rounded"
                                                            title="Archive item"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"><path fill="currentColor" d="M7 21q-.825 0-1.412-.587T5 19V6H4V4h5V3h6v1h5v2h-1v13q0 .825-.587 1.413T17 21zM17 6H7v13h10zM9 17h2V8H9zm4 0h2V8h-2zM7 6v13z" /></svg>
                                                        </button>
                                                        <div className="relative">
                                                            <button
                                                                ref={el => (menuItemDropdownRefs.current[item.item_id] = el)}
                                                                onClick={() => {
                                                                    const rect = menuItemDropdownRefs.current[item.item_id]?.getBoundingClientRect()
                                                                    if (rect) {
                                                                        const menuWidth = 192
                                                                        const clampedLeft = Math.min(
                                                                            rect.right + window.scrollX - menuWidth,
                                                                            window.innerWidth + window.scrollX - menuWidth - 8
                                                                        )
                                                                        setMenuItemDropdownPosition({
                                                                            top: rect.bottom + window.scrollY + 4,
                                                                            left: Math.max(8, clampedLeft)
                                                                        })
                                                                    }
                                                                    setOpenMenuItemId(prev => prev === item.item_id ? null : item.item_id)
                                                                }}
                                                                className="p-1 text-gray-500 hover:bg-gray-100 rounded"
                                                                title="More options"
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                                                                    <path fill="currentColor" d="M12 10c-1.1 0-2 .9-2 2s.9 2 2 2s2-.9 2-2s-.9-2-2-2m0-6c-1.1 0-2 .9-2 2s.9 2 2 2s2-.9 2-2s-.9-2-2-2m0 12c-1.1 0-2 .9-2 2s.9 2 2 2s2-.9 2-2s-.9-2-2-2" />
                                                                </svg>
                                                            </button>
                                                            {openMenuItemId === item.item_id && (
                                                                <div
                                                                    style={{
                                                                        position: 'fixed',
                                                                        top: menuItemDropdownPosition.top,
                                                                        left: menuItemDropdownPosition.left,
                                                                        zIndex: 9999
                                                                    }}
                                                                    className="w-48 bg-white border border-gray-200 rounded-lg shadow-lg"
                                                                >
                                                                    <button
                                                                        onClick={() => { openAssignVariantsModal(item); setOpenMenuItemId(null) }}
                                                                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-t-lg"
                                                                    >
                                                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path fill="currentColor" d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6z" /></svg>
                                                                        Assign Variants
                                                                    </button>
                                                                    <button
                                                                        onClick={() => { openAssignModifiersModal(item); setOpenMenuItemId(null) }}
                                                                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                                                                    >
                                                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path fill="currentColor" d="M11 9H9V2H7v7H5V2H3v7c0 2.12 1.66 3.84 3.75 3.97V22h2.5v-9.03C11.34 12.84 13 11.12 13 9V2h-2zm5-3v8h2.5v8H21V2c-2.76 0-5 2.24-5 4z" /></svg>
                                                                        Assign Modifiers
                                                                    </button>
                                                                    <button
                                                                        onClick={() => { openVariantRulesModal(item); setOpenMenuItemId(null) }}
                                                                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-b-lg"
                                                                    >
                                                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path fill="currentColor" d="M14 17H5v-2h9zm5-4H5v-2h14zm0-4H5V7h14z" /></svg>
                                                                        Manage Variant Rules
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <Pagination
                            page={menuItemsPage}
                            totalItems={filteredItems.length}
                            pageSize={ITEMS_PAGE_SIZE}
                            onPageChange={setMenuItemsPage}
                        />
                    </div>
                )}

                {/* VARIANTS TAB */}
                {activeTab === "variants" && (
                    <div className="bg-white rounded-lg shadow-sm">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex flex-col gap-4">
                                <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                                    <div>
                                        <div className="flex items-center gap-3">
                                            <h2 className="text-xl font-bold text-gray-800">Variants</h2>
                                            <button
                                                onClick={() => {
                                                    setVariantImportFile(null)
                                                    setVariantImportPreview([])
                                                    setVariantImportErrors([])
                                                    setShowVariantImportModal(true)
                                                }}
                                                disabled={variantImporting}
                                                className="px-3 py-1.5 bg-gray-700 text-white rounded-lg text-xs font-medium hover:bg-gray-800 flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"><path fill="currentColor" d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zm-1 7V3.5L18.5 9zM8 13h3v-3h2v3h3l-4 4z" /></svg>
                                                Import CSV
                                            </button>
                                            <button
                                                onClick={exportVariantsCSV}
                                                disabled={filteredVariants.length === 0}
                                                className="px-3 py-1.5 bg-green-700 text-white rounded-lg text-xs font-medium hover:bg-green-800 flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"><path fill="currentColor" d="M5 20h14v-2H5zm7-18L5.33 9h4.84v6h3.66V9h4.84z" /></svg>
                                                Export CSV
                                            </button>
                                        </div>
                                        <p className="text-sm text-gray-500 mt-1">Manage size, temperature, and other variant options</p>
                                    </div>

                                    <div className="flex flex-wrap items-center gap-3">
                                        <div className="relative">
                                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24">
                                                    <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10a7 7 0 1 0 14 0a7 7 0 1 0-14 0m18 11l-6-6" />
                                                </svg>
                                            </span>
                                            <input
                                                type="text"
                                                placeholder="Search variants..."
                                                value={variantSearchQuery}
                                                onChange={(e) => setVariantSearchQuery(e.target.value)}
                                                className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 w-52"
                                            />
                                        </div>

                                        <button
                                            onClick={openArchivedViewerModal}
                                            className="p-2 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg"
                                            title="View Archived"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                                                <path fill="currentColor" d="M3 3h18a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1h-1v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V9H3a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1m3 6v10h12V9zm2 2h8v2H8zm0 4h8v2H8zM5 5v2h14V5z" />
                                            </svg>
                                        </button>
                                        <button
                                            onClick={() => {
                                                setVariantFormData({
                                                    category: "",
                                                    variant_name: "",
                                                    price: "",
                                                    applicable_categories: []
                                                })
                                                setVariantCategorySearch("")
                                                setOpenVariantCategoryDropdown(false)
                                                setShowAddVariantModal(true)
                                            }}
                                            className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 flex items-center gap-2"
                                        >
                                            <span>+</span>
                                            <span>Add Variant</span>
                                        </button>
                                    </div>
                                </div>

                                <div className="flex gap-4 overflow-x-auto pb-1 scrollbar-hide">
                                    {["All", ...new Set(variants.map(v => v.category).filter(Boolean))].map(cat => (
                                        <button
                                            key={cat}
                                            onClick={() => setFilterVariantCategory(cat)}
                                            className={`px-4 py-2 rounded-lg text-sm font-medium flex-shrink-0 whitespace-nowrap ${filterVariantCategory === cat
                                                ? 'bg-indigo-600 text-white'
                                                : 'text-gray-600 hover:bg-gray-100'
                                                }`}
                                        >
                                            {cat}
                                            {cat !== 'All' && (
                                                <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${filterVariantCategory === cat
                                                    ? 'bg-white/30 text-white'
                                                    : 'bg-gray-100 text-gray-500'
                                                    }`}>
                                                    {variants.filter(v => v.category === cat).length}
                                                </span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Variant Name</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Applicable Categories</th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>                                 </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {loadingVariants ? (
                                        <tr><td colSpan="4" className="px-6 py-8 text-center text-gray-500">Loading variants...</td></tr>
                                    ) : filteredVariants.length === 0 ? (
                                        <tr><td colSpan="4" className="px-6 py-8 text-center text-gray-500">No variants found</td></tr>
                                    ) : (
                                        pagedVariants.map((variant) => (
                                            <tr key={variant.variant_id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-800">
                                                    {variant.variant_name}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-gray-800">
                                                    {variant.category}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-gray-800">
                                                    ₱{parseFloat(variant.price || 0).toFixed(2)}
                                                </td>
                                                <td className="px-6 py-4">
                                                    {(variant.applicable_categories || []).length === 0 ? (
                                                        <span className="text-xs text-gray-400 italic">None</span>
                                                    ) : (
                                                        <div className="flex flex-wrap gap-1">
                                                            {variant.applicable_categories.map(cat => (
                                                                <span key={cat} className="px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-100">
                                                                    {cat}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center gap-2 justify-end">
                                                        <button
                                                            onClick={() => {
                                                                handleEditVariantClick(variant)
                                                                setShowEditVariantModal(true)
                                                            }}
                                                            className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                                                            title="Edit variant"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"><path fill="currentColor" d="m14.06 9l.94.94L5.92 19H5v-.92zm3.6-6c-.25 0-.51.1-.7.29l-1.83 1.83l3.75 3.75l1.83-1.83c.39-.39.39-1.04 0-1.41l-2.34-2.34c-.2-.2-.45-.29-.71-.29m-3.6 3.19L3 17.25V21h3.75L17.81 9.94z" /></svg>
                                                        </button>

                                                        <button
                                                            onClick={() => handleArchiveVariant(variant)}
                                                            className="p-1 text-red-600 hover:bg-red-50 rounded"
                                                            title="Archive variant"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"><path fill="currentColor" d="M7 21q-.825 0-1.412-.587T5 19V6H4V4h5V3h6v1h5v2h-1v13q0 .825-.587 1.413T17 21zM17 6H7v13h10zM9 17h2V8H9zm4 0h2V8h-2z" /></svg>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                            <Pagination
                                page={variantsPage}
                                totalItems={filteredVariants.length}
                                pageSize={ITEMS_PAGE_SIZE}
                                onPageChange={setVariantsPage}
                            />
                        </div>
                    </div>
                )}

                {/* MODIFIERS TAB */}
                {activeTab === "modifiers" && (
                    <div className="bg-white rounded-lg shadow-sm">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex flex-col gap-4">
                                <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                                    <div>
                                        <div className="flex items-center gap-3">
                                            <h2 className="text-xl font-bold text-gray-800">Modifiers</h2>
                                            <button
                                                onClick={() => {
                                                    setModifierImportFile(null)
                                                    setModifierImportPreview([])
                                                    setModifierImportErrors([])
                                                    setShowModifierImportModal(true)
                                                }}
                                                disabled={modifierImporting}
                                                className="px-3 py-1.5 bg-gray-700 text-white rounded-lg text-xs font-medium hover:bg-gray-800 flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"><path fill="currentColor" d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zm-1 7V3.5L18.5 9zM8 13h3v-3h2v3h3l-4 4z" /></svg>
                                                Import CSV
                                            </button>
                                            <button
                                                onClick={exportModifiersCSV}
                                                disabled={filteredModifiers.length === 0}
                                                className="px-3 py-1.5 bg-green-700 text-white rounded-lg text-xs font-medium hover:bg-green-800 flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"><path fill="currentColor" d="M5 20h14v-2H5zm7-18L5.33 9h4.84v6h3.66V9h4.84z" /></svg>
                                                Export CSV
                                            </button>
                                        </div>
                                        <p className="text-sm text-gray-500 mt-1">Manage add-ons, toppings, and other optional modifiers</p>
                                    </div>

                                    <div className="flex flex-wrap items-center gap-3">
                                        <div className="relative">
                                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24">
                                                    <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10a7 7 0 1 0 14 0a7 7 0 1 0-14 0m18 11l-6-6" />
                                                </svg>
                                            </span>
                                            <input
                                                type="text"
                                                placeholder="Search modifiers..."
                                                value={modifierSearchQuery}
                                                onChange={(e) => setModifierSearchQuery(e.target.value)}
                                                className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 w-52"
                                            />
                                        </div>

                                        <button
                                            onClick={openArchivedViewerModal}
                                            className="p-2 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg"
                                            title="View Archived"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                                                <path fill="currentColor" d="M3 3h18a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1h-1v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V9H3a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1m3 6v10h12V9zm2 2h8v2H8zm0 4h8v2H8zM5 5v2h14V5z" />
                                            </svg>
                                        </button>
                                        <button
                                            onClick={() => {
                                                setModifierFormData({
                                                    modifier_name: "",
                                                    category: "",
                                                    price: "",
                                                    applicable_categories: [],
                                                    ingredient_id: ""
                                                })
                                                setShowAddModifierModal(true)
                                                setModifierCategorySearch("")
                                                setOpenModifierCategoryDropdown(false)
                                                setModifierIngredientSearch("")
                                                setOpenModifierIngredientDropdown(false)
                                            }}
                                            className="px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 flex items-center gap-2"
                                        >
                                            <span>+</span>
                                            <span>Add Modifier</span>
                                        </button>
                                    </div>
                                </div>

                                <div className="flex gap-4 overflow-x-auto pb-1 scrollbar-hide">
                                    {['All', ...new Set(modifiers.map(m => m.category).filter(Boolean))].map(cat => (
                                        <button
                                            key={cat}
                                            onClick={() => setFilterModifierCategory(cat)}
                                            className={`px-4 py-2 rounded-lg text-sm font-medium flex-shrink-0 whitespace-nowrap ${filterModifierCategory === cat
                                                ? 'bg-purple-600 text-white'
                                                : 'text-gray-600 hover:bg-gray-100'
                                                }`}
                                        >
                                            {cat}
                                            {cat !== 'All' && (
                                                <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${filterModifierCategory === cat
                                                    ? 'bg-white/30 text-white'
                                                    : 'bg-gray-100 text-gray-500'
                                                    }`}>
                                                    {modifiers.filter(m => m.category === cat).length}
                                                </span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Modifier Name</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Applicable Categories</th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {loadingModifiers ? (
                                        <tr><td colSpan="4" className="px-6 py-8 text-center text-gray-500">Loading modifiers...</td></tr>
                                    ) : filteredModifiers.length === 0 ? (
                                        <tr><td colSpan="4" className="px-6 py-8 text-center text-gray-500">{modifierSearchQuery || filterModifierCategory !== "All" ? "No modifiers match your search" : "No modifiers found. Add your first modifier!"}</td></tr>
                                    ) : (
                                        pagedModifiers.map((modifier) => (
                                            <tr key={modifier.modifier_id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap"><p className="font-medium text-gray-800">{modifier.modifier_name}</p></td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getCategoryBadgeStyle(modifier.category)}`}>{modifier.category}</span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-gray-800">
                                                    {parseFloat(modifier.price) === 0 ? <span className="text-gray-400 text-sm">No charge</span> : `₱${parseFloat(modifier.price).toFixed(2)}`}
                                                </td>
                                                <td className="px-6 py-4">
                                                    {(modifier.applicable_categories || []).length === 0 ? (
                                                        <span className="text-xs text-gray-400 italic">None</span>
                                                    ) : (
                                                        <div className="flex flex-wrap gap-1">
                                                            {modifier.applicable_categories.map(cat => (
                                                                <span key={cat} className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100">
                                                                    {cat}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center gap-2 justify-end">
                                                        <button onClick={() => handleEditModifierClick(modifier)} className="p-1 text-blue-600 hover:bg-blue-50 rounded" title="Edit Modifier">
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"><path fill="currentColor" d="m14.06 9l.94.94L5.92 19H5v-.92zm3.6-6c-.25 0-.51.1-.7.29l-1.83 1.83l3.75 3.75l1.83-1.83c.39-.39.39-1.04 0-1.41l-2.34-2.34c-.2-.2-.45-.29-.71-.29m-3.6 3.19L3 17.25V21h3.75L17.81 9.94z" /></svg>
                                                        </button>
                                                        <button onClick={() => handleArchiveModifier(modifier)} className="p-1 text-red-600 hover:bg-red-50 rounded" title="Delete Modifier">
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"><path fill="currentColor" d="M7 21q-.825 0-1.412-.587T5 19V6H4V4h5V3h6v1h5v2h-1v13q0 .825-.587 1.413T17 21zM17 6H7v13h10zM9 17h2V8H9zm4 0h2V8h-2zM7 6v13z" /></svg>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                            <Pagination
                                page={modifiersPage}
                                totalItems={filteredModifiers.length}
                                pageSize={ITEMS_PAGE_SIZE}
                                onPageChange={setModifiersPage}
                            />
                        </div>
                    </div>
                )}

            </div>

            {/* Assign Variants Modal */}
            {showAssignVariantsModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black bg-opacity-50" onClick={closeAssignVariantsModal}></div>

                    <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col">
                        <div className="border-b border-gray-200 px-6 py-4 flex-shrink-0">
                            <h2 className="text-xl font-bold text-gray-800">Assign Variants</h2>
                            <p className="text-sm text-gray-500 mt-0.5">
                                Select which variants apply to <span className="font-medium text-gray-700">{selectedItemForVariants?.name}</span>
                            </p>
                        </div>

                        {/* Category filter bar */}
                        <div className="px-6 py-3 border-b border-gray-100 flex-shrink-0 flex flex-wrap gap-2">
                            {['All', ...new Set(variants.map(v => v.category).filter(Boolean))].map(cat => (
                                <button
                                    key={cat}
                                    onClick={() => setAssignVariantCategoryFilter(cat)}
                                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${assignVariantCategoryFilter === cat
                                        ? 'bg-indigo-600 text-white'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                        }`}
                                >
                                    {cat}
                                </button>
                            ))}
                        </div>

                        {/* Search bar */}
                        <div className="px-6 py-3 border-b border-gray-100 flex-shrink-0">
                            <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24">
                                        <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10a7 7 0 1 0 14 0a7 7 0 1 0-14 0m18 11l-6-6" />
                                    </svg>
                                </span>
                                <input
                                    type="text"
                                    placeholder="Search variants..."
                                    value={assignVariantSearch}
                                    onChange={(e) => setAssignVariantSearch(e.target.value)}
                                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                                />
                            </div>
                        </div>

                        <div className="overflow-y-auto flex-1 px-6 py-4">
                            {loadingVariantAssignments ? (
                                <div className="flex items-center justify-center py-12">
                                    <div className="text-gray-400 text-sm">Loading variants...</div>
                                </div>
                            ) : variants.length === 0 ? (
                                <div className="text-center py-10">
                                    <p className="text-gray-500 text-sm">No variants exist yet.</p>
                                    <p className="text-gray-400 text-xs mt-1">Add variants from the Variants tab first.</p>
                                </div>
                            ) : (() => {
                                const itemCategory = selectedItemForVariants?.category || ''

                                const relevantVariants = variants.filter(v => {
                                    if (v.is_archived) return false
                                    const cats = v.applicable_categories || []
                                    const isApplicable = cats.length > 0 && cats.includes(itemCategory)
                                    const matchesFilter = assignVariantCategoryFilter === 'All' || v.category === assignVariantCategoryFilter
                                    const matchesSearch = assignVariantSearch === '' ||
                                        String(v.variant_name || '').toLowerCase().includes(assignVariantSearch.toLowerCase()) ||
                                        String(v.category || '').toLowerCase().includes(assignVariantSearch.toLowerCase())
                                    return isApplicable && matchesFilter && matchesSearch
                                })

                                if (relevantVariants.length === 0) {
                                    return (
                                        <div className="text-center py-10">
                                            <p className="text-gray-500 text-sm">
                                                {assignVariantSearch ? 'No variants match your search.' : 'No variants available for this item\'s category.'}
                                            </p>
                                            {!assignVariantSearch && (
                                                <p className="text-gray-400 text-xs mt-1">Update variant applicable categories in the Variants tab.</p>
                                            )}
                                        </div>
                                    )
                                }

                                const grouped = Object.entries(
                                    relevantVariants.reduce((acc, variant) => {
                                        if (!acc[variant.category]) acc[variant.category] = []
                                        acc[variant.category].push(variant)
                                        return acc
                                    }, {})
                                ).sort(([a], [b]) => a.localeCompare(b))

                                return (
                                    <div className="space-y-5">
                                        {grouped.map(([category, categoryVariants]) => (
                                            <div key={category}>
                                                <div className="flex items-center gap-2 mb-2">
                                                    <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-700">
                                                        {category}
                                                    </span>
                                                    <span className="text-xs text-gray-400">
                                                        {categoryVariants.filter(v => assignedVariantIds.has(v.variant_id)).length} / {categoryVariants.length} selected
                                                    </span>
                                                </div>
                                                <div className="space-y-1.5 pl-1">
                                                    {categoryVariants
                                                        .sort((a, b) => String(a.variant_name || '').localeCompare(String(b.variant_name || '')))
                                                        .map((variant) => {
                                                            const isChecked = assignedVariantIds.has(variant.variant_id)
                                                            return (
                                                                <label
                                                                    key={variant.variant_id}
                                                                    className={`flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors ${isChecked
                                                                        ? 'bg-indigo-50 border-indigo-300'
                                                                        : 'bg-white border-gray-200 hover:bg-gray-50'
                                                                        }`}
                                                                >
                                                                    <div className="flex items-center gap-3">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={isChecked}
                                                                            onChange={() => toggleVariantAssignment(variant.variant_id)}
                                                                            className="w-4 h-4 accent-indigo-600 cursor-pointer"
                                                                        />
                                                                        <div>
                                                                            <span className={`text-sm font-medium ${isChecked ? 'text-indigo-800' : 'text-gray-700'}`}>
                                                                                {variant.variant_name}
                                                                            </span>
                                                                            {(variant.applicable_categories || []).length > 0 && (
                                                                                <p className="text-xs text-gray-400 mt-0.5">
                                                                                    {variant.applicable_categories.join(', ')}
                                                                                </p>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                    <span className={`text-sm flex-shrink-0 ${isChecked ? 'text-indigo-600' : 'text-gray-400'}`}>
                                                                        {parseFloat(variant.price) === 0 ? 'No charge' : `+₱${parseFloat(variant.price).toFixed(2)}`}
                                                                    </span>
                                                                </label>
                                                            )
                                                        })}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )
                            })()}
                        </div>

                        <div className="border-t border-gray-200 px-6 py-4 flex-shrink-0 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <span className="text-sm text-gray-500">
                                    {assignedVariantIds.size} variant{assignedVariantIds.size !== 1 ? 's' : ''} selected
                                </span>
                                <button
                                    type="button"
                                    onClick={handleSelectAllVariantsInCategory}
                                    className="text-sm font-medium text-indigo-600 hover:underline"
                                >
                                    Select All
                                </button>
                                <button
                                    type="button"
                                    onClick={handleClearAllVariantsInCategory}
                                    className="text-sm font-medium text-gray-500 hover:underline"
                                >
                                    Clear All
                                </button>
                            </div>
                            <div className="flex gap-3">
                                <button onClick={closeAssignVariantsModal} className="px-5 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300">
                                    Cancel
                                </button>
                                <button
                                    onClick={saveVariantAssignments}
                                    disabled={savingVariantAssignments || !hasAssignVariantChanges}
                                    className={`px-5 py-2 rounded-lg font-medium ${savingVariantAssignments || !hasAssignVariantChanges
                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                        : 'bg-indigo-600 text-white hover:bg-indigo-700'
                                        }`}
                                >
                                    {savingVariantAssignments ? 'Saving...' : 'Save'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Assign Modifiers Modal */}
            {showAssignModifiersModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black bg-opacity-50" onClick={closeAssignModifiersModal}></div>
                    <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col">
                        <div className="border-b border-gray-200 px-6 py-4 flex-shrink-0">
                            <h2 className="text-xl font-bold text-gray-800">Assign Modifiers</h2>
                            <p className="text-sm text-gray-500 mt-0.5">
                                Select which modifiers apply to <span className="font-medium text-gray-700">{selectedItemForModifiers?.name}</span>
                            </p>
                        </div>

                        {/* Category filter bar */}
                        <div className="px-6 py-3 border-b border-gray-100 flex-shrink-0 flex flex-wrap gap-2">
                            {['All', ...new Set(modifiers.map(m => m.category).filter(Boolean))].map(cat => (
                                <button
                                    key={cat}
                                    onClick={() => setAssignModifierCategoryFilter(cat)}
                                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${assignModifierCategoryFilter === cat
                                        ? 'bg-purple-600 text-white'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                        }`}
                                >
                                    {cat}
                                </button>
                            ))}
                        </div>

                        {/* Search bar */}
                        <div className="px-6 py-3 border-b border-gray-100 flex-shrink-0">
                            <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24">
                                        <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10a7 7 0 1 0 14 0a7 7 0 1 0-14 0m18 11l-6-6" />
                                    </svg>
                                </span>
                                <input
                                    type="text"
                                    placeholder="Search modifiers..."
                                    value={assignModifierSearch}
                                    onChange={(e) => setAssignModifierSearch(e.target.value)}
                                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
                                />
                            </div>
                        </div>

                        <div className="overflow-y-auto flex-1 px-6 py-4">
                            {loadingAssignments ? (
                                <div className="flex items-center justify-center py-12">
                                    <div className="text-gray-400 text-sm">Loading modifiers...</div>
                                </div>
                            ) : modifiers.length === 0 ? (
                                <div className="text-center py-10">
                                    <p className="text-gray-500 text-sm">No modifiers exist yet.</p>
                                    <p className="text-gray-400 text-xs mt-1">Add modifiers from the Modifiers tab first.</p>
                                </div>
                            ) : (() => {
                                const itemCategory = selectedItemForModifiers?.category || ''

                                const relevantModifiers = modifiers.filter(m => {
                                    if (m.is_archived) return false
                                    const cats = m.applicable_categories || []
                                    const isApplicable = cats.length > 0 && cats.includes(itemCategory)
                                    const matchesFilter = assignModifierCategoryFilter === 'All' || m.category === assignModifierCategoryFilter
                                    const matchesSearch = assignModifierSearch === '' ||
                                        String(m.modifier_name || '').toLowerCase().includes(assignModifierSearch.toLowerCase()) ||
                                        String(m.category || '').toLowerCase().includes(assignModifierSearch.toLowerCase())
                                    return isApplicable && matchesFilter && matchesSearch
                                })

                                if (relevantModifiers.length === 0) {
                                    return (
                                        <div className="text-center py-10">
                                            <p className="text-gray-500 text-sm">
                                                {assignModifierSearch ? 'No modifiers match your search.' : 'No modifiers available for this item\'s category.'}
                                            </p>
                                            {!assignModifierSearch && (
                                                <p className="text-gray-400 text-xs mt-1">Update modifier applicable categories in the Modifiers tab.</p>
                                            )}
                                        </div>
                                    )
                                }

                                const grouped = Object.entries(
                                    relevantModifiers.reduce((acc, mod) => {
                                        if (!acc[mod.category]) acc[mod.category] = []
                                        acc[mod.category].push(mod)
                                        return acc
                                    }, {})
                                ).sort(([a], [b]) => a.localeCompare(b))

                                return (
                                    <div className="space-y-5">
                                        {grouped.map(([category, mods]) => (
                                            <div key={category}>
                                                <div className="flex items-center gap-2 mb-2">
                                                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${getCategoryBadgeStyle(category)}`}>
                                                        {category}
                                                    </span>
                                                    <span className="text-xs text-gray-400">
                                                        {mods.filter(m => assignedModifierIds.has(m.modifier_id)).length} / {mods.length} selected
                                                    </span>
                                                </div>
                                                <div className="space-y-1.5 pl-1">
                                                    {mods.map(modifier => {
                                                        const isChecked = assignedModifierIds.has(modifier.modifier_id)
                                                        return (
                                                            <label
                                                                key={modifier.modifier_id}
                                                                className={`flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors ${isChecked
                                                                    ? 'bg-orange-50 border-orange-300'
                                                                    : 'bg-white border-gray-200 hover:bg-gray-50'
                                                                    }`}
                                                            >
                                                                <div className="flex items-center gap-3">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={isChecked}
                                                                        onChange={() => toggleModifierAssignment(modifier.modifier_id)}
                                                                        className="w-4 h-4 accent-orange-500 cursor-pointer"
                                                                    />
                                                                    <div>
                                                                        <span className={`text-sm font-medium ${isChecked ? 'text-orange-800' : 'text-gray-700'}`}>
                                                                            {modifier.modifier_name}
                                                                        </span>
                                                                        {(modifier.applicable_categories || []).length > 0 && (
                                                                            <p className="text-xs text-gray-400 mt-0.5">
                                                                                {modifier.applicable_categories.join(', ')}
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <span className={`text-sm flex-shrink-0 ${isChecked ? 'text-orange-600' : 'text-gray-400'}`}>
                                                                    {parseFloat(modifier.price) === 0 ? 'Free' : `+₱${parseFloat(modifier.price).toFixed(2)}`}
                                                                </span>
                                                            </label>
                                                        )
                                                    })}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )
                            })()}
                        </div>

                        <div className="border-t border-gray-200 px-6 py-4 flex-shrink-0 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <span className="text-sm text-gray-500">
                                    {assignedModifierIds.size} modifier{assignedModifierIds.size !== 1 ? 's' : ''} selected
                                </span>
                                <button
                                    type="button"
                                    onClick={handleSelectAllModifiersInCategory}
                                    className="text-sm font-medium text-[#FF6F3C] hover:underline"
                                >
                                    Select All
                                </button>
                                <button
                                    type="button"
                                    onClick={handleClearAllModifiersInCategory}
                                    className="text-sm font-medium text-gray-500 hover:underline"
                                >
                                    Clear All
                                </button>
                            </div>
                            <div className="flex gap-3">
                                <button onClick={closeAssignModifiersModal} className="px-5 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300">
                                    Cancel
                                </button>
                                <button
                                    onClick={saveModifierAssignments}
                                    disabled={savingAssignments || !hasModifierAssignmentChanges}
                                    className={`px-5 py-2 rounded-lg font-medium ${savingAssignments || !hasModifierAssignmentChanges
                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                        : 'bg-[#FF6F3C] text-white hover:bg-[#ff4c0b]'
                                        }`}
                                >
                                    {savingAssignments ? 'Saving...' : 'Save'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Item Modal */}
            {showAddItemModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => confirmDiscardChanges(hasAddItemChanges, () => { setShowAddItemModal(false); resetItemForm() })}></div>
                    <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
                        <div className="border-b border-gray-200 px-4 sm:px-6 py-4 flex-shrink-0">
                            <h2 className="text-xl font-bold text-gray-800">Add New Menu Item</h2>
                            <p className="text-sm text-gray-500 mt-1">Create a new item and assign it to the branches where it's available.</p>
                        </div>
                        <div className="px-4 sm:px-6 py-6 space-y-4 overflow-y-auto flex-1">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Item Name <span className="text-red-500">*</span></label>
                                <input
                                    type="text"
                                    placeholder="e.g. Iced Caramel Macchiato"
                                    value={itemFormData.name}
                                    onChange={(e) => setItemFormData({ ...itemFormData, name: e.target.value })}
                                    maxLength={50}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                />
                                <p className="text-xs text-gray-500 mt-1">{itemFormData.name.length}/50 characters</p>
                            </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Base Price <span className="text-red-500">*</span></label>
                                    <input
                                        type="number"
                                        placeholder="e.g. 180.00"
                                        step="0.01"
                                        min="0"
                                        max="9999"
                                        value={itemFormData.item_price}
                                        onChange={(e) => {
                                            const raw = e.target.value.replace('.', '').replace('-', '')
                                            if (raw.length <= MAX_PRICE_LENGTH) {
                                                setItemFormData({ ...itemFormData, item_price: e.target.value })
                                            }
                                        }}
                                        onKeyDown={handlePriceKeyDown}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">{String(itemFormData.item_price).replace('.', '').replace('-', '').length} / 4 digits</p>
                                </div>
                                <div className="relative" ref={itemCategoryDropdownRef}>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Category <span className="text-red-500">*</span>
                                    </label>

                                    <input
                                        type="text"
                                        value={itemFormData.category}
                                        onFocus={() => {
                                            setItemCategorySearch(itemFormData.category || "")
                                            setOpenItemCategoryDropdown(true)
                                        }}
                                        onChange={(e) => {
                                            setItemFormData({ ...itemFormData, category: e.target.value })
                                            setItemCategorySearch(e.target.value)
                                            setOpenItemCategoryDropdown(true)
                                        }}
                                        maxLength={30}
                                        placeholder="Search or type a category..."
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                    />

                                    {openItemCategoryDropdown && (
                                        <div className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-gray-300 bg-white shadow-lg">
                                            {filteredItemCategoryOptions.length === 0 ? (
                                                <div className="px-3 py-2 text-sm text-gray-500">
                                                    No existing categories found. Continue typing to create a new one.
                                                </div>
                                            ) : (
                                                filteredItemCategoryOptions.map((category) => (
                                                    <button
                                                        key={category}
                                                        type="button"
                                                        onClick={() => {
                                                            setItemFormData({ ...itemFormData, category })
                                                            setItemCategorySearch(category)
                                                            setOpenItemCategoryDropdown(false)
                                                        }}
                                                        className="w-full border-b border-gray-100 px-3 py-2 text-left hover:bg-gray-50 last:border-b-0"
                                                    >
                                                        <div className="text-sm font-medium text-gray-800">
                                                            {category}
                                                        </div>
                                                    </button>
                                                ))
                                            )}
                                        </div>
                                    )}

                                    <p className="text-xs text-gray-500 mt-1">
                                        Select an existing category or type a new one.
                                    </p>
                                </div>
                                <div className="relative" ref={itemClassificationDropdownRef}>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Classification <span className="text-red-500">*</span>
                                    </label>

                                    <input
                                        type="text"
                                        value={itemFormData.classification}
                                        onFocus={() => {
                                            setItemClassificationSearch(itemFormData.classification || "")
                                            setOpenItemClassificationDropdown(true)
                                        }}
                                        onChange={(e) => {
                                            setItemFormData({ ...itemFormData, classification: e.target.value })
                                            setItemClassificationSearch(e.target.value)
                                            setOpenItemClassificationDropdown(true)
                                        }}
                                        maxLength={30}
                                        placeholder="Search or type a classification..."
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                    />

                                    {openItemClassificationDropdown && (
                                        <div className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-gray-300 bg-white shadow-lg">
                                            {filteredItemClassificationOptions.length === 0 ? (
                                                <div className="px-3 py-2 text-sm text-gray-500">
                                                    No existing classifications found. Continue typing to create a new one.
                                                </div>
                                            ) : (
                                                filteredItemClassificationOptions.map((classification) => (
                                                    <button
                                                        key={classification}
                                                        type="button"
                                                        onClick={() => {
                                                            setItemFormData({ ...itemFormData, classification })
                                                            setItemClassificationSearch(classification)
                                                            setOpenItemClassificationDropdown(false)
                                                        }}
                                                        className="w-full border-b border-gray-100 px-3 py-2 text-left hover:bg-gray-50 last:border-b-0"
                                                    >
                                                        <div className="text-sm font-medium text-gray-800">
                                                            {classification}
                                                        </div>
                                                    </button>
                                                ))
                                            )}
                                        </div>
                                    )}

                                    <p className="text-xs text-gray-500 mt-1">
                                        Select an existing classification or type a new one.
                                    </p>
                                </div>
                                <div className="col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Item Image</label>

                                    <div className="flex w-full sm:inline-flex sm:w-auto mb-3 rounded-lg bg-gray-100 p-1">
                                        <button
                                            type="button"
                                            onClick={() => setImageInputMode('upload')}
                                            className={`flex-1 sm:flex-none px-4 py-2 rounded-md text-sm font-medium transition-colors ${imageInputMode === 'upload'
                                                ? 'bg-[#FF6F3C] text-white shadow-sm'
                                                : 'bg-transparent text-gray-600 hover:text-gray-800'
                                                }`}
                                        >
                                            Upload Image
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() => setImageInputMode('url')}
                                            className={`flex-1 sm:flex-none px-4 py-2 rounded-md text-sm font-medium transition-colors ${imageInputMode === 'url'
                                                ? 'bg-[#FF6F3C] text-white shadow-sm'
                                                : 'bg-transparent text-gray-600 hover:text-gray-800'
                                                }`}
                                        >
                                            Use Image URL
                                        </button>
                                    </div>

                                    {imageInputMode === 'upload' ? (
                                        <div className="space-y-2">
                                            <input
                                                type="file"
                                                accept="image/png,image/jpeg,image/webp"
                                                onChange={(e) => setItemImageFile(e.target.files?.[0] || null)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                            />
                                            <p className="text-xs text-gray-500">
                                                Accepted: JPG, PNG, WEBP. Max size: 5MB.
                                            </p>
                                        </div>
                                    ) : (
                                        <input
                                            type="text"
                                            value={itemFormData.item_img}
                                            onChange={(e) => setItemFormData({ ...itemFormData, item_img: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                            placeholder="https://images.example.com/spanish-latte.jpg"
                                        />
                                    )}

                                    {(itemFormData.item_img || itemImageFile) && (
                                        <p className="text-xs text-gray-500 mt-2">
                                            {itemImageFile ? `Selected file: ${itemImageFile.name}` : 'Image URL provided'}
                                        </p>
                                    )}
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Description <span className="text-red-500">*</span></label>
                                <textarea
                                    value={itemFormData.description}
                                    placeholder="e.g. Freshly brewed espresso with milk and caramel drizzle, served over ice."
                                    onChange={(e) => setItemFormData({ ...itemFormData, description: e.target.value })} rows="3"
                                    maxLength={100}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"></textarea>
                                <p className="text-xs text-gray-500 mt-1">{itemFormData.description.length}/100 characters (min: 10)</p>
                            </div>

                            <div className="flex items-center justify-between mb-2">
                                <label className="text-sm font-medium text-gray-700">
                                    Available in Branches
                                </label>

                                <button
                                    type="button"
                                    onClick={handleToggleSelectAllBranches}
                                    className="text-sm font-medium text-[#FF6F3C] hover:underline"
                                >
                                    {areAllBranchesSelected ? 'Clear All' : 'Select All'}
                                </button>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-40 overflow-y-auto border rounded-lg p-3">
                                {branches.map(branch => {
                                    const isSelected = selectedBranchIds.has(branch.branch_id)

                                    return (
                                        <label
                                            key={branch.branch_id}
                                            className={`flex items-center gap-2 p-2 rounded cursor-pointer border ${isSelected
                                                ? 'bg-orange-50 border-orange-300'
                                                : 'border-gray-200 hover:bg-gray-50'
                                                }`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={isSelected}
                                                onChange={() => toggleBranchSelection(branch.branch_id)}
                                                className="accent-orange-500"
                                            />

                                            <span className="text-sm">
                                                {branch.name}
                                            </span>
                                        </label>
                                    )
                                })}
                            </div>
                            <p className="text-xs text-gray-500">
                                {selectedBranchIds.size} of {branches.length} selected
                            </p>
                        </div>
                        <div>
                        </div>
                        <div className="border-t border-gray-200 px-4 sm:px-6 py-4 flex flex-col-reverse sm:flex-row justify-end gap-3 flex-shrink-0">
                            <button onClick={() => confirmDiscardChanges(hasAddItemChanges, () => { setShowAddItemModal(false); resetItemForm() })} className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300">Cancel</button>
                            <button
                                onClick={handleAddItem}
                                disabled={!hasAddItemChanges}
                                className={`px-6 py-2 rounded-lg font-medium ${!hasAddItemChanges
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    : 'bg-[#FF6F3C] text-white hover:bg-[#7A4D32]'
                                    }`}
                            >
                                Add Item
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Item Modal */}
            {showEditItemModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => confirmDiscardChanges(hasEditItemChanges, () => { setShowEditItemModal(false); setEditingItem(null); resetItemForm() })}></div>
                    <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
                        <div className="border-b border-gray-200 px-6 py-4 flex-shrink-0">
                            <h2 className="text-xl font-bold text-gray-800">Edit Menu Item</h2>
                            <p className="text-sm text-gray-500 mt-1">Update this item's details, image, or branch availability.</p>
                        </div>
                        <div className="px-6 py-6 space-y-4 overflow-y-auto flex-1">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">                                <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Item Name <span className="text-red-500">*</span></label>
                                <input type="text" placeholder="e.g., Spanish Latte" value={itemFormData.name} onChange={(e) => setItemFormData({ ...itemFormData, name: e.target.value })} maxLength={50} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]" />
                                <p className="text-xs text-gray-500 mt-1">{itemFormData.name.length}/50 characters</p>
                            </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Base Price <span className="text-red-500">*</span></label>
                                    <input
                                        type="number"
                                        placeholder="e.g., 120.00"
                                        step="0.01" min="0"
                                        max="9999"
                                        value={itemFormData.item_price}
                                        onChange={(e) => {
                                            const raw = e.target.value.replace('.', '').replace('-', '')
                                            if (raw.length <= MAX_PRICE_LENGTH) {
                                                setItemFormData({ ...itemFormData, item_price: e.target.value })
                                            }
                                        }}
                                        onKeyDown={handlePriceKeyDown}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">{String(itemFormData.item_price).replace('.', '').replace('-', '').length} / 4 digits</p>
                                    <p className="text-xs text-gray-500 mt-1">Max: ₱9999</p>
                                </div>
                                <div className="relative" ref={itemCategoryDropdownRef}>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Category <span className="text-red-500">*</span>
                                    </label>

                                    <input
                                        type="text"
                                        value={itemFormData.category}
                                        onFocus={() => {
                                            setItemCategorySearch(itemFormData.category || "")
                                            setOpenItemCategoryDropdown(true)
                                        }}
                                        onChange={(e) => {
                                            setItemFormData({ ...itemFormData, category: e.target.value })
                                            setItemCategorySearch(e.target.value)
                                            setOpenItemCategoryDropdown(true)
                                        }}
                                        maxLength={30}
                                        placeholder="Search or type a category..."
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                    />

                                    {openItemCategoryDropdown && (
                                        <div className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-gray-300 bg-white shadow-lg">
                                            {filteredItemCategoryOptions.length === 0 ? (
                                                <div className="px-3 py-2 text-sm text-gray-500">
                                                    No existing categories found. Continue typing to create a new one.
                                                </div>
                                            ) : (
                                                filteredItemCategoryOptions.map((category) => (
                                                    <button
                                                        key={category}
                                                        type="button"
                                                        onClick={() => {
                                                            setItemFormData({ ...itemFormData, category })
                                                            setItemCategorySearch(category)
                                                            setOpenItemCategoryDropdown(false)
                                                        }}
                                                        className="w-full border-b border-gray-100 px-3 py-2 text-left hover:bg-gray-50 last:border-b-0"
                                                    >
                                                        <div className="text-sm font-medium text-gray-800">
                                                            {category}
                                                        </div>
                                                    </button>
                                                ))
                                            )}
                                        </div>
                                    )}

                                    <p className="text-xs text-gray-500 mt-1">
                                        Select an existing category or type a new one.
                                    </p>
                                </div>
                                <div className="relative" ref={itemClassificationDropdownRef}>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Classification <span className="text-red-500">*</span>
                                    </label>

                                    <input
                                        type="text"
                                        value={itemFormData.classification}
                                        onFocus={() => {
                                            setItemClassificationSearch(itemFormData.classification || "")
                                            setOpenItemClassificationDropdown(true)
                                        }}
                                        onChange={(e) => {
                                            setItemFormData({ ...itemFormData, classification: e.target.value })
                                            setItemClassificationSearch(e.target.value)
                                            setOpenItemClassificationDropdown(true)
                                        }}
                                        maxLength={30}
                                        placeholder="Search or type a classification..."
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                    />

                                    {openItemClassificationDropdown && (
                                        <div className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-gray-300 bg-white shadow-lg">
                                            {filteredItemClassificationOptions.length === 0 ? (
                                                <div className="px-3 py-2 text-sm text-gray-500">
                                                    No existing classifications found. Continue typing to create a new one.
                                                </div>
                                            ) : (
                                                filteredItemClassificationOptions.map((classification) => (
                                                    <button
                                                        key={classification}
                                                        type="button"
                                                        onClick={() => {
                                                            setItemFormData({ ...itemFormData, classification })
                                                            setItemClassificationSearch(classification)
                                                            setOpenItemClassificationDropdown(false)
                                                        }}
                                                        className="w-full border-b border-gray-100 px-3 py-2 text-left hover:bg-gray-50 last:border-b-0"
                                                    >
                                                        <div className="text-sm font-medium text-gray-800">
                                                            {classification}
                                                        </div>
                                                    </button>
                                                ))
                                            )}
                                        </div>
                                    )}

                                    <p className="text-xs text-gray-500 mt-1">
                                        Select an existing classification or type a new one.
                                    </p>
                                </div>
                                <div className="col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Item Image</label>
                                    <div className="flex w-full sm:inline-flex sm:w-auto mb-3 rounded-lg bg-gray-100 p-1">
                                        <button
                                            type="button"
                                            onClick={() => setImageInputMode('upload')}
                                            className={`flex-1 sm:flex-none px-4 py-2 rounded-md text-sm font-medium transition-colors ${imageInputMode === 'upload'
                                                ? 'bg-[#FF6F3C] text-white shadow-sm'
                                                : 'bg-transparent text-gray-600 hover:text-gray-800'
                                                }`}
                                        >
                                            Upload Image
                                        </button>
                                        
                                        <button
                                            type="button"
                                            onClick={() => setImageInputMode('url')}
                                            className={`flex-1 sm:flex-none px-4 py-2 rounded-md text-sm font-medium transition-colors ${imageInputMode === 'url'
                                                ? 'bg-[#FF6F3C] text-white shadow-sm'
                                                : 'bg-transparent text-gray-600 hover:text-gray-800'
                                                }`}
                                        >
                                            Use Image URL
                                        </button>
                                    </div>
                                    {imageInputMode === 'upload' ? (
                                        <div className="space-y-2">
                                            <input
                                                type="file"
                                                accept="image/png,image/jpeg,image/webp"
                                                onChange={(e) => setItemImageFile(e.target.files?.[0] || null)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                            />
                                            <p className="text-xs text-gray-500">Accepted: JPG, PNG, WEBP. Max size: 5MB.</p>
                                        </div>
                                    ) : (
                                        <input
                                            type="text"
                                            value={itemFormData.item_img}
                                            onChange={(e) => setItemFormData({ ...itemFormData, item_img: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                            placeholder="https://images.example.com/spanish-latte.jpg"
                                        />
                                    )}
                                    {(itemFormData.item_img || itemImageFile) && (
                                        <p className="text-xs text-gray-500 mt-2">
                                            {itemImageFile ? `Selected file: ${itemImageFile.name}` : 'Image URL provided'}
                                        </p>
                                    )}
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Description <span className="text-red-500">*</span></label>
                                <textarea value={itemFormData.description} placeholder="e.g., Rich espresso blended with creamy milk and sweet Spanish-style flavor." onChange={(e) => setItemFormData({ ...itemFormData, description: e.target.value })} rows="3" maxLength={500} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"></textarea>
                                <p className="text-xs text-gray-500 mt-1">{itemFormData.description.length}/500 characters (min: 10)</p>
                            </div>
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <label className="text-sm font-medium text-gray-700">
                                        Available in Branches
                                    </label>

                                    <button
                                        type="button"
                                        onClick={handleToggleSelectAllBranches}
                                        className="text-sm font-medium text-[#FF6F3C] hover:underline"
                                    >
                                        {areAllBranchesSelected ? 'Clear All' : 'Select All'}
                                    </button>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-40 overflow-y-auto border rounded-lg p-3">
                                    {branches.map(branch => {
                                        const isSelected = selectedBranchIds.has(branch.branch_id)

                                        return (
                                            <label
                                                key={branch.branch_id}
                                                className={`flex items-center gap-2 p-2 rounded cursor-pointer border ${isSelected
                                                    ? 'bg-orange-50 border-orange-300'
                                                    : 'border-gray-200 hover:bg-gray-50'
                                                    }`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => toggleBranchSelection(branch.branch_id)}
                                                    className="accent-orange-500"
                                                />

                                                <span className="text-sm">
                                                    {branch.name}
                                                </span>
                                            </label>
                                        )
                                    })}
                                </div>

                                <p className="text-xs text-gray-500 mt-2">
                                    {selectedBranchIds.size} of {branches.length} selected
                                </p>
                            </div>
                        </div>
                        <div className="border-t border-gray-200 px-4 sm:px-6 py-4 flex flex-col-reverse sm:flex-row justify-end gap-3 flex-shrink-0">
                            <button onClick={() => confirmDiscardChanges(hasEditItemChanges, () => { setShowEditItemModal(false); setEditingItem(null); resetItemForm() })} className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300">Cancel</button>
                            <button
                                onClick={handleUpdateItem}
                                disabled={!hasEditItemChanges}
                                className={`px-6 py-2 rounded-lg font-medium ${!hasEditItemChanges
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    : 'bg-[#FF6F3C] text-white hover:bg-[#7A4D32]'
                                    }`}
                            >
                                Update Item
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Variant Rule Modal */}
            {showVariantRulesModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-black bg-opacity-50"
                        onClick={closeVariantRulesModal}
                    ></div>

                    <div className="relative bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
                        <div className="border-b border-gray-200 px-4 sm:px-6 py-4 flex-shrink-0">
                            <h2 className="text-xl font-bold text-gray-800">Edit Menu Item</h2>
                            <p className="text-sm text-gray-500 mt-1">Update this item's details, image, or branch availability.</p>
                        </div>

                        <div className="px-4 sm:px-6 py-6 space-y-4 overflow-y-auto flex-1">
                            {itemVariants.length === 0 ? (
                                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-800">
                                    This item has no variants yet. Add variants first before creating variant ingredient rules.
                                </div>
                            ) : (
                                <>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="relative" ref={variantDropdownRef}>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Variant <span className="text-red-500">*</span>
                                            </label>

                                            <input
                                                type="text"
                                                value={
                                                    variantRuleFormData.variant_id
                                                        ? itemVariants.find(variant => String(variant.variant_id) === String(variantRuleFormData.variant_id))
                                                            ? `${itemVariants.find(variant => String(variant.variant_id) === String(variantRuleFormData.variant_id))?.category || ''}${itemVariants.find(variant => String(variant.variant_id) === String(variantRuleFormData.variant_id))?.category ? ' - ' : ''}${itemVariants.find(variant => String(variant.variant_id) === String(variantRuleFormData.variant_id))?.variant_name || ''}`
                                                            : variantRuleVariantSearch
                                                        : variantRuleVariantSearch
                                                }
                                                onFocus={() => setOpenVariantDropdown(true)}
                                                onChange={(e) => {
                                                    setVariantRuleFormData(prev => ({
                                                        ...prev,
                                                        variant_id: ''
                                                    }))
                                                    setVariantRuleVariantSearch(e.target.value)
                                                    setOpenVariantDropdown(true)
                                                }}
                                                placeholder="Search and select variant..."
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                            />

                                            {openVariantDropdown && (
                                                <div className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-gray-300 bg-white shadow-lg">
                                                    {filteredVariantOptions.length === 0 ? (
                                                        <div className="px-3 py-2 text-sm text-gray-500">
                                                            No variants found.
                                                        </div>
                                                    ) : (
                                                        filteredVariantOptions.map((variant) => (
                                                            <button
                                                                key={variant.variant_id}
                                                                type="button"
                                                                onClick={() => {
                                                                    setVariantRuleFormData(prev => ({
                                                                        ...prev,
                                                                        variant_id: variant.variant_id
                                                                    }))
                                                                    setVariantRuleVariantSearch(
                                                                        `${variant.category ? `${variant.category} - ` : ''}${variant.variant_name}`
                                                                    )
                                                                    setOpenVariantDropdown(false)
                                                                }}
                                                                className="w-full border-b border-gray-100 px-3 py-2 text-left hover:bg-gray-50 last:border-b-0"
                                                            >
                                                                <div className="text-sm font-medium text-gray-800">
                                                                    {variant.variant_name}
                                                                </div>
                                                                <div className="text-xs text-gray-500">
                                                                    {variant.category || 'Uncategorized'}
                                                                    {parseFloat(variant.price || 0) > 0 ? ` • +₱${parseFloat(variant.price).toFixed(2)}` : ''}
                                                                </div>
                                                            </button>
                                                        ))
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        <div className="relative" ref={ingredientDropdownRef}>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Ingredient <span className="text-red-500">*</span>
                                            </label>

                                            <input
                                                type="text"
                                                value={
                                                    variantRuleFormData.ingredient_id
                                                        ? ingredients.find(ingredient => String(ingredient.ingredient_id) === String(variantRuleFormData.ingredient_id))?.ingredient_name || ''
                                                        : variantRuleIngredientSearch
                                                }
                                                onFocus={() => setOpenIngredientDropdown(true)}
                                                onChange={(e) => {
                                                    setVariantRuleFormData(prev => ({
                                                        ...prev,
                                                        ingredient_id: '',
                                                        unit: ''
                                                    }))
                                                    setVariantRuleIngredientSearch(e.target.value)
                                                    setOpenIngredientDropdown(true)
                                                }}
                                                placeholder="Search and select ingredient..."
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                            />

                                            {openIngredientDropdown && (
                                                <div className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-gray-300 bg-white shadow-lg">
                                                    {filteredIngredientOptions.length === 0 ? (
                                                        <div className="px-3 py-2 text-sm text-gray-500">
                                                            No ingredients found.
                                                        </div>
                                                    ) : (
                                                        filteredIngredientOptions.map((ingredient) => (
                                                            <button
                                                                key={ingredient.ingredient_id}
                                                                type="button"
                                                                onClick={() => {
                                                                    setVariantRuleFormData(prev => ({
                                                                        ...prev,
                                                                        ingredient_id: ingredient.ingredient_id,
                                                                        unit: ingredient.unit || ''
                                                                    }))
                                                                    setVariantRuleIngredientSearch(ingredient.ingredient_name || ingredient.name || '')
                                                                    setOpenIngredientDropdown(false)
                                                                }}
                                                                className="w-full border-b border-gray-100 px-3 py-2 text-left hover:bg-gray-50 last:border-b-0"
                                                            >
                                                                <div className="text-sm font-medium text-gray-800">
                                                                    {ingredient.ingredient_name || ingredient.name}
                                                                </div>
                                                                <div className="text-xs text-gray-500">
                                                                    {ingredient.unit || '—'}{ingredient.category ? ` • ${ingredient.category}` : ''}
                                                                </div>
                                                            </button>
                                                        ))
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Quantity Delta <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                value={variantRuleFormData.quantity_delta}
                                                onChange={(e) =>
                                                    setVariantRuleFormData({
                                                        ...variantRuleFormData,
                                                        quantity_delta: e.target.value
                                                    })
                                                }
                                                onInput={(e) => {
                                                    if (e.target.value.replace('.', '').replace('-', '').length > 6)
                                                        e.target.value = e.target.value.slice(0, e.target.value.includes('.') ? 7 : 6)
                                                }}
                                                placeholder="e.g. 1, 40, 120"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                            />
                                            <p className="text-xs text-gray-500 mt-1">
                                                {String(variantRuleFormData.quantity_delta).replace('.', '').replace('-', '').length} / 6 characters
                                            </p>

                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Unit <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={variantRuleFormData.unit}
                                                readOnly
                                                placeholder="Auto-filled from selected ingredient"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
                                            />
                                            <p className="text-xs text-gray-500 mt-1">
                                                Unit is based on the selected ingredient and cannot be edited here.
                                            </p>
                                        </div>

                                        <div className="md:col-span-2">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Notes
                                            </label>
                                            <input
                                                type="text"
                                                value={variantRuleFormData.notes}
                                                onChange={(e) =>
                                                    setVariantRuleFormData({
                                                        ...variantRuleFormData,
                                                        notes: e.target.value
                                                    })
                                                }
                                                maxLength={30}
                                                placeholder="Optional note"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                            />
                                            <p className="text-xs text-gray-500 mt-1">
                                                {(variantRuleFormData.notes || '').length} / 30 characters
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex justify-end gap-3">
                                        {variantRuleFormData.id && (
                                            <button
                                                type="button"
                                                onClick={resetVariantRuleForm}
                                                className="px-5 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300"
                                            >
                                                Cancel Edit
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            onClick={handleSaveVariantRule}
                                            disabled={savingVariantRule || !hasVariantRuleChanges}
                                            className={`px-5 py-2 rounded-lg font-medium ${savingVariantRule || !hasVariantRuleChanges
                                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                : 'bg-[#FF6F3C] text-white hover:bg-[#ff4c0b]'
                                                }`}
                                        >
                                            {savingVariantRule
                                                ? 'Saving...'
                                                : variantRuleFormData.id
                                                    ? 'Update Rule'
                                                    : 'Add Rule'}
                                        </button>
                                    </div>
                                </>
                            )}

                            <div className="border border-gray-200 rounded-lg overflow-hidden">
                                <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 flex items-center justify-between gap-3">
                                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Saved Rules ({variantRules.length})
                                    </span>
                                    <div className="relative w-56">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24">
                                                <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10a7 7 0 1 0 14 0a7 7 0 1 0-14 0m18 11l-6-6" />
                                            </svg>
                                        </span>
                                        <input
                                            type="text"
                                            placeholder="Search rules..."
                                            value={variantRuleTableSearch}
                                            onChange={(e) => setVariantRuleTableSearch(e.target.value)}
                                            className="w-full pl-8 pr-3 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                        />
                                    </div>
                                </div>

                                {loadingVariantRules ? (
                                    <div className="px-4 py-8 text-center text-gray-500 text-sm">Loading variant rules...</div>
                                ) : variantRules.length === 0 ? (
                                    <div className="px-4 py-8 text-center text-gray-500 text-sm">No variant rules added yet.</div>
                                ) : (() => {
                                    const query = variantRuleTableSearch.toLowerCase()

                                    const grouped = variantRules.reduce((acc, rule) => {
                                        const key = rule.variant_id
                                        if (!acc[key]) acc[key] = []
                                        acc[key].push(rule)
                                        return acc
                                    }, {})

                                    const filteredGroupEntries = Object.entries(grouped).filter(([variantId, rules]) => {
                                        const variantLabel = getVariantLabelById(variantId).toLowerCase()
                                        if (variantLabel.includes(query)) return true
                                        return rules.some(r =>
                                            getIngredientLabelById(r.ingredient_id).toLowerCase().includes(query) ||
                                            String(r.notes || '').toLowerCase().includes(query)
                                        )
                                    })

                                    if (filteredGroupEntries.length === 0) {
                                        return <div className="px-4 py-8 text-center text-gray-500 text-sm">No rules match your search.</div>
                                    }

                                    return (
                                        <VariantRuleGroups
                                            groupEntries={filteredGroupEntries}
                                            getVariantLabelById={getVariantLabelById}
                                            getIngredientLabelById={getIngredientLabelById}
                                            handleEditVariantRule={handleEditVariantRule}
                                            handleDeleteVariantRule={handleDeleteVariantRule}
                                            query={query}
                                        />
                                    )
                                })()}
                            </div>
                        </div>

                        <div className="border-t border-gray-200 px-6 py-4 flex justify-end flex-shrink-0">
                            <button
                                onClick={closeVariantRulesModal}
                                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Variant Modal */}
            {showAddVariantModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-black bg-opacity-50"
                        onClick={() => confirmDiscardChanges(hasAddVariantChanges, () => {
                            setShowAddVariantModal(false)
                            setVariantFormData({ category: "", variant_name: "", price: "", applicable_categories: [] })
                            setVariantCategorySearch("")
                            setOpenVariantCategoryDropdown(false)
                        })}
                    ></div>

                    <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] flex flex-col">
                        <div className="border-b border-gray-200 px-6 py-4 flex-shrink-0">
                            <h2 className="text-xl font-bold text-gray-800">Add Variant</h2>
                            <p className="text-sm text-gray-500 mt-1">Create a new variant option, like a size or temperature, that items can offer.</p>
                        </div>

                        <div className="px-6 py-6 space-y-4 overflow-y-auto flex-1">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Variant Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={variantFormData.variant_name}
                                    onChange={(e) => setVariantFormData({ ...variantFormData, variant_name: e.target.value })}
                                    maxLength={30}
                                    placeholder="e.g. Grande, Iced, 50%"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    {variantFormData.variant_name.length}/30 characters
                                </p>
                            </div>

                            <div className="relative" ref={variantCategoryDropdownRef}>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Category <span className="text-red-500">*</span>
                                </label>

                                <input
                                    type="text"
                                    value={variantFormData.category}
                                    onFocus={() => {
                                        setVariantCategorySearch(variantFormData.category || "")
                                        setOpenVariantCategoryDropdown(true)
                                    }}
                                    onChange={(e) => {
                                        setVariantFormData({ ...variantFormData, category: e.target.value })
                                        setVariantCategorySearch(e.target.value)
                                        setOpenVariantCategoryDropdown(true)
                                    }}
                                    maxLength={30}
                                    placeholder="Search or type a category..."
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                                />

                                {openVariantCategoryDropdown && (
                                    <div className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-gray-300 bg-white shadow-lg">
                                        {filteredVariantCategoryOptions.length === 0 ? (
                                            <div className="px-3 py-2 text-sm text-gray-500">
                                                No existing categories found. Continue typing to create a new one.
                                            </div>
                                        ) : (
                                            filteredVariantCategoryOptions.map((category) => (
                                                <button
                                                    key={category}
                                                    type="button"
                                                    onClick={() => {
                                                        setVariantFormData({ ...variantFormData, category })
                                                        setVariantCategorySearch(category)
                                                        setOpenVariantCategoryDropdown(false)
                                                    }}
                                                    className="w-full border-b border-gray-100 px-3 py-2 text-left hover:bg-gray-50 last:border-b-0"
                                                >
                                                    <div className="text-sm font-medium text-gray-800">
                                                        {category}
                                                    </div>
                                                </button>
                                            ))
                                        )}
                                    </div>
                                )}

                                <p className="text-xs text-gray-500 mt-1">
                                    {variantFormData.category.length}/30 characters
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                    Select an existing category or type a new one.
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Price <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="9999"
                                    value={variantFormData.price}
                                    onChange={(e) => {
                                        const raw = e.target.value.replace('.', '').replace('-', '')
                                        if (raw.length <= MAX_PRICE_LENGTH) {
                                            setVariantFormData({ ...variantFormData, price: e.target.value })
                                        }
                                    }}
                                    onKeyDown={handlePriceKeyDown}
                                    placeholder="0.00"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                                />
                                <p className="text-xs text-gray-500 mt-1">{String(variantFormData.price).replace('.', '').replace('-', '').length} / 4 digits</p>
                                <p className="text-xs text-gray-500 mt-1">(can be 0 for no charge)</p>
                            </div>

                            <ApplicableCategoriesSelector
                                value={variantFormData.applicable_categories}
                                onChange={(cats) => setVariantFormData({ ...variantFormData, applicable_categories: cats })}
                            />
                        </div>

                        <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3 flex-shrink-0">                            <button
                                onClick={() => confirmDiscardChanges(hasAddVariantChanges, () => {
                                    setVariantFormData({ category: "", variant_name: "", price: "", applicable_categories: [] })
                                    setShowAddVariantModal(false)
                                    setVariantCategorySearch("")
                                    setOpenVariantCategoryDropdown(false)
                                })}
                                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddVariant}
                                disabled={savingVariant || !hasAddVariantChanges}
                                className={`px-6 py-2 rounded-lg font-medium ${savingVariant || !hasAddVariantChanges
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    : 'bg-indigo-600 text-white hover:bg-indigo-700'
                                    }`}
                            >
                                {savingVariant ? 'Saving...' : 'Add Variant'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Variant Modal */}
            {showEditVariantModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-black bg-opacity-50"
                        onClick={() => confirmDiscardChanges(hasEditVariantChanges, () => {
                            setShowEditVariantModal(false)
                            setEditingVariant(null)
                            setVariantFormData({ category: "", variant_name: "", price: "", applicable_categories: [] })
                            setInitialVariantFormData(null)
                        })}
                    ></div>

                    <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] flex flex-col">
                        <div className="border-b border-gray-200 px-6 py-4 flex-shrink-0">
                            <h2 className="text-xl font-bold text-gray-800">Edit Variant</h2>
                            <p className="text-sm text-gray-500 mt-1">Update this variant's name, category, price, or applicable item categories.</p>
                        </div>

                        <div className="px-6 py-6 space-y-4 overflow-y-auto flex-1">

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Variant Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={variantFormData.variant_name}
                                    onChange={(e) => setVariantFormData({ ...variantFormData, variant_name: e.target.value })}
                                    maxLength={30}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    {variantFormData.category.length}/30 characters
                                </p>
                            </div>

                            <div className="relative" ref={variantCategoryDropdownRef}>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Category <span className="text-red-500">*</span>
                                </label>

                                <input
                                    type="text"
                                    value={variantFormData.category}
                                    onFocus={() => {
                                        setVariantCategorySearch(variantFormData.category || "")
                                        setOpenVariantCategoryDropdown(true)
                                    }}
                                    onChange={(e) => {
                                        setVariantFormData({ ...variantFormData, category: e.target.value })
                                        setVariantCategorySearch(e.target.value)
                                        setOpenVariantCategoryDropdown(true)
                                    }}
                                    maxLength={30}
                                    placeholder="Search or type a category..."
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                                />

                                {openVariantCategoryDropdown && (
                                    <div className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-gray-300 bg-white shadow-lg">
                                        {filteredVariantCategoryOptions.length === 0 ? (
                                            <div className="px-3 py-2 text-sm text-gray-500">
                                                No existing categories found. Continue typing to create a new one.
                                            </div>
                                        ) : (
                                            filteredVariantCategoryOptions.map((category) => (
                                                <button
                                                    key={category}
                                                    type="button"
                                                    onClick={() => {
                                                        setVariantFormData({ ...variantFormData, category })
                                                        setVariantCategorySearch(category)
                                                        setOpenVariantCategoryDropdown(false)
                                                    }}
                                                    className="w-full border-b border-gray-100 px-3 py-2 text-left hover:bg-gray-50 last:border-b-0"
                                                >
                                                    <div className="text-sm font-medium text-gray-800">
                                                        {category}
                                                    </div>
                                                </button>
                                            ))
                                        )}
                                    </div>
                                )}

                                <p className="text-xs text-gray-500 mt-1">
                                    Select an existing category or type a new one.
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Price <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="9999"
                                    value={variantFormData.price}
                                    onChange={(e) => {
                                        const raw = e.target.value.replace('.', '').replace('-', '')
                                        if (raw.length <= MAX_PRICE_LENGTH) {
                                            setVariantFormData({ ...variantFormData, price: e.target.value })
                                        }
                                    }}
                                    onKeyDown={handlePriceKeyDown}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                                />
                                <p className="text-xs text-gray-500 mt-1">{String(variantFormData.price).replace('.', '').replace('-', '').length} / 4 digits</p>
                                <p className="text-xs text-gray-500 mt-1">(can be 0 for no charge)</p>
                            </div>

                            <ApplicableCategoriesSelector
                                value={variantFormData.applicable_categories}
                                onChange={(cats) => setVariantFormData({ ...variantFormData, applicable_categories: cats })}
                            />

                        </div>

                        <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3 flex-shrink-0">                            <button
                                onClick={() => confirmDiscardChanges(hasEditVariantChanges, () => {
                                    setVariantFormData({ category: "", variant_name: "", price: "", applicable_categories: [] })
                                    setShowEditVariantModal(false)
                                    setEditingVariant(null)
                                    setInitialVariantFormData(null)
                                })}
                                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleUpdateVariant}
                                disabled={savingVariant || !hasEditVariantChanges}
                                className={`px-6 py-2 rounded-lg font-medium ${savingVariant || !hasEditVariantChanges
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    : 'bg-indigo-600 text-white hover:bg-indigo-700'
                                    }`}
                            >
                                {savingVariant ? 'Saving...' : 'Update Variant'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Modifier Modal */}
            {showAddModifierModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black bg-opacity-50"
                        onClick={() => confirmDiscardChanges(hasAddModifierChanges, () => {
                            setShowAddModifierModal(false);
                            setModifierFormData({ modifier_name: "", category: "", price: "", applicable_categories: [], ingredient_id: "" })
                            setInitialModifierFormData(null)
                            setModifierIngredientSearch("")
                            setOpenModifierIngredientDropdown(false)
                        })}></div>
                    <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] flex flex-col">
                        <div className="border-b border-gray-200 px-6 py-4 flex-shrink-0">
                            <h2 className="text-xl font-bold text-gray-800">Add Modifier</h2>
                            <p className="text-sm text-gray-500 mt-1">Create a new add-on or topping option that items can offer.</p>
                        </div>
                        <div className="px-6 py-6 space-y-4 overflow-y-auto flex-1">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Modifier Name <span className="text-red-500">*</span></label>
                                <input type="text" value={modifierFormData.modifier_name} onChange={(e) => setModifierFormData({ ...modifierFormData, modifier_name: e.target.value })} maxLength={50} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]" placeholder="e.g., Extra Whipped Cream" />
                                <p className="text-xs text-gray-500 mt-1">{modifierFormData.modifier_name.length}/50 characters</p>
                            </div>
                            {/* Linked Ingredient */}
                            <div className="relative" ref={modifierIngredientDropdownRef}>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Linked Ingredient
                                    <span className="ml-1 text-xs font-normal text-gray-400">(optional)</span>
                                </label>
                                <input
                                    type="text"
                                    value={
                                        modifierFormData.ingredient_id
                                            ? ingredients.find(ing => String(ing.ingredient_id) === String(modifierFormData.ingredient_id))?.ingredient_name
                                            || ingredients.find(ing => String(ing.ingredient_id) === String(modifierFormData.ingredient_id))?.name
                                            || modifierIngredientSearch
                                            : modifierIngredientSearch
                                    }
                                    onFocus={() => setOpenModifierIngredientDropdown(true)}
                                    onChange={(e) => {
                                        setModifierFormData(prev => ({ ...prev, ingredient_id: '' }))
                                        setModifierIngredientSearch(e.target.value)
                                        setOpenModifierIngredientDropdown(true)
                                    }}
                                    placeholder="Search and select ingredient..."
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400"
                                />
                                {modifierFormData.ingredient_id && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setModifierFormData(prev => ({ ...prev, ingredient_id: '' }))
                                            setModifierIngredientSearch('')
                                        }}
                                        className="absolute right-3 top-[2.15rem] text-gray-400 hover:text-gray-600"
                                        title="Clear ingredient"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24">
                                            <path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                                        </svg>
                                    </button>
                                )}
                                {openModifierIngredientDropdown && (
                                    <div className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-gray-300 bg-white shadow-lg">
                                        {(() => {
                                            const query = modifierIngredientSearch.toLowerCase()
                                            const filtered = ingredients.filter(ing =>
                                                String(ing.ingredient_name || ing.name || '').toLowerCase().includes(query) ||
                                                String(ing.unit || '').toLowerCase().includes(query) ||
                                                String(ing.category || '').toLowerCase().includes(query)
                                            )
                                            if (filtered.length === 0) {
                                                return <div className="px-3 py-2 text-sm text-gray-500">No ingredients found.</div>
                                            }
                                            return filtered.map(ing => (
                                                <button
                                                    key={ing.ingredient_id}
                                                    type="button"
                                                    onClick={() => {
                                                        setModifierFormData(prev => ({ ...prev, ingredient_id: ing.ingredient_id }))
                                                        setModifierIngredientSearch(ing.ingredient_name || ing.name || '')
                                                        setOpenModifierIngredientDropdown(false)
                                                    }}
                                                    className="w-full border-b border-gray-100 px-3 py-2 text-left hover:bg-gray-50 last:border-b-0"
                                                >
                                                    <div className="text-sm font-medium text-gray-800">
                                                        {ing.ingredient_name || ing.name}
                                                    </div>
                                                    <div className="text-xs text-gray-500">
                                                        {ing.unit || '—'}{ing.category ? ` • ${ing.category}` : ''}
                                                    </div>
                                                </button>
                                            ))
                                        })()}
                                    </div>
                                )}
                                <p className="text-xs text-gray-500 mt-1">
                                    Link this modifier to an inventory ingredient for stock tracking.
                                </p>
                            </div>
                            <div className="relative" ref={modifierCategoryDropdownRef}>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Category <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={modifierFormData.category}
                                    onFocus={() => {
                                        setModifierCategorySearch(modifierFormData.category || "")
                                        setOpenModifierCategoryDropdown(true)
                                    }}
                                    onChange={(e) => {
                                        setModifierFormData({ ...modifierFormData, category: e.target.value })
                                        setModifierCategorySearch(e.target.value)
                                        setOpenModifierCategoryDropdown(true)
                                    }}
                                    maxLength={30}
                                    placeholder="Search or type a category..."
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400"
                                />
                                {openModifierCategoryDropdown && (
                                    <div className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-gray-300 bg-white shadow-lg">
                                        {filteredModifierCategoryOptions.length === 0 ? (
                                            <div className="px-3 py-2 text-sm text-gray-500">
                                                No existing categories found. Continue typing to create a new one.
                                            </div>
                                        ) : (
                                            filteredModifierCategoryOptions.map((category) => (
                                                <button
                                                    key={category}
                                                    type="button"
                                                    onClick={() => {
                                                        setModifierFormData({ ...modifierFormData, category })
                                                        setModifierCategorySearch(category)
                                                        setOpenModifierCategoryDropdown(false)
                                                    }}
                                                    className="w-full border-b border-gray-100 px-3 py-2 text-left hover:bg-gray-50 last:border-b-0"
                                                >
                                                    <div className="text-sm font-medium text-gray-800">{category}</div>
                                                </button>
                                            ))
                                        )}
                                    </div>
                                )}
                                <p className="text-xs text-gray-500 mt-1">
                                    {modifierFormData.category.length}/30 characters
                                </p>
                                <p className="text-xs text-gray-500 mt-1">Select an existing category or type a new one.</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Price <span className="text-red-500">*</span></label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="9999"
                                    value={modifierFormData.price}
                                    onChange={(e) => {
                                        const raw = e.target.value.replace('.', '').replace('-', '')
                                        if (raw.length <= MAX_PRICE_LENGTH) {
                                            setModifierFormData({ ...modifierFormData, price: e.target.value })
                                        }
                                    }}
                                    onKeyDown={handlePriceKeyDown}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                    placeholder="0.00"
                                />
                                <p className="text-xs text-gray-500 mt-1">{String(modifierFormData.price).replace('.', '').replace('-', '').length} / 4 digits</p>
                                <p className="text-xs text-gray-500 mt-1">(can be 0 for no charge)</p>
                            </div>
                            <ApplicableCategoriesSelector
                                value={modifierFormData.applicable_categories}
                                onChange={(cats) => setModifierFormData({ ...modifierFormData, applicable_categories: cats })}
                            />
                        </div>
                        <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3 flex-shrink-0">
                            <button
                                onClick={() => confirmDiscardChanges(hasAddModifierChanges, () => {
                                    setShowAddModifierModal(false)
                                    setModifierFormData({ modifier_name: "", category: "", price: "", applicable_categories: [] })
                                    setInitialModifierFormData(null)
                                    setModifierIngredientSearch("")
                                    setOpenModifierIngredientDropdown(false)
                                })}
                                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddModifier}
                                disabled={!hasAddModifierChanges}
                                className={`px-6 py-2 rounded-lg font-medium ${!hasAddModifierChanges
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    : 'bg-purple-600 text-white hover:bg-purple-700'
                                    }`}
                            >
                                Add Modifier
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Modifier Modal */}
            {showEditModifierModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black bg-opacity-50"
                        onClick={() => confirmDiscardChanges(hasEditModifierChanges, () => {
                            setShowEditModifierModal(false)
                            setEditingModifier(null)
                            setModifierFormData({ modifier_name: "", category: "", price: "", applicable_categories: [], ingredient_id: "" })
                            setInitialModifierFormData(null)
                            setModifierIngredientSearch("")
                            setOpenModifierIngredientDropdown(false)
                        })}>
                    </div>
                    <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] flex flex-col">
                        <div className="border-b border-gray-200 px-6 py-4 flex-shrink-0">
                            <h2 className="text-xl font-bold text-gray-800">Edit Modifier</h2>
                            <p className="text-sm text-gray-500 mt-1">Update this modifier's name, category, price, or linked ingredient.</p>
                        </div>
                        <div className="px-6 py-6 space-y-4 overflow-y-auto flex-1">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Modifier Name <span className="text-red-500">*</span></label>
                                <input type="text" value={modifierFormData.modifier_name} onChange={(e) => setModifierFormData({ ...modifierFormData, modifier_name: e.target.value })} maxLength={50} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]" />
                                <p className="text-xs text-gray-500 mt-1">{modifierFormData.modifier_name.length}/50 characters</p>
                            </div>
                            {/* Linked Ingredient */}
                            <div className="relative" ref={modifierIngredientDropdownRef}>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Linked Ingredient
                                </label>
                                <input
                                    type="text"
                                    value={
                                        modifierFormData.ingredient_id
                                            ? ingredients.find(ing => String(ing.ingredient_id) === String(modifierFormData.ingredient_id))?.ingredient_name
                                            || ingredients.find(ing => String(ing.ingredient_id) === String(modifierFormData.ingredient_id))?.name
                                            || modifierIngredientSearch
                                            : modifierIngredientSearch
                                    }
                                    onFocus={() => setOpenModifierIngredientDropdown(true)}
                                    onChange={(e) => {
                                        setModifierFormData(prev => ({ ...prev, ingredient_id: '' }))
                                        setModifierIngredientSearch(e.target.value)
                                        setOpenModifierIngredientDropdown(true)
                                    }}
                                    placeholder="Search and select ingredient..."
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400"
                                />
                                {modifierFormData.ingredient_id && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setModifierFormData(prev => ({ ...prev, ingredient_id: '' }))
                                            setModifierIngredientSearch('')
                                        }}
                                        className="absolute right-3 top-[2.15rem] text-gray-400 hover:text-gray-600"
                                        title="Clear ingredient"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24">
                                            <path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                                        </svg>
                                    </button>
                                )}
                                {openModifierIngredientDropdown && (
                                    <div className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-gray-300 bg-white shadow-lg">
                                        {(() => {
                                            const query = modifierIngredientSearch.toLowerCase()
                                            const filtered = ingredients.filter(ing =>
                                                String(ing.ingredient_name || ing.name || '').toLowerCase().includes(query) ||
                                                String(ing.unit || '').toLowerCase().includes(query) ||
                                                String(ing.category || '').toLowerCase().includes(query)
                                            )
                                            if (filtered.length === 0) {
                                                return <div className="px-3 py-2 text-sm text-gray-500">No ingredients found.</div>
                                            }
                                            return filtered.map(ing => (
                                                <button
                                                    key={ing.ingredient_id}
                                                    type="button"
                                                    onClick={() => {
                                                        setModifierFormData(prev => ({ ...prev, ingredient_id: ing.ingredient_id }))
                                                        setModifierIngredientSearch(ing.ingredient_name || ing.name || '')
                                                        setOpenModifierIngredientDropdown(false)
                                                    }}
                                                    className="w-full border-b border-gray-100 px-3 py-2 text-left hover:bg-gray-50 last:border-b-0"
                                                >
                                                    <div className="text-sm font-medium text-gray-800">
                                                        {ing.ingredient_name || ing.name}
                                                    </div>
                                                    <div className="text-xs text-gray-500">
                                                        {ing.unit || '—'}{ing.category ? ` • ${ing.category}` : ''}
                                                    </div>
                                                </button>
                                            ))
                                        })()}
                                    </div>
                                )}
                                <p className="text-xs text-gray-500 mt-1">
                                    Link this modifier to an inventory ingredient for stock tracking.
                                </p>
                            </div>
                            <div className="relative" ref={modifierCategoryDropdownRef}>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Category <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={modifierFormData.category}
                                    onFocus={() => {
                                        setModifierCategorySearch(modifierFormData.category || "")
                                        setOpenModifierCategoryDropdown(true)
                                    }}
                                    onChange={(e) => {
                                        setModifierFormData({ ...modifierFormData, category: e.target.value })
                                        setModifierCategorySearch(e.target.value)
                                        setOpenModifierCategoryDropdown(true)
                                    }}
                                    maxLength={30}
                                    placeholder="Search or type a category..."
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400"
                                />
                                {openModifierCategoryDropdown && (
                                    <div className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-gray-300 bg-white shadow-lg">
                                        {filteredModifierCategoryOptions.length === 0 ? (
                                            <div className="px-3 py-2 text-sm text-gray-500">
                                                No existing categories found. Continue typing to create a new one.
                                            </div>
                                        ) : (
                                            filteredModifierCategoryOptions.map((category) => (
                                                <button
                                                    key={category}
                                                    type="button"
                                                    onClick={() => {
                                                        setModifierFormData({ ...modifierFormData, category })
                                                        setModifierCategorySearch(category)
                                                        setOpenModifierCategoryDropdown(false)
                                                    }}
                                                    className="w-full border-b border-gray-100 px-3 py-2 text-left hover:bg-gray-50 last:border-b-0"
                                                >
                                                    <div className="text-sm font-medium text-gray-800">{category}</div>
                                                </button>
                                            ))
                                        )}
                                    </div>
                                )}
                                <p className="text-xs text-gray-500 mt-1">
                                    {modifierFormData.category.length}/30 characters
                                </p>
                                <p className="text-xs text-gray-500 mt-1">Select an existing category or type a new one.</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Price <span className="text-red-500">*</span></label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="9999"
                                    value={modifierFormData.price}
                                    onChange={(e) => {
                                        const raw = e.target.value.replace('.', '').replace('-', '')
                                        if (raw.length <= MAX_PRICE_LENGTH) {
                                            setModifierFormData({ ...modifierFormData, price: e.target.value })
                                        }
                                    }}
                                    onKeyDown={handlePriceKeyDown}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                />
                                <p className="text-xs text-gray-500 mt-1">{String(modifierFormData.price).replace('.', '').replace('-', '').length} / 4 digits</p>
                                <p className="text-xs text-gray-500 mt-1">Max: ₱9999 (can be 0 for no charge)</p>
                            </div>
                            <ApplicableCategoriesSelector
                                value={modifierFormData.applicable_categories}
                                onChange={(cats) => setModifierFormData({ ...modifierFormData, applicable_categories: cats })}
                            />
                        </div>
                        <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3 flex-shrink-0">
                            <button
                                onClick={() => confirmDiscardChanges(hasEditModifierChanges, () => {
                                    setShowEditModifierModal(false)
                                    setEditingModifier(null)
                                    setModifierFormData({ modifier_name: "", category: "", price: "", applicable_categories: [], ingredient_id: "" })
                                    setInitialModifierFormData(null)
                                    setModifierIngredientSearch("")
                                    setOpenModifierIngredientDropdown(false)
                                })}
                                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleUpdateModifier}
                                disabled={!hasEditModifierChanges}
                                className={`px-6 py-2 rounded-lg font-medium ${!hasEditModifierChanges
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    : 'bg-purple-600 text-white hover:bg-purple-700'
                                    }`}
                            >
                                Update Modifier
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Archived Modal */}
            {showArchivedViewerModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-black bg-opacity-50"
                        onClick={closeArchivedViewerModal}
                    ></div>

                    <div className="relative bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
                        <div className="border-b border-gray-200 px-6 py-4 space-y-3 flex-shrink-0">
                            <div className="flex items-center justify-between gap-4 flex-wrap">
                                <div>
                                    <h2 className="text-xl font-bold text-gray-800">
                                        Archived {activeTab === 'menu' ? 'Menu Items' : activeTab === 'variants' ? 'Variants' : 'Modifiers'}
                                    </h2>
                                    <p className="text-sm text-gray-500 mt-1">
                                        Reinstate archived records for the currently active tab.
                                    </p>
                                </div>
                            </div>
                            <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24">
                                        <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10a7 7 0 1 0 14 0a7 7 0 1 0-14 0m18 11l-6-6" />
                                    </svg>
                                </span>
                                <input
                                    type="text"
                                    placeholder={`Search archived ${activeTab === 'menu' ? 'items' : activeTab === 'variants' ? 'variants' : 'modifiers'}...`}
                                    value={archivedSearch}
                                    onChange={(e) => setArchivedSearch(e.target.value)}
                                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                />
                            </div>

                            {activeTab === 'menu' && (
                                <>
                                    <div className="flex gap-2 overflow-x-auto pb-1">
                                        {['All', ...new Set(archivedMenuItems.map(i => i.category).filter(Boolean))].map(cat => (
                                            <button
                                                key={cat}
                                                onClick={() => {
                                                    setArchivedCategoryFilter(cat)
                                                    setArchivedClassificationFilter('All')
                                                    if (cat === 'All') {
                                                        setArchivedAvailableClassifications([])
                                                    } else {
                                                        const classifications = [
                                                            ...new Set(
                                                                archivedMenuItems
                                                                    .filter(item => item.category === cat)
                                                                    .map(item => item.classification)
                                                                    .filter(Boolean)
                                                            )
                                                        ].sort()
                                                        setArchivedAvailableClassifications(classifications)
                                                    }
                                                }}
                                                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors flex-shrink-0 whitespace-nowrap ${archivedCategoryFilter === cat
                                                    ? 'bg-[#A0826D] text-white'
                                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                    }`}
                                            >
                                                {cat}
                                                {cat !== 'All' && (
                                                    <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${archivedCategoryFilter === cat
                                                        ? 'bg-white/30 text-white'
                                                        : 'bg-gray-200 text-gray-500'
                                                        }`}>
                                                        {archivedMenuItems.filter(i => i.category === cat).length}
                                                    </span>
                                                )}
                                            </button>
                                        ))}
                                    </div>

                                    {archivedCategoryFilter !== 'All' && archivedAvailableClassifications.length > 0 && (
                                        <div
                                            className="flex items-center gap-2 overflow-x-auto pb-1 pl-1 scrollbar-hide"
                                            onMouseDown={handleDragMouseDown}
                                            onMouseMove={handleDragMouseMove}
                                            onMouseUp={handleDragMouseUpOrLeave}
                                            onMouseLeave={handleDragMouseUpOrLeave}
                                        >
                                            <span className="text-xs text-gray-400 font-medium shrink-0">
                                                {archivedCategoryFilter}:
                                            </span>
                                            {['All', ...archivedAvailableClassifications].map(cls => (
                                                <button
                                                    key={cls}
                                                    onClick={() => setArchivedClassificationFilter(cls)}
                                                    className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all flex-shrink-0 whitespace-nowrap ${archivedClassificationFilter === cls
                                                        ? 'bg-[#303030] border-[#303030] text-white'
                                                        : 'bg-white border-gray-300 text-gray-600 hover:border-gray-500'
                                                        }`}
                                                >
                                                    {cls === 'All' ? `All ${archivedCategoryFilter}` : cls}
                                                    {cls !== 'All' && (
                                                        <span className={`ml-1 ${archivedClassificationFilter === cls ? 'opacity-70' : 'text-gray-400'}`}>
                                                            ({archivedMenuItems.filter(i => i.category === archivedCategoryFilter && i.classification === cls).length})
                                                        </span>
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}

                            {activeTab === 'variants' && (
                                <div className="flex flex-wrap gap-2">
                                    {['All', ...new Set(archivedVariants.map(v => v.category).filter(Boolean))].map(cat => (
                                        <button
                                            key={cat}
                                            onClick={() => setArchivedCategoryFilter(cat)}
                                            className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${archivedCategoryFilter === cat
                                                ? 'bg-indigo-600 text-white'
                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                }`}
                                        >
                                            {cat}
                                            {cat !== 'All' && (
                                                <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${archivedCategoryFilter === cat
                                                    ? 'bg-white/30 text-white'
                                                    : 'bg-gray-200 text-gray-500'
                                                    }`}>
                                                    {archivedVariants.filter(v => v.category === cat).length}
                                                </span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {activeTab === 'modifiers' && (
                                <div className="flex flex-wrap gap-2">
                                    {['All', ...new Set(archivedModifiers.map(m => m.category).filter(Boolean))].map(cat => (
                                        <button
                                            key={cat}
                                            onClick={() => setArchivedCategoryFilter(cat)}
                                            className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${archivedCategoryFilter === cat
                                                ? 'bg-purple-600 text-white'
                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                }`}
                                        >
                                            {cat}
                                            {cat !== 'All' && (
                                                <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${archivedCategoryFilter === cat
                                                    ? 'bg-white/30 text-white'
                                                    : 'bg-gray-200 text-gray-500'
                                                    }`}>
                                                    {archivedModifiers.filter(m => m.category === cat).length}
                                                </span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>


                        <div className="overflow-y-auto flex-1">
                            {loadingArchivedItems ? (
                                <div className="px-6 py-10 text-center text-gray-500">
                                    Loading archived records...
                                </div>
                            ) : activeTab === 'menu' ? (() => {
                                const filteredArchivedMenuItems = archivedMenuItems.filter(item => {
                                    const matchesSearch =
                                        String(item.name || '').toLowerCase().includes(archivedSearch.toLowerCase()) ||
                                        String(item.category || '').toLowerCase().includes(archivedSearch.toLowerCase()) ||
                                        String(item.classification || '').toLowerCase().includes(archivedSearch.toLowerCase())
                                    const matchesCategory = archivedCategoryFilter === 'All' || item.category === archivedCategoryFilter
                                    const matchesClassification = archivedClassificationFilter === 'All' || item.classification === archivedClassificationFilter
                                    return matchesSearch && matchesCategory && matchesClassification
                                })

                                const ARCHIVED_PAGE_SIZE = 10
                                const pagedArchivedMenuItems = filteredArchivedMenuItems.slice(
                                    (archivedPage - 1) * ARCHIVED_PAGE_SIZE,
                                    archivedPage * ARCHIVED_PAGE_SIZE
                                )

                                return filteredArchivedMenuItems.length === 0 ? (
                                    <div className="px-6 py-10 text-center text-gray-500">
                                        {archivedSearch || archivedCategoryFilter !== 'All' ? 'No archived items match your filters.' : 'No archived menu items found.'}
                                    </div>
                                ) : (
                                    <>
                                        <table className="w-full">
                                            <thead className="bg-gray-50 border-b border-gray-200">
                                                <tr>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200 bg-white">
                                                {pagedArchivedMenuItems.map((item) => (
                                                    <tr key={item.item_id}>
                                                        <td className="px-6 py-4">
                                                            <div className="flex items-center gap-3">
                                                                {item.item_img && (
                                                                    <img
                                                                        src={item.item_img}
                                                                        alt={item.name}
                                                                        className="w-10 h-10 rounded-lg object-cover"
                                                                    />
                                                                )}
                                                                <div>
                                                                    <p className="font-medium text-gray-800">{item.name}</p>
                                                                    <p className="text-sm text-gray-500">{item.classification}</p>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 text-gray-700">{item.category}</td>
                                                        <td className="px-6 py-4 text-gray-700">₱{parseFloat(item.item_price || 0).toFixed(2)}</td>
                                                        <td className="px-6 py-4">
                                                            <button
                                                                onClick={() => handleReinstateItem(item)}
                                                                disabled={isDuplicateActiveMenuItem(item)}
                                                                className={`px-3 py-1.5 rounded-lg text-sm font-medium ${isDuplicateActiveMenuItem(item)
                                                                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                                                    : 'bg-green-600 text-white hover:bg-green-700'
                                                                    }`}
                                                                title={isDuplicateActiveMenuItem(item) ? 'An active duplicate already exists' : 'Reinstate'}
                                                            >
                                                                {isDuplicateActiveMenuItem(item) ? 'Duplicate Exists' : 'Reinstate'}
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        <Pagination
                                            page={archivedPage}
                                            totalItems={filteredArchivedMenuItems.length}
                                            pageSize={ARCHIVED_PAGE_SIZE}
                                            onPageChange={setArchivedPage}
                                        />
                                    </>
                                )
                            })() : activeTab === 'variants' ? (() => {
                                const filteredArchivedVariants = archivedVariants.filter(variant => {
                                    const matchesSearch =
                                        String(variant.variant_name || '').toLowerCase().includes(archivedSearch.toLowerCase()) ||
                                        String(variant.category || '').toLowerCase().includes(archivedSearch.toLowerCase())
                                    const matchesCategory = archivedCategoryFilter === 'All' || variant.category === archivedCategoryFilter
                                    return matchesSearch && matchesCategory
                                })
                                const ARCHIVED_PAGE_SIZE = 10
                                const pagedArchivedVariants = filteredArchivedVariants.slice(
                                    (archivedPage - 1) * ARCHIVED_PAGE_SIZE,
                                    archivedPage * ARCHIVED_PAGE_SIZE
                                )

                                return filteredArchivedVariants.length === 0 ? (
                                    <div className="px-6 py-10 text-center text-gray-500">
                                        No archived variants found.
                                    </div>
                                ) : (
                                    <>
                                        <table className="w-full">
                                            <thead className="bg-gray-50 border-b border-gray-200">
                                                <tr>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Variant</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200 bg-white">
                                                {pagedArchivedVariants.map((variant) => (
                                                    <tr key={variant.variant_id}>
                                                        <td className="px-6 py-4 font-medium text-gray-800">{variant.variant_name}</td>
                                                        <td className="px-6 py-4 text-gray-700">{variant.category}</td>
                                                        <td className="px-6 py-4 text-gray-700">₱{parseFloat(variant.price || 0).toFixed(2)}</td>
                                                        <td className="px-6 py-4">
                                                            <button
                                                                onClick={() => handleReinstateVariant(variant)}
                                                                disabled={isDuplicateActiveVariant(variant)}
                                                                className={`px-3 py-1.5 rounded-lg text-sm font-medium ${isDuplicateActiveVariant(variant)
                                                                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                                                    : 'bg-green-600 text-white hover:bg-green-700'
                                                                    }`}
                                                                title={isDuplicateActiveVariant(variant) ? 'An active duplicate already exists' : 'Reinstate'}
                                                            >
                                                                {isDuplicateActiveVariant(variant) ? 'Duplicate Exists' : 'Reinstate'}
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        <Pagination
                                            page={archivedPage}
                                            totalItems={filteredArchivedVariants.length}
                                            pageSize={ARCHIVED_PAGE_SIZE}
                                            onPageChange={setArchivedPage}
                                        />
                                    </>
                                )
                            })() : (
                                (() => {
                                    const filteredArchivedModifiers = archivedModifiers.filter(modifier => {
                                        const matchesSearch =
                                            String(modifier.modifier_name || '').toLowerCase().includes(archivedSearch.toLowerCase()) ||
                                            String(modifier.category || '').toLowerCase().includes(archivedSearch.toLowerCase())
                                        const matchesCategory = archivedCategoryFilter === 'All' || modifier.category === archivedCategoryFilter
                                        return matchesSearch && matchesCategory
                                    })
                                    const ARCHIVED_PAGE_SIZE = 10
                                    const pagedArchivedModifiers = filteredArchivedModifiers.slice(
                                        (archivedPage - 1) * ARCHIVED_PAGE_SIZE,
                                        archivedPage * ARCHIVED_PAGE_SIZE
                                    )

                                    return filteredArchivedModifiers.length === 0 ? (
                                        <div className="px-6 py-10 text-center text-gray-500">
                                            No archived modifiers found.
                                        </div>
                                    ) : (
                                        <>
                                            <table className="w-full">
                                                <thead className="bg-gray-50 border-b border-gray-200">
                                                    <tr>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Modifier</th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-200 bg-white">
                                                    {pagedArchivedModifiers.map((modifier) => (
                                                        <tr key={modifier.modifier_id}>
                                                            <td className="px-6 py-4 font-medium text-gray-800">{modifier.modifier_name}</td>
                                                            <td className="px-6 py-4 text-gray-700">{modifier.category}</td>
                                                            <td className="px-6 py-4 text-gray-700">
                                                                {parseFloat(modifier.price || 0) === 0
                                                                    ? 'No charge'
                                                                    : `₱${parseFloat(modifier.price || 0).toFixed(2)}`}
                                                            </td>
                                                            <td className="px-6 py-4">
                                                                <button
                                                                    onClick={() => handleReinstateModifier(modifier)}
                                                                    disabled={isDuplicateActiveModifier(modifier)}
                                                                    className={`px-3 py-1.5 rounded-lg text-sm font-medium ${isDuplicateActiveModifier(modifier)
                                                                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                                                        : 'bg-green-600 text-white hover:bg-green-700'
                                                                        }`}
                                                                    title={isDuplicateActiveModifier(modifier) ? 'An active duplicate already exists' : 'Reinstate'}
                                                                >
                                                                    {isDuplicateActiveModifier(modifier) ? 'Duplicate Exists' : 'Reinstate'}
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                            <Pagination
                                                page={archivedPage}
                                                totalItems={filteredArchivedModifiers.length}
                                                pageSize={ARCHIVED_PAGE_SIZE}
                                                onPageChange={setArchivedPage}
                                            />
                                        </>
                                    )
                                })()
                            )}
                        </div>

                        <div className="border-t border-gray-200 px-6 py-4 flex justify-end flex-shrink-0">
                            <button
                                onClick={closeArchivedViewerModal}
                                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Price Change Requests Modal */}
            {showPriceRequestsModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black bg-opacity-50" onClick={closePriceRequestsModal} />
                    <div className="relative bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col">
                        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
                            <div>
                                <h2 className="text-xl font-bold text-gray-800">Price Change Requests</h2>
                                <p className="text-sm text-gray-500 mt-0.5">Review and action admin price change requests</p>
                            </div>
                            {/* Filter tabs */}
                            <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                                {['pending', 'approved', 'rejected'].map(status => (
                                    <button
                                        key={status}
                                        onClick={() => setPriceRequestFilter(status)}
                                        className={`px-3 py-1.5 rounded-md text-xs font-semibold capitalize transition-all ${priceRequestFilter === status
                                            ? status === 'pending' ? 'bg-amber-500 text-white'
                                                : status === 'approved' ? 'bg-emerald-600 text-white'
                                                    : 'bg-red-600 text-white'
                                            : 'text-gray-600 hover:bg-gray-200'
                                            }`}
                                    >
                                        {status}
                                        <span className="ml-1">
                                            ({priceRequests.filter(r => r.status === status).length})
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="overflow-y-auto flex-1">
                            {loadingPriceRequests ? (
                                <div className="px-6 py-10 text-center text-gray-500">Loading requests...</div>
                            ) : (() => {
                                const filteredPriceRequests = priceRequests.filter(r => r.status === priceRequestFilter)
                                const PRICE_REQUESTS_PAGE_SIZE = 10
                                const pagedPriceRequests = filteredPriceRequests.slice(
                                    (priceRequestsModalPage - 1) * PRICE_REQUESTS_PAGE_SIZE,
                                    priceRequestsModalPage * PRICE_REQUESTS_PAGE_SIZE
                                )

                                return filteredPriceRequests.length === 0 ? (
                                    <div className="px-6 py-10 text-center text-gray-500">
                                        No {priceRequestFilter} requests found.
                                    </div>
                                ) : (
                                    <>
                                        <table className="w-full">
                                            <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                                                <tr>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price Change</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                                    {priceRequestFilter === 'pending' && (
                                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                                                    )}
                                                    {priceRequestFilter === 'rejected' && (
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rejection Reason</th>
                                                    )}
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200 bg-white">
                                                {pagedPriceRequests.map(request => (
                                                    <tr key={request.id} className="hover:bg-gray-50">
                                                        <td className="px-6 py-4">
                                                            <p className="font-medium text-gray-800 text-sm">{request.item_name}</p>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${request.item_type === 'menu_item' ? 'bg-orange-100 text-orange-700' :
                                                                request.item_type === 'variant' ? 'bg-indigo-100 text-indigo-700' :
                                                                    'bg-purple-100 text-purple-700'
                                                                }`}>
                                                                {request.item_type.replace('_', ' ')}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <span className="line-through text-red-400 text-sm">₱{parseFloat(request.current_price).toFixed(2)}</span>
                                                            <span className="mx-1.5 text-gray-400">→</span>
                                                            <span className="text-emerald-600 font-bold text-sm">₱{parseFloat(request.requested_price).toFixed(2)}</span>
                                                        </td>
                                                        <td className="px-6 py-4 max-w-xs">
                                                            <p className="text-sm text-gray-600 truncate" title={request.reason}>{request.reason}</p>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500">
                                                            {new Date(request.created_at).toLocaleDateString('en-PH', {
                                                                month: 'short', day: 'numeric', year: 'numeric'
                                                            })}
                                                        </td>
                                                        {priceRequestFilter === 'pending' && (
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                <div className="flex items-center gap-2 justify-end">
                                                                    <button
                                                                        onClick={() => handleApprovePriceRequest(request)}
                                                                        disabled={processingPriceRequestId === request.id}
                                                                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${processingPriceRequestId === request.id
                                                                            ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                                                            : 'bg-emerald-600 text-white hover:bg-emerald-700'
                                                                            }`}
                                                                    >
                                                                        {processingPriceRequestId === request.id ? 'Processing...' : 'Approve'}
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        )}
                                                        {priceRequestFilter === 'rejected' && (
                                                            <td className="px-6 py-4 max-w-xs">
                                                                <span className="text-xs text-gray-400 italic">Rejected</span>
                                                            </td>
                                                        )}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        <Pagination
                                            page={priceRequestsModalPage}
                                            totalItems={filteredPriceRequests.length}
                                            pageSize={PRICE_REQUESTS_PAGE_SIZE}
                                            onPageChange={setPriceRequestsModalPage}
                                        />
                                    </>
                                )
                            })()}
                        </div>

                        <div className="border-t border-gray-200 px-6 py-4 flex justify-end flex-shrink-0">
                            <button onClick={closePriceRequestsModal} className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300">
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Bulk Import Modal */}
            {showBulkImportModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setShowBulkImportModal(false)} />
                    <div className="relative bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
                        <div className="border-b border-gray-200 px-6 py-4 flex-shrink-0">
                            <h2 className="text-xl font-bold text-gray-800">Bulk Import Menu Items</h2>
                            <p className="text-sm text-gray-500 mt-1">
                                Upload a CSV file with columns: <code className="bg-gray-100 px-1 rounded text-xs">name, item_price, category, classification, description, item_img (optional), branches (optional)</code>
                            </p>
                        </div>

                        <div className="px-6 py-4 border-b border-gray-100 flex-shrink-0 flex items-center gap-4 flex-wrap">
                            <div className="flex items-center gap-2">
                                <label className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg cursor-pointer text-sm font-medium text-gray-700">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path fill="currentColor" d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zm-1 7V3.5L18.5 9zM8 13h3v-3h2v3h3l-4 4z" /></svg>
                                    {bulkImportFile ? bulkImportFile.name : 'Choose CSV File'}
                                    <input type="file" accept=".xlsx" onChange={handleBulkImportFileChange} className="hidden" />
                                </label>
                                {bulkImportFile && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setBulkImportFile(null)
                                            setBulkImportPreview([])
                                            setBulkImportErrors([])
                                            setBulkImportResult(null)
                                        }}
                                        className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg"
                                        title="Remove file"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24">
                                            <path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                                        </svg>
                                    </button>
                                )}
                            </div>

                            {bulkImportPreview.length > 0 && (
                                <div className="flex gap-2 text-sm flex-wrap">
                                    {bulkImportCounts.new > 0 && (
                                        <span className="px-2.5 py-1 rounded-full bg-green-100 text-green-700 font-semibold">
                                            {bulkImportCounts.new} new
                                        </span>
                                    )}
                                    {bulkImportCounts.update > 0 && (
                                        <span className="px-2.5 py-1 rounded-full bg-blue-100 text-blue-700 font-semibold">
                                            {bulkImportCounts.update} to update
                                        </span>
                                    )}
                                    {bulkImportCounts.duplicate > 0 && (
                                        <span className="px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 font-semibold">
                                            {bulkImportCounts.duplicate} unchanged
                                        </span>
                                    )}
                                    {bulkImportErrors.length > 0 && (
                                        <span className="px-2.5 py-1 rounded-full bg-red-100 text-red-700 font-semibold">
                                            {bulkImportErrors.length} with errors
                                        </span>
                                    )}
                                </div>
                            )}

                            <button
                                type="button"
                                onClick={() => downloadExcelTemplate(
                                    'menu_import_template.xlsx',
                                    'Menu Items',
                                    ['name', 'item_price', 'category', 'classification', 'description', 'item_img', 'branches'],
                                    [['Spanish Latte', 120, 'Coffee', 'Hot', 'A creamy and slightly sweet espresso drink with a distinct Spanish twist.', 'https://example.com/image.jpg', 'Main Branch|Downtown Branch']]
                                )}
                                className="ml-auto text-xs text-indigo-600 hover:underline flex items-center gap-1"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24"><path fill="currentColor" d="M5 20h14v-2H5zm7-18L5.33 9h4.84v6h3.66V9h4.84z" /></svg>
                                Download Template
                            </button>

                        </div>

                        {bulkImportErrors.length > 0 && (
                            <div className="px-6 py-3 bg-red-50 border-b border-red-100 flex-shrink-0 max-h-32 overflow-y-auto">
                                <p className="text-xs font-semibold text-red-700 mb-1">Errors found — these rows will be skipped:</p>
                                {bulkImportErrors.map((e, i) => (
                                    <p key={i} className="text-xs text-red-600">
                                        Row {e.row} ({e.name}): {e.errors.join('; ')}
                                    </p>
                                ))}
                            </div>
                        )}

                        {/* Branch Assignment */}
                        <div className="px-6 py-4 border-b border-gray-100 flex-shrink-0">
                            <div className="flex items-center justify-between mb-2">
                                <label className="text-sm font-medium text-gray-700">
                                    Fallback Branches
                                    {bulkImportNeedsFallbackBranches && <span className="text-red-500"> *</span>}
                                </label>
                                <button
                                    type="button"
                                    onClick={() => setBulkImportBranchIds(
                                        bulkImportBranchIds.size === branches.length
                                            ? new Set()
                                            : new Set(branches.map(b => b.branch_id))
                                    )}
                                    className="text-xs font-medium text-[#FF6F3C] hover:underline"
                                >
                                    {bulkImportBranchIds.size === branches.length ? 'Clear All' : 'Select All'}
                                </button>
                            </div>
                            <div className="grid grid-cols-3 gap-2 max-h-28 overflow-y-auto border rounded-lg p-2">
                                {branches.map(branch => {
                                    const isSelected = bulkImportBranchIds.has(branch.branch_id)
                                    return (
                                        <label
                                            key={branch.branch_id}
                                            className={`flex items-center gap-2 p-2 rounded cursor-pointer border text-xs ${isSelected ? 'bg-orange-50 border-orange-300' : 'border-gray-200 hover:bg-gray-50'
                                                }`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={isSelected}
                                                onChange={() => {
                                                    setBulkImportBranchIds(prev => {
                                                        const next = new Set(prev)
                                                        if (next.has(branch.branch_id)) next.delete(branch.branch_id)
                                                        else next.add(branch.branch_id)
                                                        return next
                                                    })
                                                }}
                                                className="accent-orange-500"
                                            />
                                            <span>{branch.name}</span>
                                        </label>
                                    )
                                })}
                            </div>
                            <p className="text-xs text-gray-500 mt-1">{bulkImportBranchIds.size} of {branches.length} selected</p>
                            <p className="text-xs text-gray-500 mt-1">
                                {bulkImportNeedsFallbackBranches
                                    ? 'Used for rows that don\'t specify a branches column in the CSV.'
                                    : 'Not required — every row in this CSV already specifies its own branches.'}
                            </p>
                        </div>

                        <div className="overflow-y-auto flex-1">
                            {bulkImportPreview.length === 0 ? (
                                <div className="px-6 py-12 text-center text-gray-400 text-sm">
                                    Upload a CSV file to preview items before importing.
                                </div>
                            ) : (
                                <table className="w-full">
                                    <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Classification</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Branches</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Changes</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 bg-white">
                                        {bulkImportPreview.map((row, i) => {
                                            const { status, changes } = bulkImportRowStatuses[i]
                                            const rowBg =
                                                status === 'error' ? 'bg-red-50' :
                                                    status === 'duplicate' ? 'bg-amber-50' :
                                                        status === 'update' ? 'bg-blue-50' :
                                                            'hover:bg-gray-50'
                                            return (
                                                <tr key={i} className={rowBg}>
                                                    <td className="px-4 py-2.5">
                                                        {status === 'error' && (
                                                            <span className="text-xs font-semibold text-red-600">Error</span>
                                                        )}
                                                        {status === 'duplicate' && (
                                                            <span className="text-xs font-semibold text-amber-600">Duplicate</span>
                                                        )}
                                                        {status === 'update' && (
                                                            <span className="text-xs font-semibold text-blue-600">Update</span>
                                                        )}
                                                        {status === 'new' && (
                                                            <span className="text-xs font-semibold text-green-600">Ready</span>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-2.5 text-sm font-medium text-gray-800">{row.name}</td>
                                                    <td className="px-4 py-2.5 text-sm text-gray-700">
                                                        {row.item_price !== '' ? `₱${parseFloat(row.item_price).toFixed(2)}` : '—'}
                                                    </td>
                                                    <td className="px-4 py-2.5 text-sm text-gray-700">{row.category}</td>
                                                    <td className="px-4 py-2.5 text-sm text-gray-700">{row.classification}</td>
                                                    <td className="px-4 py-2.5 text-sm text-gray-500 max-w-xs truncate" title={row.description}>{row.description}</td>
                                                    <td className="px-4 py-2.5 text-sm text-gray-500">
                                                        {row._branchIds === 'all'
                                                            ? 'All branches'
                                                            : row._branchIds.length > 0
                                                                ? branches
                                                                    .filter(b => row._branchIds.includes(b.branch_id))
                                                                    .map(b => formatBranchName(b.name))
                                                                    .join(', ')
                                                                : '—'}
                                                    </td>
                                                    <td className="px-4 py-2.5 text-xs text-blue-700">
                                                        {status === 'update' ? changes.join(', ') : '—'}
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        <div className="border-t border-gray-200 px-6 py-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 flex-shrink-0">
                            <p className="text-xs text-gray-400 min-w-0">
                                <span className="text-amber-600 font-medium">Duplicate</span> rows are unchanged and will be skipped. <span className="text-blue-600 font-medium">Update</span> rows will overwrite the existing item. <span className="text-red-600 font-medium">Error</span> rows are skipped.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3 shrink-0">
                                <button onClick={() => setShowBulkImportModal(false)} className="w-full sm:w-auto px-5 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300">
                                    Cancel
                                </button>
                                <button
                                    onClick={handleBulkImport}
                                    disabled={bulkImporting || (bulkImportCounts.new + bulkImportCounts.update) === 0}
                                    className={`w-full sm:w-auto px-5 py-2 rounded-lg font-medium whitespace-normal text-center ${bulkImporting || (bulkImportCounts.new + bulkImportCounts.update) === 0
                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                        : 'bg-[#FF6F3C] text-white hover:bg-[#ff4c0b]'
                                        }`}
                                >
                                    {bulkImporting ? 'Importing...' : `Import ${bulkImportCounts.new} New, Update ${bulkImportCounts.update}`}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )
            }

            {/* Variant Import Modal */}
            {
                showVariantImportModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setShowVariantImportModal(false)} />
                        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
                            <div className="border-b border-gray-200 px-6 py-4 flex-shrink-0">
                                <h2 className="text-xl font-bold text-gray-800">Import Variants CSV</h2>
                                <p className="text-sm text-gray-500 mt-1">
                                    Required columns: <code className="bg-gray-100 px-1 rounded text-xs">category, variant_name, price</code> — Optional: <code className="bg-gray-100 px-1 rounded text-xs">applicable_categories</code> (pipe-separated, e.g. <code className="bg-gray-100 px-1 rounded text-xs">Coffee|Tea</code>)
                                </p>
                            </div>

                            <div className="px-6 py-4 border-b border-gray-100 flex-shrink-0 flex items-center gap-4 flex-wrap">
                                <label className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg cursor-pointer text-sm font-medium text-gray-700">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path fill="currentColor" d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zm-1 7V3.5L18.5 9zM8 13h3v-3h2v3h3l-4 4z" /></svg>
                                    {variantImportFile ? variantImportFile.name : 'Choose CSV File'}
                                    <input type="file" accept=".xlsx" onChange={handleVariantImportFileChange} className="hidden" />
                                </label>
                                {variantImportFile && (
                                    <button onClick={() => { setVariantImportFile(null); setVariantImportPreview([]); setVariantImportErrors([]) }}
                                        className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg">✕</button>
                                )}
                                {variantImportPreview.length > 0 && (
                                    <div className="flex gap-3 text-sm">
                                        <span className="px-2.5 py-1 rounded-full bg-green-100 text-green-700 font-semibold">
                                            {variantImportPreview.filter(r => !r._hasError).length} valid
                                        </span>
                                        {variantImportErrors.length > 0 && (
                                            <span className="px-2.5 py-1 rounded-full bg-red-100 text-red-700 font-semibold">
                                                {variantImportErrors.length} with errors
                                            </span>
                                        )}
                                    </div>
                                )}
                                <button
                                    type="button"
                                    onClick={() => downloadExcelTemplate(
                                        'variants_template.xlsx',
                                        'Variants',
                                        ['category', 'variant_name', 'price', 'applicable_categories'],
                                        [
                                            ['Size', 'Small', 0, 'Coffee|Tea'],
                                            ['Size', 'Medium', 20, 'Coffee|Tea'],
                                            ['Size', 'Large', 40, 'Coffee|Tea'],
                                        ]
                                    )}
                                    className="ml-auto text-xs text-indigo-600 hover:underline flex items-center gap-1"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24"><path fill="currentColor" d="M5 20h14v-2H5zm7-18L5.33 9h4.84v6h3.66V9h4.84z" /></svg>
                                    Download Template
                                </button>
                            </div>

                            {variantImportErrors.length > 0 && (
                                <div className="px-6 py-3 bg-red-50 border-b border-red-100 flex-shrink-0 max-h-28 overflow-y-auto">
                                    <p className="text-xs font-semibold text-red-700 mb-1">Errors — these rows will be skipped:</p>
                                    {variantImportErrors.map((e, i) => (
                                        <p key={i} className="text-xs text-red-600">Row {e.row} ({e.name}): {e.errors.join('; ')}</p>
                                    ))}
                                </div>
                            )}

                            <div className="overflow-y-auto flex-1">
                                {variantImportPreview.length === 0 ? (
                                    <div className="px-6 py-12 text-center text-gray-400 text-sm">Upload a CSV file to preview before importing.</div>
                                ) : (
                                    <table className="w-full">
                                        <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Variant Name</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Applicable Categories</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100 bg-white">
                                            {variantImportPreview.map((row, i) => {
                                                const isDupe = !row._hasError && isDuplicateVariant(row.category, row.variant_name)
                                                return (
                                                    <tr key={i} className={row._hasError ? 'bg-red-50' : isDupe ? 'bg-amber-50' : 'hover:bg-gray-50'}>
                                                        <td className="px-4 py-2.5">
                                                            {row._hasError ? <span className="text-xs font-semibold text-red-600">Error</span>
                                                                : isDupe ? <span className="text-xs font-semibold text-amber-600">Duplicate</span>
                                                                    : <span className="text-xs font-semibold text-green-600">Ready</span>}
                                                        </td>
                                                        <td className="px-4 py-2.5 text-sm text-gray-800">{row.category}</td>
                                                        <td className="px-4 py-2.5 text-sm font-medium text-gray-800">{row.variant_name}</td>
                                                        <td className="px-4 py-2.5 text-sm text-gray-700">₱{row.price !== '' ? parseFloat(row.price).toFixed(2) : '—'}</td>
                                                        <td className="px-4 py-2.5 text-sm text-gray-500">{row.applicable_categories.join(', ') || '—'}</td>
                                                    </tr>
                                                )
                                            })}
                                        </tbody>
                                    </table>
                                )}
                            </div>

                            <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
                                <p className="text-xs text-gray-400">Duplicates and error rows are skipped automatically.</p>
                                <div className="flex gap-3">
                                    <button onClick={() => setShowVariantImportModal(false)} className="px-5 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300">Cancel</button>
                                    <button
                                        onClick={handleVariantImport}
                                        disabled={variantImporting || variantImportPreview.filter(r => !r._hasError).length === 0}
                                        className={`px-5 py-2 rounded-lg font-medium ${variantImporting || variantImportPreview.filter(r => !r._hasError).length === 0 ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}
                                    >
                                        {variantImporting ? 'Importing...' : `Import ${variantImportPreview.filter(r => !r._hasError && !isDuplicateVariant(r.category, r.variant_name)).length} Variants`}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Modifier Import Modal */}
            {
                showModifierImportModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setShowModifierImportModal(false)} />
                        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
                            <div className="border-b border-gray-200 px-6 py-4 flex-shrink-0">
                                <h2 className="text-xl font-bold text-gray-800">Import Modifiers CSV</h2>
                                <p className="text-sm text-gray-500 mt-1">
                                    Required columns: <code className="bg-gray-100 px-1 rounded text-xs">modifier_name, category, price</code> — Optional: <code className="bg-gray-100 px-1 rounded text-xs">applicable_categories</code> (pipe-separated), <code className="bg-gray-100 px-1 rounded text-xs">ingredient_id</code>
                                </p>
                            </div>

                            <div className="px-6 py-4 border-b border-gray-100 flex-shrink-0 flex items-center gap-4 flex-wrap">
                                <label className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg cursor-pointer text-sm font-medium text-gray-700">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path fill="currentColor" d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zm-1 7V3.5L18.5 9zM8 13h3v-3h2v3h3l-4 4z" /></svg>
                                    {modifierImportFile ? modifierImportFile.name : 'Choose CSV File'}
                                    <input type="file" accept=".xlsx" onChange={handleModifierImportFileChange} className="hidden" />
                                </label>
                                {modifierImportFile && (
                                    <button onClick={() => { setModifierImportFile(null); setModifierImportPreview([]); setModifierImportErrors([]) }}
                                        className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg">✕</button>
                                )}
                                {modifierImportPreview.length > 0 && (
                                    <div className="flex gap-3 text-sm">
                                        <span className="px-2.5 py-1 rounded-full bg-green-100 text-green-700 font-semibold">
                                            {modifierImportPreview.filter(r => !r._hasError).length} valid
                                        </span>
                                        {modifierImportErrors.length > 0 && (
                                            <span className="px-2.5 py-1 rounded-full bg-red-100 text-red-700 font-semibold">
                                                {modifierImportErrors.length} with errors
                                            </span>
                                        )}
                                    </div>
                                )}
                                <button
                                    type="button"
                                    onClick={() => downloadExcelTemplate(
                                        'modifiers_template.xlsx',
                                        'Modifiers',
                                        ['modifier_name', 'category', 'price', 'applicable_categories', 'ingredient_id'],
                                        [
                                            ['Extra Shot', 'Add-ons', 30, 'Coffee|Tea', ''],
                                            ['No Sugar', 'Add-ons', 0, 'Coffee|Tea', ''],
                                        ]
                                    )}
                                    className="ml-auto text-xs text-indigo-600 hover:underline flex items-center gap-1"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24"><path fill="currentColor" d="M5 20h14v-2H5zm7-18L5.33 9h4.84v6h3.66V9h4.84z" /></svg>
                                    Download Template
                                </button>
                            </div>

                            {modifierImportErrors.length > 0 && (
                                <div className="px-6 py-3 bg-red-50 border-b border-red-100 flex-shrink-0 max-h-28 overflow-y-auto">
                                    <p className="text-xs font-semibold text-red-700 mb-1">Errors — these rows will be skipped:</p>
                                    {modifierImportErrors.map((e, i) => (
                                        <p key={i} className="text-xs text-red-600">Row {e.row} ({e.name}): {e.errors.join('; ')}</p>
                                    ))}
                                </div>
                            )}

                            <div className="overflow-y-auto flex-1">
                                {modifierImportPreview.length === 0 ? (
                                    <div className="px-6 py-12 text-center text-gray-400 text-sm">Upload a CSV file to preview before importing.</div>
                                ) : (
                                    <table className="w-full">
                                        <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Modifier Name</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Applicable Categories</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ingredient</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100 bg-white">
                                            {modifierImportPreview.map((row, i) => {
                                                const isDupe = !row._hasError && isDuplicateModifier(row.modifier_name, row.category)
                                                return (
                                                    <tr key={i} className={row._hasError ? 'bg-red-50' : isDupe ? 'bg-amber-50' : 'hover:bg-gray-50'}>
                                                        <td className="px-4 py-2.5">
                                                            {row._hasError ? <span className="text-xs font-semibold text-red-600">Error</span>
                                                                : isDupe ? <span className="text-xs font-semibold text-amber-600">Duplicate</span>
                                                                    : <span className="text-xs font-semibold text-green-600">Ready</span>}
                                                        </td>
                                                        <td className="px-4 py-2.5 text-sm font-medium text-gray-800">{row.modifier_name}</td>
                                                        <td className="px-4 py-2.5 text-sm text-gray-800">{row.category}</td>
                                                        <td className="px-4 py-2.5 text-sm text-gray-700">₱{row.price !== '' ? parseFloat(row.price).toFixed(2) : '—'}</td>
                                                        <td className="px-4 py-2.5 text-sm text-gray-500">{row.applicable_categories.join(', ') || '—'}</td>
                                                        <td className="px-4 py-2.5 text-sm text-gray-500">{row.ingredient_id || '—'}</td>
                                                    </tr>
                                                )
                                            })}
                                        </tbody>
                                    </table>
                                )}
                            </div>

                            <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
                                <p className="text-xs text-gray-400">Duplicates and error rows are skipped automatically.</p>
                                <div className="flex gap-3">
                                    <button onClick={() => setShowModifierImportModal(false)} className="px-5 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300">Cancel</button>
                                    <button
                                        onClick={handleModifierImport}
                                        disabled={modifierImporting || modifierImportPreview.filter(r => !r._hasError).length === 0}
                                        className={`px-5 py-2 rounded-lg font-medium ${modifierImporting || modifierImportPreview.filter(r => !r._hasError).length === 0 ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-purple-600 text-white hover:bg-purple-700'}`}
                                    >
                                        {modifierImporting ? 'Importing...' : `Import ${modifierImportPreview.filter(r => !r._hasError && !isDuplicateModifier(r.modifier_name, r.category)).length} Modifiers`}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Rejection Reason Modal */}
            {
                showRejectReasonModal && (
                    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
                        <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setShowRejectReasonModal(false)} />
                        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
                            <div className="border-b border-gray-200 px-6 py-4">
                                <h2 className="text-lg font-bold text-gray-800">
                                    {rejectingSource === 'price' ? 'Reject Price Change' : 'Reject Request'}
                                </h2>
                                <p className="text-sm text-gray-500 mt-0.5">
                                    Rejecting {rejectingSource === 'price' ? 'price change' : 'request'} for <span className="font-medium">{rejectingRequest?.item_name}</span>
                                </p>
                            </div>
                            <div className="px-6 py-5 space-y-4">
                                {rejectingSource === 'price' ? (
                                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                        <p className="text-sm text-red-700">
                                            Requested: <span className="font-bold">₱{parseFloat(rejectingRequest?.requested_price || 0).toFixed(2)}</span>
                                            <span className="ml-2 text-red-400">(current: ₱{parseFloat(rejectingRequest?.current_price || 0).toFixed(2)})</span>
                                        </p>
                                    </div>
                                ) : (
                                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                        <p className="text-sm text-red-700">
                                            <span className="font-semibold capitalize">{rejectingRequest?.request_type}</span> request
                                            {rejectingRequest?.item_type ? ` for a ${rejectingRequest.item_type === 'menu' ? 'menu item' : rejectingRequest.item_type}` : ''}
                                        </p>
                                    </div>
                                )}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Reason for Rejection <span className="text-red-500">*</span>
                                    </label>
                                    <textarea
                                        value={rejectionReason}
                                        onChange={(e) => setRejectionReason(e.target.value)}
                                        placeholder="Explain why this price change is being rejected..."
                                        rows={4}
                                        maxLength={300}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400 text-sm resize-none"
                                    />
                                    <p className="text-xs text-gray-400 mt-1 text-right">{rejectionReason.length}/300</p>
                                </div>
                            </div>
                            <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3 flex-shrink-0">
                                <button onClick={() => setShowRejectReasonModal(false)} className="px-5 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300">
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSubmitRejection}
                                    disabled={submittingRejection || !rejectionReason.trim()}
                                    className={`px-5 py-2 rounded-lg font-medium ${submittingRejection || !rejectionReason.trim()
                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                        : 'bg-red-600 text-white hover:bg-red-700'
                                        }`}
                                >
                                    {submittingRejection ? 'Submitting...' : 'Confirm Rejection'}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }
        </div >
    )
}